import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import "./HealthRecords.css"; // Import the CSS file
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPencilAlt,
  faCalendarAlt,
  faPlus,
  faFileMedicalAlt,
  faChartBar,
  faUpload,
  faChevronLeft,
  faChevronRight,
  faTimes,
  faSpinner,
  faSave,
  faInfoCircle,
  faExclamationTriangle,
  faSliders,
  faCheck,
  faTrashAlt,
  faBriefcaseMedical,
} from "@fortawesome/free-solid-svg-icons";
import {
  faHistory,
  faArrowUp,
  faArrowDown,
  faChartLine,
} from "@fortawesome/free-solid-svg-icons";
import {
  faPrescriptionBottleAlt,
  faHospital,
  faFileInvoiceDollar,
  faSyringe,
  faHeartbeat,
  faStethoscope,
  faClipboardList,
  faRulerVertical,
  faWeightScale,
  faHeartPulse,
  faLungs,
  faCalculator,
  faDroplet,
} from "@fortawesome/free-solid-svg-icons";
import { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { Container } from "react-bootstrap";
import { useNavigate, useLocation } from "react-router-dom";
import healthRecordsService, {
  PrescriptionRecord,
  HospitalizationRecord,
  MedicalBillRecord,
  VaccinationCertificate,
  CompareResult,
  VitalsComparison,
  HealthConditionRecord,
  HealthConditionPage,
  ConditionPayload,
  MedicationTaken,
} from "../../services/healthRecordsService";import {
  Dependant,
} from "../../types";
import { API_ENDPOINTS } from '../../api/config';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Legend,
} from 'recharts';
import BackButton from '../../components/BackButton/BackButton';
import ConfirmDialog from '../../components/ConfirmDialog/ConfirmDialog';
import axiosInstance, { getApiErrorMessage } from '../../api/axiosInstance';
import { useProfile } from '../../context/ProfileContext';
import { useServiceAccess } from '../../context/ServiceAccessContext';
import { useTranslation } from 'react-i18next';

interface Vital {
  name: string;
  value: string;
  lastUpdated: string;
  type: 'height' | 'weight' | 'heartRate' | 'bloodPressure' | 'oxygenSaturation' | 'bmi' | 'glucose';
  history: { id?: number; value: string; date: string }[];
  bmiNumeric?: number;
  bmiCategory?: string;
  bmiCategoryLabel?: string;
}

// New interface for Lab Parameters
interface LabParameter {
  id: string; // Unique ID for key prop and easy deletion
  parameterName: string;
  result: string;
  unit: string;
  startRange: string;
  endRange: string;
}

interface ConditionMedicineRow {
  id?: number;
  medicine_name: string;
  medicine_type: string;
  dosage: string;
  frequency: string;
  source: string;
  is_self_prescribed: boolean;
  notes: string;
}

const emptyConditionMedicine = (): ConditionMedicineRow => ({
  medicine_name: '',
  medicine_type: 'tablet',
  dosage: '',
  frequency: 'unknown',
  source: 'self',
  is_self_prescribed: true,
  notes: '',
});

const CONDITIONS_PER_PAGE = 6;

interface HealthRecordItem {
  name: string;
  relation: string;
  type: string;
  recordName: string;
  doctor: string;
  recordDate: string;
  typeOfRecord: string;
  specialization?: string; // Added specialization field
  labParameters?: LabParameter[]; // Optional array of lab parameters
}

// Tab configuration matching CD-style
const tabConfig = [
  {
    key: "Prescription & Lab Tests",
    label: "Prescriptions",
    icon: faPrescriptionBottleAlt,
    color: "#06b6d4",
    headerClass: "hr-tab-header-prescriptions",
    iconClass: "hr-icon-prescriptions",
    headerTitle: "Prescriptions & Lab Tests",
    headerDesc: "View and manage your prescriptions, lab reports, and doctor consultations. Keep all your medical records organized in one place.",
  },
  {
    key: "Hospitalizations",
    label: "Hospitalizations",
    icon: faHospital,
    color: "#f59e0b",
    headerClass: "hr-tab-header-hospitalizations",
    iconClass: "hr-icon-hospitalizations",
    headerTitle: "Hospitalization Records",
    headerDesc: "Track admission and discharge details, hospital stays, treatment summaries, and associated documents.",
  },
  {
    key: "Medical Bills",
    label: "Medical Bills",
    icon: faFileInvoiceDollar,
    color: "#10b981",
    headerClass: "hr-tab-header-bills",
    iconClass: "hr-icon-bills",
    headerTitle: "Medical Bills & Insurance",
    headerDesc: "Manage your medical expenses, insurance claims, and billing history. Keep track of all healthcare-related finances.",
  },
  {
    key: "Vaccinations Certificates",
    label: "Vaccinations",
    icon: faSyringe,
    color: "#8b5cf6",
    headerClass: "hr-tab-header-vaccinations",
    iconClass: "hr-icon-vaccinations",
    headerTitle: "Vaccination Certificates",
    headerDesc: "Maintain your vaccination records, track dose schedules, and store certificates for easy access.",
  },
  {
    key: "Health Conditions",
    label: "Health Conditions",
    icon: faBriefcaseMedical,
    color: "#14b8a6",
    headerClass: "hr-tab-header-conditions",
    iconClass: "hr-icon-conditions",
    headerTitle: "Health Conditions",
    headerDesc: "Record conditions you treated yourself at home — a fever, a cold, an injury — along with the medicines you took for them.",
  },
];

const vitalIconMap: Record<string, { icon: IconDefinition; color: string }> = {
  height:           { icon: faRulerVertical, color: '#8b5cf6' },
  weight:           { icon: faWeightScale,   color: '#06b6d4' },
  heartRate:        { icon: faHeartbeat,     color: '#f43f5e' },
  bloodPressure:    { icon: faHeartPulse,    color: '#ec4899' },
  oxygenSaturation: { icon: faLungs,         color: '#10b981' },
  bmi:              { icon: faCalculator,     color: '#f59e0b' },
  glucose:          { icon: faDroplet,        color: '#f97316' },
};

const BMI_CATEGORY_MAP: Record<string, { label: string; color: string; className: string }> = {
  underweight:    { label: 'Underweight',    color: '#3b82f6', className: 'bmi-underweight' },
  normal:         { label: 'Normal',          color: '#10b981', className: 'bmi-normal' },
  overweight:     { label: 'Overweight',      color: '#f59e0b', className: 'bmi-overweight' },
  obese:          { label: 'Obese',           color: '#f97316', className: 'bmi-obese' },
  severely_obese: { label: 'Severely Obese',  color: '#ef4444', className: 'bmi-severe' },
};

const getBmiCategory = (category?: string): { label: string; color: string; className: string } => {
  return category && BMI_CATEGORY_MAP[category] ? BMI_CATEGORY_MAP[category] : { label: 'Unknown', color: '#94a3b8', className: '' };
};

// ── Normal Range Reference Data ──
interface VitalRange {
  min: number;
  max: number;
  unit: string;
  display: string;
  source: string;
  sourceUrl: string;
}

const FALLBACK_VITAL_RANGES: Record<string, VitalRange> = {
  height:           { min: 150, max: 185, unit: 'cm', display: '150–185 cm', source: 'WHO Growth Reference', sourceUrl: 'https://www.who.int/tools/growth-reference-data-for-5to19-years' },
  weight:           { min: 50, max: 90, unit: 'kg', display: '50–90 kg', source: 'WHO BMI Classification', sourceUrl: 'https://www.who.int/data/gho/data/themes/topics/topic-details/GHO/body-mass-index' },
  heartRate:        { min: 60, max: 100, unit: 'BPM', display: '60–100 BPM', source: 'American Heart Association', sourceUrl: 'https://www.heart.org/en/health-topics/high-blood-pressure/the-facts-about-high-blood-pressure/all-about-heart-rate-pulse' },
  bloodPressure:    { min: 90, max: 120, unit: 'mmHg', display: '90/60–120/80 mmHg', source: 'American Heart Association', sourceUrl: 'https://www.heart.org/en/health-topics/high-blood-pressure/understanding-blood-pressure-readings' },
  oxygenSaturation: { min: 95, max: 100, unit: '%', display: '95–100%', source: 'WHO Pulse Oximetry Guidelines', sourceUrl: 'https://www.who.int/patientsafety/safesurgery/pulse_oximetry/en/' },
  bmi:              { min: 18.5, max: 24.9, unit: 'kg/m²', display: '18.5–24.9 kg/m²', source: 'WHO BMI Classification', sourceUrl: 'https://www.who.int/data/gho/data/themes/topics/topic-details/GHO/body-mass-index' },
  glucose:          { min: 70, max: 100, unit: 'mg/dL', display: '70–100 mg/dL (fasting)', source: 'American Diabetes Association', sourceUrl: 'https://diabetes.org/about-diabetes/diagnosis' },
};  

const CUSTOM_RANGES_KEY = 'infinicare_vital_ranges';

const loadCustomRanges = (): Record<string, Partial<VitalRange>> => {
  try {
    const saved = localStorage.getItem(CUSTOM_RANGES_KEY);
    return saved ? JSON.parse(saved) : {};
  } catch { return {}; }
};

const saveCustomRanges = (ranges: Record<string, Partial<VitalRange>>) => {
  localStorage.setItem(CUSTOM_RANGES_KEY, JSON.stringify(ranges));
};

const isInRange = (type: string, value: string, range: VitalRange): 'normal' | 'low' | 'high' | 'unknown' => {
  if (value === '--') return 'unknown';
  if (type === 'bloodPressure') {
    const match = value.match(/(\d+)\s*\/\s*(\d+)/);
    if (!match) return 'unknown';
    const sys = parseInt(match[1]);
    if (sys < range.min) return 'low';
    if (sys > range.max) return 'high';
    return 'normal';
  }
  const num = parseFloat(value.replace(/[^0-9.]/g, ''));
  if (isNaN(num)) return 'unknown';
  if (num < range.min) return 'low';
  if (num > range.max) return 'high';
  return 'normal';
};

const rangeStatusConfig = {
  normal: { label: 'Normal', color: '#10b981', bg: '#ecfdf5' },
  low: { label: 'Below Normal', color: '#f59e0b', bg: '#fffbeb' },
  high: { label: 'Above Normal', color: '#ef4444', bg: '#fef2f2' },
  unknown: { label: '', color: '#94a3b8', bg: 'transparent' },
};

const REPORT_ERROR_EMAIL = 'support@infinicare.ai';

// ── Vitals History Statistics / Trend helpers ──
interface VitalTrendPoint {
  label: string;
  value?: number;
  sys?: number;
  dia?: number;
}

interface VitalStats {
  count: number;
  latest: number;
  min: number;
  max: number;
  avg: number;
  delta: number;
  latestDate?: string;
}

const parseHistoryDate = (date: string): Date => {
  const parts = date.split('-').map(Number);
  if (parts.length === 3) return new Date(parts[2], parts[1] - 1, parts[0]);
  return new Date(date);
};

const parseVitalNumber = (type: string, value: string): number | null => {
  if (!value || value === '--') return null;
  if (type === 'bloodPressure') {
    const match = String(value).match(/(\d+)\s*\/\s*(\d+)/);
    return match ? parseInt(match[1]) : null;
  }
  const num = parseFloat(String(value).replace(/[^0-9.]/g, ''));
  return isNaN(num) ? null : num;
};

const buildVitalTrendData = (type: string, history: { value: string; date: string }[]): VitalTrendPoint[] => {
  return [...history]
    .sort((a, b) => parseHistoryDate(a.date).getTime() - parseHistoryDate(b.date).getTime())
    .map((entry) => {
    const parts = entry.date.split('-').map(Number);
    const d = parts.length === 3
      ? new Date(parts[2], parts[1] - 1, parts[0])
      : new Date(entry.date);
    const label = d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
    if (type === 'bloodPressure') {
      const m = String(entry.value).match(/(\d+)\s*\/\s*(\d+)/);
      return { label, sys: m ? parseInt(m[1]) : undefined, dia: m ? parseInt(m[2]) : undefined };
    }
    const num = parseFloat(String(entry.value).replace(/[^0-9.]/g, ''));
    return { label, value: isNaN(num) ? undefined : num };
  });
};

const computeVitalStats = (type: string, history: { value: string; date: string }[]): VitalStats | null => {
  const sorted = [...history].sort(
    (a, b) => parseHistoryDate(a.date).getTime() - parseHistoryDate(b.date).getTime()
  );
  const nums = sorted
    .map((e) => parseVitalNumber(type, e.value))
    .filter((n): n is number => n !== null);
  if (nums.length === 0) return null;
  const latest = nums[nums.length - 1];
  const delta = nums.length > 1 ? latest - nums[nums.length - 2] : 0;
  return {
    count: nums.length,
    latest,
    min: Math.min(...nums),
    max: Math.max(...nums),
    avg: nums.reduce((a, b) => a + b, 0) / nums.length,
    delta,
    latestDate: sorted[sorted.length - 1]?.date,
  };
};

const getVitalUnit = (type: string): string => {
  const key = type as keyof typeof FALLBACK_VITAL_RANGES;
  return FALLBACK_VITAL_RANGES[key]?.unit || '';
};

const HealthRecords: React.FC = () => {
  const { t } = useTranslation(['health', 'common']);
  const { activeProfile, isDependantUser: isLoggedInAsDependant } = useProfile();
  const { canAccess } = useServiceAccess();
  const inputRef1 = useRef<HTMLInputElement>(null);
  const inputRef2 = useRef<HTMLInputElement>(null);

  const openDatepicker = (ref: React.RefObject<HTMLInputElement | null>) => {
    if (ref.current) {
      if (typeof ref.current.showPicker === "function") {
        ref.current.showPicker(); // opens native calendar popup
      } else {
        ref.current.focus(); // fallback: just focus input
      }
    }
  };

  // API-based vital ranges
  const [apiRanges, setApiRanges] = useState<Record<string, VitalRange> | null>(null);

  useEffect(() => {
    const fetchRanges = async () => {
      try {
        const response = await axiosInstance.get('/api/health-records/vital-ranges/');
        const data = response.data;
        const ranges: Record<string, VitalRange> = {};
        for (const [type, r] of Object.entries(data) as [string, any][]) {
          ranges[type] = {
            min: r.min,
            max: r.max,
            unit: r.unit,
            display: r.display,
            source: r.source,
            sourceUrl: r.source_url,
          };
        }
        setApiRanges(ranges);
      } catch (err) {
        console.error('Failed to fetch vital ranges:', err);
      }
    };
    fetchRanges();
  }, []);

  const getVitalRange = (type: string, customRanges: Record<string, Partial<VitalRange>>): VitalRange => {
    const fallback = FALLBACK_VITAL_RANGES[type];
    const apiRange = apiRanges?.[type];
    const base = apiRange || fallback;
    if (!base) return { min: 0, max: 0, unit: '', display: '--', source: '', sourceUrl: '' };

    const custom = customRanges[type];
    if (!custom) return base;

    const min = custom.min ?? base.min;
    const max = custom.max ?? base.max;
    const unit = base.unit;
    let display: string;
    if (type === 'bloodPressure') {
      display = `${min}/60–${max}/80 ${unit}`;
    } else {
      display = `${min}–${max} ${unit}`;
    }
    return { ...base, min, max, display, source: custom.source || base.source + ' (modified)', sourceUrl: base.sourceUrl };
  };

  // Loading and error states for vitals
  const [vitalsLoading, setVitalsLoading] = useState(true);
  const [vitalsError, setVitalsError] = useState<string | null>(null);
  const [showComparison, setShowComparison] = useState(false);
  const [comparison, setComparison] = useState<VitalsComparison | null>(null);
  const [savingVital, setSavingVital] = useState<string | null>(null);
  const [vitalsCarouselIndex, setVitalsCarouselIndex] = useState(0);
  const VITALS_VISIBLE = 4;

  // Format date helper
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }).replace(/\//g, '-') + ' ' + date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  };

  const formatDateOnly = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }).replace(/\//g, '-');
  };

  // Default empty vitals
  const defaultVitals: Vital[] = [
    { name: t('health:vitals.height'), value: "--", lastUpdated: "--", type: 'height', history: [] },
    { name: t('health:vitals.weight'), value: "--", lastUpdated: "--", type: 'weight', history: [] },
    { name: t('health:vitals.bmi'), value: "--", lastUpdated: "--", type: 'bmi', history: [] },
    { name: t('health:vitals.heartRate'), value: "--", lastUpdated: "--", type: 'heartRate', history: [] },
    { name: t('health:vitals.bloodPressure'), value: "--", lastUpdated: "--", type: 'bloodPressure', history: [] },
    { name: t('health:vitals.o2Saturation'), value: "--", lastUpdated: "--", type: 'oxygenSaturation', history: [] },
    { name: t('health:vitals.glucose'), value: "--", lastUpdated: "--", type: 'glucose', history: [] },
  ];

  const [vitals, setVitals] = useState<Vital[]>(defaultVitals);

  // State for health records by type
  const [prescriptions, setPrescriptions] = useState<PrescriptionRecord[]>([]);
  const [hospitalizations, setHospitalizations] = useState<HospitalizationRecord[]>([]);
  const [medicalBills, setMedicalBills] = useState<MedicalBillRecord[]>([]);
  const [vaccinations, setVaccinations] = useState<VaccinationCertificate[]>([]);
  const [recordsLoading, setRecordsLoading] = useState(false);
  const [recordsError, setRecordsError] = useState<string | null>(null);
  const [conditionsRefreshKey, setConditionsRefreshKey] = useState(0);
  const [conditionChoices, setConditionChoices] = useState<{
    condition_types: { value: string; label: string }[];
    severities: { value: string; label: string }[];
    statuses: { value: string; label: string }[];
    medicine_types: { value: string; label: string }[];
    frequencies: { value: string; label: string }[];
    medication_sources: { value: string; label: string }[];
  } | null>(null);
  const [conditionsList, setConditionsList] = useState<HealthConditionRecord[]>([]);
  const [conditionsLoading, setConditionsLoading] = useState(false);
  const [conditionsError, setConditionsError] = useState<string | null>(null);
  const [conditionsPage, setConditionsPage] = useState(1);
  const [conditionsCount, setConditionsCount] = useState(0);
  const [conditionExpandedId, setConditionExpandedId] = useState<number | null>(null);
  const [editingConditionId, setEditingConditionId] = useState<number | null>(null);
  const [addConditionName, setAddConditionName] = useState('');
  const [addConditionType, setAddConditionType] = useState('fever');
  const [addConditionSeverity, setAddConditionSeverity] = useState<'mild' | 'moderate' | 'severe'>('moderate');
  const [addConditionStartDate, setAddConditionStartDate] = useState('');
  const [addConditionEndDate, setAddConditionEndDate] = useState('');
  const [addConditionStatus, setAddConditionStatus] = useState<'ongoing' | 'resolved'>('ongoing');
  const [addConditionTreatedByDoctor, setAddConditionTreatedByDoctor] = useState(false);
  const [addConditionDoctorName, setAddConditionDoctorName] = useState('');
  const [addConditionNotes, setAddConditionNotes] = useState('');
  const [addConditionMedicines, setAddConditionMedicines] = useState<ConditionMedicineRow[]>([]);
  const [conditionExistingDocs, setConditionExistingDocs] = useState<{ id: number; file: string }[]>([]);
  const [conditionRemovedDocIds, setConditionRemovedDocIds] = useState<number[]>([]);

  // Fetch vitals from API — first load latest (1 call), then lazy-load history
  const fetchVitals = useCallback(async () => {
    setVitalsLoading(true);
    setVitalsError(null);
    try {
      // Step 1: Single API call to get all latest vitals
      const allLatest = await healthRecordsService.getAllLatestVitals();

      const height = allLatest.height;
      const weight = allLatest.weight;
      const heartRate = allLatest.heartRate;
      const bp = allLatest.bloodPressure;
      const o2 = allLatest.oxygenSaturation;
      const bmi = allLatest.bmi;
      const glucose = allLatest.glucose;

      // Always derive BMI from the latest height + weight so it reflects the
      // current values for EVERY user — not only those who happen to have a
      // stored BMI record. A stored record is used only as a fallback when
      // height/weight are unavailable. Unit-safe (cm/ft, kg/lb).
      let bmiVal: number | undefined;
      let bmiUpdatedAt: string | undefined;
      if (height && weight) {
        const heightUnit = (height.unit || 'cm').toLowerCase();
        const weightUnit = (weight.unit || 'kg').toLowerCase();
        const heightInMeters = heightUnit === 'ft'
          ? Number(height.value) * 0.3048
          : Number(height.value) / 100;
        const weightKg = weightUnit === 'lb'
          ? Number(weight.value) * 0.453592
          : Number(weight.value);
        if (heightInMeters > 0 && weightKg > 0) {
          bmiVal = Math.round((weightKg / (heightInMeters * heightInMeters)) * 10) / 10;
          bmiUpdatedAt = new Date(height.updated_at) > new Date(weight.updated_at)
            ? height.updated_at
            : weight.updated_at;
        }
      }
      // Extract BMI category from backend if available
      let bmiCategory: string | undefined;
      let bmiCategoryLabel: string | undefined;
      if (bmi && bmi.category) {
        bmiCategory = bmi.category;
      }
      if (bmi && bmi.category_label) {
        bmiCategoryLabel = bmi.category_label;
      }

      // Fallback to a stored BMI record only when we couldn't derive one.
      if (bmiVal === undefined && bmi) {
        bmiVal = bmi.value;
        bmiUpdatedAt = bmi.updated_at;
      }

      // Show vitals immediately with empty history
      const initialVitals = [
        {
          name: t('health:vitals.height'),
          value: height ? `${height.value} ${height.unit.toUpperCase()}` : "--",
          lastUpdated: height ? formatDate(height.updated_at) : "--",
          type: 'height' as const,
          history: [] as { value: string; date: string }[],
        },
        {
          name: t('health:vitals.weight'),
          value: weight ? `${weight.value} ${weight.unit.toUpperCase()}` : "--",
          lastUpdated: weight ? formatDate(weight.updated_at) : "--",
          type: 'weight' as const,
          history: [] as { value: string; date: string }[],
        },
        {
          name: t('health:vitals.bmi'),
          value: bmiVal !== undefined ? `${bmiVal}` : "--",
          lastUpdated: bmiUpdatedAt ? formatDate(bmiUpdatedAt) : "--",
          type: 'bmi' as const,
          history: [] as { value: string; date: string }[],
          bmiNumeric: bmiVal,
          bmiCategory: bmiCategory,
          bmiCategoryLabel: bmiCategoryLabel,
        },
        {
          name: t('health:vitals.heartRate'),
          value: heartRate ? `${heartRate.value} ${heartRate.unit}` : "--",
          lastUpdated: heartRate ? formatDate(heartRate.updated_at) : "--",
          type: 'heartRate' as const,
          history: [] as { value: string; date: string }[],
        },
        {
          name: t('health:vitals.bloodPressure'),
          value: bp ? `${bp.systolic}/${bp.diastolic} ${bp.unit}` : "--",
          lastUpdated: bp ? formatDate(bp.updated_at) : "--",
          type: 'bloodPressure' as const,
          history: [] as { value: string; date: string }[],
        },
        {
          name: t('health:vitals.o2Saturation'),
          value: o2 ? `${o2.value}${o2.unit}` : "--",
          lastUpdated: o2 ? formatDate(o2.updated_at) : "--",
          type: 'oxygenSaturation' as const,
          history: [] as { value: string; date: string }[],
        },
        {
          name: t('health:vitals.glucose'),
          value: glucose ? `${glucose.value} ${glucose.unit}` : "--",
          lastUpdated: glucose ? formatDate(glucose.updated_at) : "--",
          type: 'glucose' as const,
          history: [] as { value: string; date: string }[],
        },
      ];
      setVitals(initialVitals);
      setVitalsLoading(false);

      // Step 2: Fetch history records in batches of 2 to avoid rate limits
      const historyFetchers: { index: number; fetcher: () => Promise<{ id?: number; value: string; date: string }[]> }[] = [
        { index: 0, fetcher: async () => (await healthRecordsService.getHeightRecords()).slice(0, 20).map(r => ({ id: r.id, value: `${r.value} ${r.unit.toUpperCase()}`, date: formatDateOnly(r.updated_at) })) },
        { index: 1, fetcher: async () => (await healthRecordsService.getWeightRecords()).slice(0, 20).map(r => ({ id: r.id, value: `${r.value} ${r.unit.toUpperCase()}`, date: formatDateOnly(r.updated_at) })) },
        { index: 2, fetcher: async () => (await healthRecordsService.getBmiRecords()).slice(0, 20).map(r => ({ id: r.id, value: `${r.value}`, date: formatDateOnly(r.updated_at) })) },
        { index: 3, fetcher: async () => (await healthRecordsService.getHeartRateRecords()).slice(0, 20).map(r => ({ id: r.id, value: `${r.value} ${r.unit}`, date: formatDateOnly(r.updated_at) })) },
        { index: 4, fetcher: async () => (await healthRecordsService.getBloodPressureRecords()).slice(0, 20).map(r => ({ id: r.id, value: `${r.systolic}/${r.diastolic} ${r.unit}`, date: formatDateOnly(r.updated_at) })) },
        { index: 5, fetcher: async () => (await healthRecordsService.getOxygenSaturationRecords()).slice(0, 20).map(r => ({ id: r.id, value: `${r.value}${r.unit}`, date: formatDateOnly(r.updated_at) })) },
        { index: 6, fetcher: async () => (await healthRecordsService.getGlucoseRecords()).slice(0, 20).map(r => ({ id: r.id, value: `${r.value} ${r.unit}`, date: formatDateOnly(r.updated_at) })) },
      ];

      // Fetch in batches of 2
      for (let i = 0; i < historyFetchers.length; i += 2) {
        const batch = historyFetchers.slice(i, i + 2);
        const results = await Promise.allSettled(batch.map(b => b.fetcher()));
        setVitals(prev => {
          const updated = [...prev];
          results.forEach((result, idx) => {
            const targetIndex = batch[idx].index;
            if (result.status === 'fulfilled' && updated[targetIndex]) {
              updated[targetIndex] = { ...updated[targetIndex], history: result.value };
            }
          });
          return updated;
        });
      }
    } catch (err) {
      console.error('Error fetching vitals:', err);
      setVitalsError('Failed to load vitals. Please try again.');
      setVitalsLoading(false);
    }
  }, [t]);

  // Fetch vitals on mount and when active profile changes
  useEffect(() => {
    fetchVitals();
  }, [fetchVitals, activeProfile]);

  useEffect(() => {
    const handleVitalsUpdated = () => {
      fetchVitals();
    };
    window.addEventListener('vitals-updated', handleVitalsUpdated);
    return () => {
      window.removeEventListener('vitals-updated', handleVitalsUpdated);
    };
  }, [fetchVitals]);

  // ─── 3-Month Comparison (server-computed) ──────────────────────────
  const fetchComparison = useCallback(async () => {
    try {
      setComparison(await healthRecordsService.getVitalsComparison(3));
    } catch (err) {
      console.error('Error fetching vitals comparison:', err);
    }
  }, []);

  useEffect(() => {
    fetchComparison();
  }, [fetchComparison, activeProfile]);

  useEffect(() => {
    const handleVitalsUpdated = () => {
      fetchComparison();
    };
    window.addEventListener('vitals-updated', handleVitalsUpdated);
    return () => {
      window.removeEventListener('vitals-updated', handleVitalsUpdated);
    };
  }, [fetchComparison]);

  const vitalNameByType: Record<string, string> = useMemo(() => ({
    height: t('health:vitals.height'),
    weight: t('health:vitals.weight'),
    bmi: t('health:vitals.bmi'),
    heartRate: t('health:vitals.heartRate'),
    bloodPressure: t('health:vitals.bloodPressure'),
    oxygenSaturation: t('health:vitals.o2Saturation'),
    glucose: t('health:vitals.glucose'),
  }), [t]);

  const comparisonRows = useMemo(() => {
    if (!comparison) return [];
    return comparison.vitals.map(v => {
      const pointAt = (m: number) => v.points.find(p => p.months_ago === m);
      const p1 = pointAt(1);
      const p2 = pointAt(2);
      const p3 = pointAt(3);
      let trendColor = '#94a3b8';
      if (v.trend !== 'stable') {
        trendColor = v.direction === 'better' ? '#10b981' : v.direction === 'worse' ? '#ef4444' : '#3b82f6';
      }
      return {
        name: vitalNameByType[v.type] || v.label,
        type: v.type,
        rangeDisplay: v.normal_range ? v.normal_range.display : '--',
        current: v.current ? v.current.raw : '--',
        mo1: p1 ? p1.raw : '—',
        mo2: p2 ? p2.raw : '—',
        mo3: p3 ? p3.raw : '—',
        trend: v.trend,
        trendColor,
      };
    });
  }, [comparison, vitalNameByType]);

  // Filter state
  const [filterName, setFilterName] = useState('');
  const [filterStartDate, setFilterStartDate] = useState('');
  const [filterEndDate, setFilterEndDate] = useState('');
  const [filterForWhom, setFilterForWhom] = useState<'' | 'self' | 'dependant'>('');

  // Fetch records based on active tab (no server-side filters - backend doesn't support them)
  const fetchRecords = async (tab: string) => {
    setRecordsLoading(true);
    setRecordsError(null);
    try {
      switch (tab) {
        case "Prescription & Lab Tests":
          setPrescriptions(await healthRecordsService.getPrescriptions());
          break;
        case "Hospitalizations":
          setHospitalizations(await healthRecordsService.getHospitalizations());
          break;
        case "Medical Bills":
          setMedicalBills(await healthRecordsService.getMedicalBills());
          break;
        case "Vaccinations Certificates":
          setVaccinations(await healthRecordsService.getVaccinationCertificates());
          break;
      }
    } catch (err) {
      console.error('Error fetching records:', err);
      setRecordsError('Failed to load records. Please try again.');
    } finally {
      setRecordsLoading(false);
    }
  };

  // Client-side filter helpers
  const isDateInRange = (dateStr: string, startDate: string, endDate: string): boolean => {
    if (!startDate && !endDate) return true;
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return true;
    if (startDate && d < new Date(startDate)) return false;
    if (endDate && d > new Date(endDate + 'T23:59:59')) return false;
    return true;
  };

  const matchesForWhom = (recordForWhom: string, filter: string): boolean => {
    if (!filter) return true;
    return recordForWhom === filter;
  };

  const nameMatchesFilter = (name: string | undefined | null, filter: string): boolean => {
    if (!filter) return true;
    return (name || '').toLowerCase().includes(filter.toLowerCase());
  };

  // Filtered record lists (client-side)
  const filteredPrescriptions = useMemo(() => {
    return prescriptions.filter(r =>
      nameMatchesFilter(r.doctor_name, filterName) &&
      isDateInRange(r.record_date, filterStartDate, filterEndDate) &&
      matchesForWhom(r.for_whom, filterForWhom)
    );
  }, [prescriptions, filterName, filterStartDate, filterEndDate, filterForWhom]);

  const filteredHospitalizations = useMemo(() => {
    return hospitalizations.filter(r =>
      nameMatchesFilter(r.hospital_name, filterName) &&
      isDateInRange(r.admission_date, filterStartDate, filterEndDate) &&
      matchesForWhom(r.for_whom, filterForWhom)
    );
  }, [hospitalizations, filterName, filterStartDate, filterEndDate, filterForWhom]);

  const filteredMedicalBills = useMemo(() => {
    return medicalBills.filter(r =>
      nameMatchesFilter(r.hospital_name, filterName) &&
      isDateInRange(r.bill_date, filterStartDate, filterEndDate) &&
      matchesForWhom(r.for_whom, filterForWhom)
    );
  }, [medicalBills, filterName, filterStartDate, filterEndDate, filterForWhom]);

  const filteredVaccinations = useMemo(() => {
    return vaccinations.filter(r =>
      nameMatchesFilter(r.vaccine_name, filterName) &&
      isDateInRange(r.vaccination_date, filterStartDate, filterEndDate) &&
      matchesForWhom(r.for_whom, filterForWhom)
    );
  }, [vaccinations, filterName, filterStartDate, filterEndDate, filterForWhom]);

  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editedValue, setEditedValue] = useState<string>("");
  const [editedBpSystolic, setEditedBpSystolic] = useState<string>("");
  const [editedBpDiastolic, setEditedBpDiastolic] = useState<string>("");

  // Normal range state
  const [customRanges, setCustomRanges] = useState<Record<string, Partial<VitalRange>>>(loadCustomRanges);
  const [editingRange, setEditingRange] = useState<string | null>(null);
  const [rangeEditMin, setRangeEditMin] = useState('');
  const [rangeEditMax, setRangeEditMax] = useState('');
  const [historyTab, setHistoryTab] = useState<'history' | 'stats' | 'trend' | 'range'>('history');

  const handleStartRangeEdit = (type: string) => {
    const range = getVitalRange(type, customRanges);
    setRangeEditMin(String(range.min));
    setRangeEditMax(String(range.max));
    setEditingRange(type);
  };

  const handleSaveRange = (type: string) => {
    const min = parseFloat(rangeEditMin);
    const max = parseFloat(rangeEditMax);
    if (isNaN(min) || isNaN(max) || min >= max) {
      showToast(t('health:messages.enterValidValues'), 'warning');
      return;
    }
    const updated = { ...customRanges, [type]: { min, max } };
    setCustomRanges(updated);
    saveCustomRanges(updated);
    setEditingRange(null);
  };

  const handleResetRange = (type: string) => {
    const updated = { ...customRanges };
    delete updated[type];
    setCustomRanges(updated);
    saveCustomRanges(updated);
    setEditingRange(null);
  };
  const [activeTab, setActiveTab] = useState("Prescription & Lab Tests");

  // Disease risk profile
  const [riskProfile, setRiskProfile] = useState<any>(null);

  useEffect(() => {
    if (!canAccess('disease-prediction')) {
      setRiskProfile(null);
      return;
    }
    const fetchRisk = async () => {
      try {
        const res = await axiosInstance.get(API_ENDPOINTS.diseasePrediction.latest);
        if (res.data.has_prediction) setRiskProfile(res.data);
      } catch { /* silently fail */ }
    };
    fetchRisk();
  }, [activeProfile, canAccess]);

  // Fetch all record types on mount/profile change for stat counts
  const fetchAllRecords = async () => {
    try {
      const [p, h, b, v] = await Promise.all([
        healthRecordsService.getPrescriptions(),
        healthRecordsService.getHospitalizations(),
        healthRecordsService.getMedicalBills(),
        healthRecordsService.getVaccinationCertificates(),
      ]);
      setPrescriptions(p);
      setHospitalizations(h);
      setMedicalBills(b);
      setVaccinations(v);
    } catch (err) {
      console.error('Error fetching all records:', err);
    }
  };

  const initialLoadDone = React.useRef(false);

  useEffect(() => {
    setRecordsLoading(true);
    initialLoadDone.current = false;
    fetchAllRecords().finally(() => {
      setRecordsLoading(false);
      initialLoadDone.current = true;
    });
  }, [activeProfile]); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-fetch current tab when tab changes (skip initial mount — fetchAllRecords already loaded everything)
  useEffect(() => {
    if (initialLoadDone.current) {
      fetchRecords(activeTab);
    }
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps
  const [isAddingRecord, setIsAddingRecord] = useState(false);
  const [recordCarouselIndex, setRecordCarouselIndex] = useState(0);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- setter kept with the dormant record-carousel state below
  const [records, setRecords] = useState<HealthRecordItem[]>([]);
  const [showEditModal, setShowEditModal] = useState(false);

  // ─── Pagination State ───
  const RECORDS_PER_PAGE = 6;
  const [currentPage, setCurrentPage] = useState(1);

  // Reset page when tab or filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab, filterName, filterStartDate, filterEndDate, filterForWhom]);

  // Paginated record lists
  const activeFilteredCount = activeTab === 'Prescription & Lab Tests' ? filteredPrescriptions.length
    : activeTab === 'Hospitalizations' ? filteredHospitalizations.length
    : activeTab === 'Medical Bills' ? filteredMedicalBills.length
    : filteredVaccinations.length;
  const totalPages = Math.max(1, Math.ceil(activeFilteredCount / RECORDS_PER_PAGE));
  const paginatedPrescriptions = filteredPrescriptions.slice((currentPage - 1) * RECORDS_PER_PAGE, currentPage * RECORDS_PER_PAGE);
  const paginatedHospitalizations = filteredHospitalizations.slice((currentPage - 1) * RECORDS_PER_PAGE, currentPage * RECORDS_PER_PAGE);
  const paginatedMedicalBills = filteredMedicalBills.slice((currentPage - 1) * RECORDS_PER_PAGE, currentPage * RECORDS_PER_PAGE);
  const paginatedVaccinations = filteredVaccinations.slice((currentPage - 1) * RECORDS_PER_PAGE, currentPage * RECORDS_PER_PAGE);

  // ─── Edit Record Modal State ───
  const [editRecordId, setEditRecordId] = useState<number | null>(null);
  const [editRecordTab, setEditRecordTab] = useState<string>('');
  const [editRecordSaving, setEditRecordSaving] = useState(false);
  const [editRecordForWhom, setEditRecordForWhom] = useState<'self' | 'dependant'>('self');
  const [editRecordDependantId, setEditRecordDependantId] = useState<number | ''>('');
  const [editRecordFiles, setEditRecordFiles] = useState<File[]>([]);
  const editRecordFileRef = useRef<HTMLInputElement>(null);
  const [editExistingDocs, setEditExistingDocs] = useState<{ id: number; file: string; created_at: string }[]>([]);
  const [editRemovedDocIds, setEditRemovedDocIds] = useState<number[]>([]);
  // Prescription edit fields
  const [editRecordName, setEditRecordName] = useState('');
  const [editRecordType, setEditRecordType] = useState('');
  const [editDoctorName, setEditDoctorName] = useState('');
  const [editSpecialization, setEditSpecialization] = useState('');
  const [editRecordDate, setEditRecordDate] = useState('');
  const [editReason, setEditReason] = useState('');
  const [editShowLabParams, setEditShowLabParams] = useState(false);
  const [editLabParams, setEditLabParams] = useState<{ id: string; parameterName: string; result: string; unit: string; startRange: string; endRange: string }[]>([]);
  // Hospitalization edit fields
  const [editHospitalizationType, setEditHospitalizationType] = useState('inpatient');
  const [editHospitalName, setEditHospitalName] = useState('');
  const [editAdmissionDate, setEditAdmissionDate] = useState('');
  const [editDischargeDate, setEditDischargeDate] = useState('');
  const [editHospReason, setEditHospReason] = useState('');
  const [editHospDoctorName, setEditHospDoctorName] = useState('');
  const [editHospDiagnosis, setEditHospDiagnosis] = useState('');
  const [editHospProcedureName, setEditHospProcedureName] = useState('');
  const [editRoomType, setEditRoomType] = useState('');
  const [editTotalBill, setEditTotalBill] = useState('');
  const [editHospInsuranceClaimed, setEditHospInsuranceClaimed] = useState(false);
  const [editHospInsuranceAmount, setEditHospInsuranceAmount] = useState('');
  const [editHospNotes, setEditHospNotes] = useState('');
  // Medical Bill edit fields
  const [editBillHospitalName, setEditBillHospitalName] = useState('');
  const [editBillNumber, setEditBillNumber] = useState('');
  const [editBillDate, setEditBillDate] = useState('');
  const [editBillType, setEditBillType] = useState('');
  const [editBillAmount, setEditBillAmount] = useState('');
  const [editBillPaidAmount, setEditBillPaidAmount] = useState('');
  const [editBillInsuranceClaimed, setEditBillInsuranceClaimed] = useState(false);
  const [editBillInsuranceAmount, setEditBillInsuranceAmount] = useState('');
  const [editBillNotes, setEditBillNotes] = useState('');
  // Vaccination edit fields
  const [editVaccineName, setEditVaccineName] = useState('');
  const [editVaccinationDate, setEditVaccinationDate] = useState('');
  const [editDoseNumber, setEditDoseNumber] = useState('');
  const [editTotalDoses, setEditTotalDoses] = useState('');
  const [editAdministeredBy, setEditAdministeredBy] = useState('');
  const [editVaccineLocation, setEditVaccineLocation] = useState('');
  const [editBatchNumber, setEditBatchNumber] = useState('');
  const [editNextDueDate, setEditNextDueDate] = useState('');
  const [editVaccineNotes, setEditVaccineNotes] = useState('');
  const [historyVital, setHistoryVital] = useState<Vital | null>(null);
  const [deletingVitalId, setDeletingVitalId] = useState<number | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Scroll to Add Record section when navigated with ?action=add
  const addRecordSectionRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('action') === 'add') {
      // Clean up the URL
      navigate('/health-records', { replace: true });
      // Scroll to the Add Record section after a brief delay for render
      setTimeout(() => {
        addRecordSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 300);
    }
  }, [location.search, navigate]);

  // ─── Add Record Modal State ───
  const addRecordFileRef = useRef<HTMLInputElement>(null);
  const [addRecordFiles, setAddRecordFiles] = useState<File[]>([]);
  const [addRecordSaving, setAddRecordSaving] = useState(false);
  const [addRecordForWhom, setAddRecordForWhom] = useState<'self' | 'dependant'>('self');
  const [addShowLabParams, setAddShowLabParams] = useState(false);
  const [addLabParams, setAddLabParams] = useState<{ id: string; parameterName: string; result: string; unit: string; startRange: string; endRange: string }[]>([]);

  // Prescription fields
  const [addRecordName, setAddRecordName] = useState('');
  const [addRecordType, setAddRecordType] = useState('');
  const [addDoctorName, setAddDoctorName] = useState('');
  const [addSpecialization, setAddSpecialization] = useState('');
  const [addRecordDate, setAddRecordDate] = useState('');
  const [addReason, setAddReason] = useState('');

  // Hospitalization fields
  const [addHospitalizationType, setAddHospitalizationType] = useState('inpatient');
  const [addHospitalName, setAddHospitalName] = useState('');
  const [addAdmissionDate, setAddAdmissionDate] = useState('');
  const [addDischargeDate, setAddDischargeDate] = useState('');
  const [addHospReason, setAddHospReason] = useState('');
  const [addHospDoctorName, setAddHospDoctorName] = useState('');
  const [addHospDiagnosis, setAddHospDiagnosis] = useState('');
  const [addHospProcedureName, setAddHospProcedureName] = useState('');
  const [addRoomType, setAddRoomType] = useState('');
  const [addTotalBill, setAddTotalBill] = useState('');
  const [addHospInsuranceClaimed, setAddHospInsuranceClaimed] = useState(false);
  const [addHospInsuranceAmount, setAddHospInsuranceAmount] = useState('');
  const [addHospNotes, setAddHospNotes] = useState('');

  // Medical Bill fields
  const [addBillHospitalName, setAddBillHospitalName] = useState('');
  const [addBillNumber, setAddBillNumber] = useState('');
  const [addBillDate, setAddBillDate] = useState('');
  const [addBillType, setAddBillType] = useState('');
  const [addBillAmount, setAddBillAmount] = useState('');
  const [addBillPaidAmount, setAddBillPaidAmount] = useState('');
  const [addBillInsuranceClaimed, setAddBillInsuranceClaimed] = useState(false);
  const [addBillInsuranceAmount, setAddBillInsuranceAmount] = useState('');
  const [addBillNotes, setAddBillNotes] = useState('');

  // Vaccination fields
  const [addVaccineName, setAddVaccineName] = useState('');
  const [addVaccinationDate, setAddVaccinationDate] = useState('');
  const [addDoseNumber, setAddDoseNumber] = useState('');
  const [addTotalDoses, setAddTotalDoses] = useState('');
  const [addAdministeredBy, setAddAdministeredBy] = useState('');
  const [addVaccineLocation, setAddVaccineLocation] = useState('');
  const [addBatchNumber, setAddBatchNumber] = useState('');
  const [addNextDueDate, setAddNextDueDate] = useState('');
  const [addVaccineNotes, setAddVaccineNotes] = useState('');

  // Bug 1 fix: Success feedback state
  const [saveSuccessMessage, setSaveSuccessMessage] = useState<string | null>(null);

  // In-app toast for validation/error messages (replaces alert())
  const [toastMsg, setToastMsg] = useState<{ message: string; type: 'success' | 'error' | 'warning' } | null>(null);
  const showToast = (message: string, type: 'success' | 'error' | 'warning' = 'error') => {
    setToastMsg({ message, type });
    setTimeout(() => setToastMsg(null), 4000);
  };

  // In-page confirm dialog (replaces window.confirm)
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
    variant?: 'danger' | 'warning' | 'primary';
    confirmLabel?: string;
  }>({ open: false, title: '', message: '', onConfirm: () => {} });

  // Bug 3 fix: Dependant selector state
  const [addRecordDependantId, setAddRecordDependantId] = useState<number | ''>('');
  const [dependantsList, setDependantsList] = useState<Dependant[]>([]);
  const [dependantsLoading, setDependantsLoading] = useState(false);

  // Fetch dependants list
  const fetchDependants = async () => {
    setDependantsLoading(true);
    try {
      const response = await axiosInstance.get(API_ENDPOINTS.dependants.list);
      setDependantsList(response.data.results || response.data || []);
    } catch (err) {
      console.error('Error fetching dependants:', err);
    } finally {
      setDependantsLoading(false);
    }
  };

  // Fetch dependants on mount
  useEffect(() => {
    fetchDependants();
  }, []);

  // Bug 2 fix: View parameters/documents state
  const [viewParamsRecordId, setViewParamsRecordId] = useState<number | null>(null);
  const [viewDocsRecordId, setViewDocsRecordId] = useState<number | null>(null);

  const handleViewParameters = (recordId: number) => {
    setViewParamsRecordId(prev => prev === recordId ? null : recordId);
  };

  const handleViewDocuments = (recordId: number) => {
    setViewDocsRecordId(prev => prev === recordId ? null : recordId);
  };

  const resetAddRecordForm = () => {
    setAddRecordForWhom('self');
    setAddRecordDependantId('');
    setAddRecordFiles([]);
    setAddShowLabParams(false);
    setAddLabParams([]);
    setAddRecordName(''); setAddRecordType(''); setAddDoctorName('');
    setAddSpecialization(''); setAddRecordDate(''); setAddReason('');
    setAddHospitalizationType('inpatient'); setAddHospitalName(''); setAddAdmissionDate(''); setAddDischargeDate('');
    setAddHospReason(''); setAddHospDoctorName(''); setAddHospDiagnosis(''); setAddHospProcedureName(''); setAddRoomType('');
    setAddTotalBill(''); setAddHospInsuranceClaimed(false); setAddHospInsuranceAmount(''); setAddHospNotes('');
    setAddBillHospitalName(''); setAddBillNumber(''); setAddBillDate(''); setAddBillType('');
    setAddBillAmount(''); setAddBillPaidAmount(''); setAddBillInsuranceClaimed(false); setAddBillInsuranceAmount(''); setAddBillNotes('');
    setAddVaccineName(''); setAddVaccinationDate(''); setAddDoseNumber('');
    setAddTotalDoses(''); setAddAdministeredBy(''); setAddVaccineLocation('');
    setAddBatchNumber(''); setAddNextDueDate(''); setAddVaccineNotes('');
  };

  useEffect(() => {
    if (activeTab !== 'Health Conditions' || conditionChoices) return;
    let active = true;
    healthRecordsService
      .getHealthConditionChoices()
      .then((data) => {
        if (active) setConditionChoices(data);
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [activeTab, conditionChoices]);

  const fetchConditions = useCallback(async () => {
    setConditionsLoading(true);
    setConditionsError(null);
    try {
      const data = await healthRecordsService.getHealthConditions({
        page: conditionsPage,
        page_size: CONDITIONS_PER_PAGE,
        search: filterName.trim() || undefined,
        start_date_from: filterStartDate || undefined,
        start_date_to: filterEndDate || undefined,
        for_whom: filterForWhom || undefined,
      });
      setConditionsList(data.results);
      setConditionsCount(data.count);
    } catch (err) {
      setConditionsError(getApiErrorMessage(err, t('health:conditions.loadError')));
    } finally {
      setConditionsLoading(false);
    }
  }, [t, conditionsPage, filterName, filterStartDate, filterEndDate, filterForWhom]);

  useEffect(() => {
    if (activeTab !== 'Health Conditions') return;
    fetchConditions();
  }, [activeTab, conditionsRefreshKey, fetchConditions]);

  const conditionsTotalPages = Math.max(1, Math.ceil(conditionsCount / CONDITIONS_PER_PAGE));

  useEffect(() => {
    setConditionsPage(1);
  }, [filterName, filterStartDate, filterEndDate, filterForWhom]);

  useEffect(() => {
    if (conditionsPage > conditionsTotalPages) setConditionsPage(conditionsTotalPages);
  }, [conditionsPage, conditionsTotalPages]);

  const conditionChoiceLabel = (
    options: { value: string; label: string }[] | undefined,
    value: string | undefined,
    display?: string | null,
  ): string => options?.find((o) => o.value === value)?.label || display || (value ? value.replace(/_/g, ' ') : '-');

  const handleRemoveConditionDoc = (docId: number) => {
    setConditionRemovedDocIds((prev) => [...prev, docId]);
    setConditionExistingDocs((prev) => prev.filter((d) => d.id !== docId));
  };

  const resetConditionForm = () => {
    setEditingConditionId(null);
    setAddConditionName('');
    setAddConditionType('fever');
    setAddConditionSeverity('moderate');
    setAddConditionStartDate(new Date().toISOString().slice(0, 10));
    setAddConditionEndDate('');
    setAddConditionStatus('ongoing');
    setAddConditionTreatedByDoctor(false);
    setAddConditionDoctorName('');
    setAddConditionNotes('');
    setAddConditionMedicines([emptyConditionMedicine()]);
    setConditionExistingDocs([]);
    setConditionRemovedDocIds([]);
  };

  const openEditCondition = (record: HealthConditionRecord) => {
    setAddRecordForWhom(record.for_whom);
    setAddRecordDependantId(record.dependant || '');
    setEditingConditionId(record.id);
    setAddConditionName(record.condition_name);
    setAddConditionType(record.condition_type);
    setAddConditionSeverity(record.severity);
    setAddConditionStartDate(record.start_date);
    setAddConditionEndDate(record.end_date || '');
    setAddConditionStatus(record.status);
    setAddConditionTreatedByDoctor(record.treated_by_doctor);
    setAddConditionDoctorName(record.doctor_name || '');
    setAddConditionNotes(record.notes || '');
    setAddConditionMedicines(
      record.medications && record.medications.length > 0
        ? record.medications.map((m) => ({
            id: m.id,
            medicine_name: m.medicine_name,
            medicine_type: m.medicine_type,
            dosage: m.dosage || '',
            frequency: m.frequency,
            source: m.source,
            is_self_prescribed: m.is_self_prescribed,
            notes: m.notes || '',
          }))
        : [emptyConditionMedicine()],
    );
    setConditionExistingDocs(record.documents || []);
    setConditionRemovedDocIds([]);
    setIsAddingRecord(true);
  };

  const updateConditionMedicine = (
    index: number,
    key: keyof ConditionMedicineRow,
    value: string | boolean,
  ) => {
    setAddConditionMedicines(prev =>
      prev.map((row, i) => (i === index ? { ...row, [key]: value } : row)),
    );
  };

  const handleSaveCondition = async (): Promise<boolean> => {
    if (!addConditionName.trim() || !addConditionStartDate) {
      showToast(t('health:messages.fillRequired'), 'warning');
      return false;
    }
    if (addRecordForWhom === 'dependant' && !addRecordDependantId) {
      showToast(t('health:messages.selectDependant'), 'warning');
      return false;
    }

    const medications: Partial<MedicationTaken>[] = addConditionMedicines
      .filter((m) => m.medicine_name.trim())
      .map((m) => ({
        id: m.id,
        medicine_name: m.medicine_name,
        medicine_type: m.medicine_type,
        dosage: m.dosage,
        frequency: m.frequency,
        source: m.source,
        is_self_prescribed: m.is_self_prescribed,
        notes: m.notes,
      }));

    const payload: ConditionPayload = {
      condition_name: addConditionName.trim(),
      condition_type: addConditionType,
      severity: addConditionSeverity,
      start_date: addConditionStartDate,
      end_date: addConditionEndDate || null,
      status: addConditionStatus,
      treated_by_doctor: addConditionTreatedByDoctor,
      doctor_name: addConditionDoctorName || null,
      notes: addConditionNotes,
      for_whom: addRecordForWhom,
      dependant: addRecordForWhom === 'dependant' ? Number(addRecordDependantId) : null,
      medications,
      keep_medications: medications
        .map((m) => m.id)
        .filter((id): id is number => typeof id === 'number'),
    };

    if (editingConditionId !== null) {
      await healthRecordsService.updateHealthCondition(editingConditionId, payload, addRecordFiles);
      for (const docId of conditionRemovedDocIds) {
        await healthRecordsService.deleteHealthConditionDocument(editingConditionId, docId);
      }
    } else {
      await healthRecordsService.createHealthCondition(payload, addRecordFiles);
    }
    setConditionsRefreshKey(k => k + 1);
    return true;
  };

  const handleResolveCondition = (record: HealthConditionRecord) => {
    setConfirmDialog({
      open: true,
      title: t('health:conditions.markResolved'),
      message: t('health:conditions.resolveMessage', { name: record.condition_name }),
      variant: 'primary',
      confirmLabel: t('health:conditions.markResolved'),
      onConfirm: async () => {
        try {
          await healthRecordsService.resolveHealthCondition(record.id);
          setConditionsRefreshKey(k => k + 1);
        } catch (err) {
          showToast(getApiErrorMessage(err, t('health:conditions.resolveError')), 'error');
        }
      },
    });
  };

  const handleDeleteCondition = (record: HealthConditionRecord) => {
    setConfirmDialog({
      open: true,
      title: t('health:conditions.deleteTitle'),
      message: t('health:conditions.deleteMessage', { name: record.condition_name }),
      onConfirm: async () => {
        try {
          await healthRecordsService.deleteHealthCondition(record.id);
          setConditionsRefreshKey(k => k + 1);
        } catch (err) {
          showToast(getApiErrorMessage(err, t('health:conditions.deleteError')), 'error');
        }
      },
    });
  };

  const handleCloseAddRecord = () => {
    if (activeTab === 'Health Conditions') {
      resetConditionForm();
    }
    setIsAddingRecord(false);
    resetAddRecordForm();
  };

  const handleAddRecordFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files) {
      const validFiles = Array.from(files).filter((f) =>
        ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'].includes(f.type)
      );
      setAddRecordFiles((prev) => [...prev, ...validFiles]);
    }
  };

  const handleRemoveAddFile = (idx: number) => {
    setAddRecordFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleAddLabParam = () => {
    setAddLabParams((prev) => [
      ...prev,
      { id: Date.now().toString(), parameterName: '', result: '', unit: '', startRange: '', endRange: '' },
    ]);
  };

  const handleAddLabParamChange = (id: string, field: string, value: string) => {
    setAddLabParams((prev) =>
      prev.map((p) => (p.id === id ? { ...p, [field]: value } : p))
    );
  };

  const handleRemoveAddLabParam = (id: string) => {
    setAddLabParams((prev) => prev.filter((p) => p.id !== id));
  };

  const handleSaveAddRecord = async () => {
    setAddRecordSaving(true);
    try {
      if (activeTab === 'Health Conditions') {
        const saved = await handleSaveCondition();
        setAddRecordSaving(false);
        if (!saved) return;
        await fetchRecords(activeTab);
        setConditionsRefreshKey(k => k + 1);
        handleCloseAddRecord();
        setSaveSuccessMessage(t('health:messages.savedSuccess'));
        setTimeout(() => setSaveSuccessMessage(null), 3000);
        return;
      }

      const formData = new FormData();
      formData.append('for_whom', addRecordForWhom);

      // Bug 3 fix: Include dependant ID when saving for a dependant
      if (addRecordForWhom === 'dependant') {
        if (!addRecordDependantId) {
          showToast(t('health:messages.selectDependant'), 'warning');
          setAddRecordSaving(false);
          return;
        }
        formData.append('dependant', String(addRecordDependantId));
      }

      // Append files
      addRecordFiles.forEach((file) => {
        formData.append('documents', file);
      });

      switch (activeTab) {
        case 'Prescription & Lab Tests': {
          if (!addRecordName || !addRecordDate || !addDoctorName) {
            showToast(t('health:messages.fillRequired'), 'warning');
            setAddRecordSaving(false);
            return;
          }
          formData.append('record_name', addRecordName);
          formData.append('record_type', addRecordType);
          formData.append('doctor_name', addDoctorName);
          if (addSpecialization) formData.append('specialization', addSpecialization);
          formData.append('record_date', addRecordDate);
          if (addReason) formData.append('reason', addReason);
          // Lab parameters as JSON
          if (addLabParams.length > 0) {
            formData.append('lab_parameters_json', JSON.stringify(
              addLabParams.map((p) => ({
                parameter_name: p.parameterName,
                result: p.result,
                unit: p.unit,
                start_range: p.startRange,
                end_range: p.endRange,
              }))
            ));
          }
          await healthRecordsService.createPrescription(formData);
          break;
        }
        case 'Hospitalizations': {
          if (!addHospitalName || !addAdmissionDate || !addHospReason) {
            showToast(t('health:messages.fillRequired'), 'warning');
            setAddRecordSaving(false);
            return;
          }
          formData.append('hospitalization_type', addHospitalizationType);
          formData.append('hospital_name', addHospitalName);
          formData.append('admission_date', addAdmissionDate);
          if (addDischargeDate) formData.append('discharge_date', addDischargeDate);
          formData.append('reason', addHospReason);
          if (addHospDoctorName) formData.append('doctor_name', addHospDoctorName);
          if (addHospDiagnosis) formData.append('diagnosis', addHospDiagnosis);
          if (addHospitalizationType === 'surgery' && addHospProcedureName) formData.append('procedure_name', addHospProcedureName);
          if (addRoomType) formData.append('room_type', addRoomType);
          if (addTotalBill) formData.append('total_bill', addTotalBill);
          formData.append('insurance_claimed', addHospInsuranceClaimed ? 'true' : 'false');
          if (addHospInsuranceClaimed && addHospInsuranceAmount) formData.append('insurance_amount', addHospInsuranceAmount);
          if (addHospNotes) formData.append('notes', addHospNotes);
          await healthRecordsService.createHospitalization(formData);
          break;
        }
        case 'Medical Bills': {
          if (!addBillHospitalName || !addBillDate || !addBillType || !addBillAmount) {
            showToast(t('health:messages.fillRequired'), 'warning');
            setAddRecordSaving(false);
            return;
          }
          formData.append('hospital_name', addBillHospitalName);
          if (addBillNumber) formData.append('record_bill_number', addBillNumber);
          formData.append('bill_date', addBillDate);
          formData.append('bill_type', addBillType);
          formData.append('amount', addBillAmount);
          if (addBillPaidAmount) formData.append('paid_amount', addBillPaidAmount);
          formData.append('insurance_claimed', addBillInsuranceClaimed ? 'true' : 'false');
          if (addBillInsuranceClaimed && addBillInsuranceAmount) formData.append('insurance_amount', addBillInsuranceAmount);
          if (addBillNotes) formData.append('notes', addBillNotes);
          await healthRecordsService.createMedicalBill(formData);
          break;
        }
        case 'Vaccinations Certificates': {
          if (!addVaccineName || !addVaccinationDate) {
            showToast(t('health:messages.fillRequired'), 'warning');
            setAddRecordSaving(false);
            return;
          }
          formData.append('vaccine_name', addVaccineName);
          formData.append('vaccination_date', addVaccinationDate);
          if (addDoseNumber) formData.append('dose_number', addDoseNumber);
          if (addTotalDoses) formData.append('total_doses', addTotalDoses);
          if (addAdministeredBy) formData.append('administered_by', addAdministeredBy);
          if (addVaccineLocation) formData.append('location', addVaccineLocation);
          if (addBatchNumber) formData.append('batch_number', addBatchNumber);
          if (addNextDueDate) formData.append('next_due_date', addNextDueDate);
          if (addVaccineNotes) formData.append('notes', addVaccineNotes);
          await healthRecordsService.createVaccinationCertificate(formData);
          break;
        }
      }

      // Refresh records and close modal
      await fetchRecords(activeTab);
      handleCloseAddRecord();

      // Bug 1 fix: Show success notification
      setSaveSuccessMessage(t('health:messages.savedSuccess'));
      setTimeout(() => setSaveSuccessMessage(null), 3000);
    } catch (err) {
      console.error('Error saving record:', err);
      showToast(t('health:messages.saveFailed'), 'error');
    } finally {
      setAddRecordSaving(false);
    }
  };

  const handleShowHistory = (index: number) => {
    setHistoryVital(vitals[index]);
    setHistoryTab('history');
    setEditingRange(null);
  };

  const handleCloseHistory = () => {
    setHistoryVital(null);
    setDeletingVitalId(null);
  };

  const handleDeleteVitalHistory = (type: Vital['type'], entryId: number | undefined) => {
    if (!entryId) {
      showToast(t('health:messages.deleteFailed'), 'error');
      return;
    }
    setConfirmDialog({
      open: true,
      title: t('common:buttons.delete'),
      message: t('health:messages.deleteConfirm'),
      onConfirm: async () => {
        setDeletingVitalId(entryId);
        try {
          await healthRecordsService.deleteVital(type, entryId);
          setVitals(prev => prev.map(v => v.type === type
            ? { ...v, history: v.history.filter(entry => entry.id !== entryId) }
            : v
          ));
          setHistoryVital(prev => prev
            ? { ...prev, history: prev.history.filter(entry => entry.id !== entryId) }
            : prev
          );
          showToast(t('health:messages.deletedSuccess'), 'success');
          fetchVitals();
        } catch (err) {
          console.error('Error deleting vital record:', err);
          showToast(t('health:messages.deleteFailed'), 'error');
        } finally {
          setDeletingVitalId(null);
        }
      },
    });
  };

  const handleTabClick = (tab: string) => {
    setActiveTab(tab);
  };

  const RECORDS_VISIBLE = 3;

  const handleEdit = (index: number) => {
    const vital = vitals[index];
    setEditingIndex(index);
    if (vital.type === 'bloodPressure') {
      const parts = vital.value.replace(/[^0-9/]/g, '').split('/');
      setEditedBpSystolic(parts[0] || '');
      setEditedBpDiastolic(parts[1] || '');
      setEditedValue('');
    } else {
      setEditedValue(vital.value.replace(/[^0-9.]/g, ''));
      setEditedBpSystolic('');
      setEditedBpDiastolic('');
    }
  };

  // Get placeholder and unit label for each vital type
  const vitalInputConfig: Record<string, { placeholder: string; unit: string; type: string }> = {
    height:           { placeholder: 'e.g. 170', unit: 'cm', type: 'number' },
    weight:           { placeholder: 'e.g. 72', unit: 'kg', type: 'number' },
    heartRate:        { placeholder: 'e.g. 72', unit: 'BPM', type: 'number' },
    oxygenSaturation: { placeholder: 'e.g. 98', unit: '%', type: 'number' },
    glucose:          { placeholder: 'e.g. 110', unit: 'mg/dL', type: 'number' },
    bmi:              { placeholder: '--', unit: 'kg/m²', type: 'number' },
    bloodPressure:    { placeholder: '', unit: 'mmHg', type: 'text' },
  };

  const handleSave = async (index: number) => {
    const vital = vitals[index];
    setSavingVital(vital.type);

    try {
      if (vital.type === 'bloodPressure') {
        const sys = parseInt(editedBpSystolic);
        const dia = parseInt(editedBpDiastolic);
        if (isNaN(sys) || isNaN(dia) || sys <= 0 || dia <= 0) {
          showToast(t('health:messages.enterValidValues'), 'warning');
          return;
        }
        await healthRecordsService.addBloodPressureRecord({
          systolic: sys,
          diastolic: dia,
          unit: 'mmhg',
        });
      } else {
        const numValue = parseFloat(editedValue);
        if (isNaN(numValue) || numValue <= 0) {
          showToast(t('health:messages.enterValidNumber'), 'warning');
          return;
        }

        switch (vital.type) {
          case 'height':
            await healthRecordsService.addHeightRecord({ value: numValue, unit: 'cm' });
            break;
          case 'weight':
            await healthRecordsService.addWeightRecord({ value: numValue, unit: 'kg' });
            break;
          case 'heartRate':
            await healthRecordsService.addHeartRateRecord({ value: numValue, unit: 'bpm' });
            break;
          case 'oxygenSaturation':
            await healthRecordsService.addOxygenSaturationRecord({ value: numValue, unit: '%' });
            break;
          case 'glucose':
            await healthRecordsService.addGlucoseRecord({ value: numValue, unit: 'mg/dl' });
            break;
        }
      }

      // After saving height or weight, auto-calculate and save BMI
      if (vital.type === 'height' || vital.type === 'weight') {
        try {
          const latestHeight = vital.type === 'height'
            ? parseFloat(editedValue)
            : await healthRecordsService.getLatestHeight().then(h => h ? h.value : null);
          const latestWeight = vital.type === 'weight'
            ? parseFloat(editedValue)
            : await healthRecordsService.getLatestWeight().then(w => w ? w.value : null);

          if (latestHeight && latestWeight && latestHeight > 0) {
            const heightInMeters = (latestHeight as number) / 100;
            const bmiValue = Math.round(((latestWeight as number) / (heightInMeters * heightInMeters)) * 10) / 10;
            await healthRecordsService.addBmiRecord({ value: bmiValue, unit: 'BMI' });
          }
        } catch (bmiErr) {
          console.error('Error auto-calculating BMI:', bmiErr);
        }
      }

      // Refresh vitals after saving
      await fetchVitals();
      setEditingIndex(null);
    } catch (err) {
      console.error('Error saving vital:', err);
      showToast(t('health:messages.saveFailed'), 'error');
    } finally {
      setSavingVital(null);
    }
  };

  const handleCancelEdit = () => {
    setEditingIndex(null);
    setEditedValue("");
  };
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- dormant record carousel, kept for future re-enable
  const handleRecordPrev = () => {
    setRecordCarouselIndex((prev) =>
      prev === 0 ? Math.max(0, records.length - RECORDS_VISIBLE) : prev - 1
    );
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- dormant record carousel, kept for future re-enable
  const handleRecordNext = () => {
    setRecordCarouselIndex((prev) =>
      prev >= records.length - RECORDS_VISIBLE ? 0 : prev + 1
    );
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- dormant record carousel, kept for future re-enable
  const getVisibleRecords = () => {
    if (records.length <= RECORDS_VISIBLE) {
      return records;
    }
    return records.slice(
      recordCarouselIndex,
      recordCarouselIndex + RECORDS_VISIBLE
    );
  };


  // Helper: extract file name from URL
  const getFileNameFromUrl = (url: string) => {
    try {
      const parts = url.split('/');
      const fileName = parts[parts.length - 1];
      return decodeURIComponent(fileName.split('?')[0]);
    } catch {
      return 'Document';
    }
  };

  // Determine if current user is a dependant
  const isDependantUser = (activeProfile !== null && activeProfile.type === 'dependant') || isLoggedInAsDependant;

  // Map display record_type back to raw value (API returns display name via get_record_type_display)
  const recordTypeDisplayToValue: Record<string, string> = {
    'Health Record': 'health_record',
    'Radiology Medical Report': 'radiology_report',
    'Lab Report': 'lab_report',
    'Doctor Prescription': 'doctor_prescription',
  };

  const handleEditPrescription = (record: PrescriptionRecord) => {
    setEditRecordId(record.id);
    setEditRecordTab('Prescription & Lab Tests');
    setEditRecordForWhom(record.for_whom);
    setEditRecordDependantId(record.dependant || '');
    setEditRecordName(record.record_name);
    // record_type comes as display name from API, map back to raw value
    setEditRecordType(recordTypeDisplayToValue[record.record_type] || record.record_type);
    setEditDoctorName(record.doctor_name);
    setEditSpecialization(record.specialization || '');
    setEditRecordDate(record.record_date);
    setEditReason(record.reason || '');
    setEditExistingDocs(record.documents || []);
    setEditRemovedDocIds([]);
    setEditRecordFiles([]);
    const params = record.parameters?.map(p => ({
      id: String(p.id), parameterName: p.parameter_name, result: p.result,
      unit: p.unit, startRange: p.start_range || '', endRange: p.end_range || '',
    })) || [];
    setEditLabParams(params);
    setEditShowLabParams(params.length > 0);
    setShowEditModal(true);
  };

  // Map display hospitalization_type back to raw value
  const hospTypeDisplayToValue: Record<string, string> = {
    'Inpatient': 'inpatient',
    'Outpatient': 'outpatient',
    'Emergency': 'emergency',
    'Surgery': 'surgery',
    'Day Care': 'day_care',
  };

  const handleEditHospitalization = (record: HospitalizationRecord) => {
    setEditRecordId(record.id);
    setEditRecordTab('Hospitalizations');
    setEditRecordForWhom(record.for_whom);
    setEditRecordDependantId(record.dependant || '');
    // hospitalization_type comes as display name from API, map back to raw value
    const rawHospType = hospTypeDisplayToValue[record.hospitalization_type || ''] || record.hospitalization_type || 'inpatient';
    setEditHospitalizationType(rawHospType);
    setEditHospitalName(record.hospital_name);
    setEditAdmissionDate(record.admission_date);
    setEditDischargeDate(record.discharge_date || '');
    setEditHospReason(record.reason || '');
    setEditHospDoctorName(record.doctor_name || '');
    setEditHospDiagnosis(record.diagnosis || '');
    setEditHospProcedureName(record.procedure_name || '');
    setEditRoomType(record.room_type || '');
    setEditTotalBill(record.total_bill ? String(record.total_bill) : '');
    setEditHospInsuranceClaimed(record.insurance_claimed || false);
    setEditHospInsuranceAmount(record.insurance_amount ? String(record.insurance_amount) : '');
    setEditHospNotes(record.notes || '');
    setEditExistingDocs(record.documents || []);
    setEditRemovedDocIds([]);
    setEditRecordFiles([]);
    setShowEditModal(true);
  };

  const handleEditMedicalBill = (record: MedicalBillRecord) => {
    setEditRecordId(record.id);
    setEditRecordTab('Medical Bills');
    setEditRecordForWhom(record.for_whom);
    setEditRecordDependantId(record.dependant || '');
    setEditBillHospitalName(record.hospital_name);
    setEditBillNumber(record.record_bill_number || '');
    setEditBillDate(record.bill_date);
    setEditBillType(record.bill_type);
    setEditBillAmount(String(record.amount));
    setEditBillPaidAmount(record.paid_amount ? String(record.paid_amount) : '');
    setEditBillInsuranceClaimed(record.insurance_claimed || false);
    setEditBillInsuranceAmount(record.insurance_amount ? String(record.insurance_amount) : '');
    setEditBillNotes(record.notes || '');
    setEditExistingDocs(record.documents || []);
    setEditRemovedDocIds([]);
    setEditRecordFiles([]);
    setShowEditModal(true);
  };

  const handleEditVaccination = (record: VaccinationCertificate) => {
    setEditRecordId(record.id);
    setEditRecordTab('Vaccinations Certificates');
    setEditRecordForWhom(record.for_whom);
    setEditRecordDependantId(record.dependant || '');
    setEditVaccineName(record.vaccine_name);
    setEditVaccinationDate(record.vaccination_date);
    setEditDoseNumber(record.dose_number ? String(record.dose_number) : '');
    setEditTotalDoses(record.total_doses ? String(record.total_doses) : '');
    setEditAdministeredBy(record.administered_by || '');
    setEditVaccineLocation(record.location || '');
    setEditBatchNumber(record.batch_number || '');
    setEditNextDueDate(record.next_due_date || '');
    setEditVaccineNotes(record.notes || '');
    setEditExistingDocs(record.documents || []);
    setEditRemovedDocIds([]);
    setEditRecordFiles([]);
    setShowEditModal(true);
  };

  const handleEditFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files) {
      const validFiles = Array.from(files).filter((file) =>
        ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'].includes(file.type)
      );
      setEditRecordFiles((prev) => [...prev, ...validFiles]);
    }
  };

  const handleRemoveEditFile = (idx: number) => {
    setEditRecordFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleRemoveExistingDoc = (docId: number) => {
    setEditRemovedDocIds((prev) => [...prev, docId]);
    setEditExistingDocs((prev) => prev.filter((d) => d.id !== docId));
  };

  const handleEditLabParamChange = (id: string, field: string, value: string) => {
    setEditLabParams((prev) => prev.map((p) => (p.id === id ? { ...p, [field]: value } : p)));
  };

  const handleAddEditLabParam = () => {
    setEditLabParams((prev) => [
      ...prev,
      { id: Date.now().toString(), parameterName: '', result: '', unit: '', startRange: '', endRange: '' },
    ]);
  };

  const handleRemoveEditLabParam = (id: string) => {
    setEditLabParams((prev) => prev.filter((p) => p.id !== id));
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setEditRecordId(null);
    setEditRecordTab('');
    setEditRecordFiles([]);
    setEditExistingDocs([]);
    setEditRemovedDocIds([]);
  };

  const handleUpdate = async () => {
    if (!editRecordId) return;
    setEditRecordSaving(true);
    try {
      // Validate dependant selection
      if (editRecordForWhom === 'dependant' && !editRecordDependantId) {
        showToast(t('health:messages.selectDependant'), 'warning');
        setEditRecordSaving(false);
        return;
      }

      // Delete removed documents via separate API calls first
      for (const docId of editRemovedDocIds) {
        try {
          switch (editRecordTab) {
            case 'Prescription & Lab Tests':
              await healthRecordsService.deletePrescriptionDocument(editRecordId, docId);
              break;
            case 'Hospitalizations':
              await healthRecordsService.deleteHospitalizationDocument(editRecordId, docId);
              break;
            case 'Medical Bills':
              await healthRecordsService.deleteMedicalBillDocument(editRecordId, docId);
              break;
            case 'Vaccinations Certificates':
              await healthRecordsService.deleteVaccinationDocument(editRecordId, docId);
              break;
          }
        } catch (err) {
          console.error(`Failed to delete document ${docId}:`, err);
        }
      }

      const formData = new FormData();

      // Append new files
      editRecordFiles.forEach((file) => {
        formData.append('documents', file);
      });

      switch (editRecordTab) {
        case 'Prescription & Lab Tests': {
          if (!editRecordName || !editRecordDate || !editDoctorName) {
            showToast(t('health:messages.fillRequired'), 'warning');
            setEditRecordSaving(false);
            return;
          }
          // Prescription backend uses _extract_payload to parse a JSON 'data' blob
          // This allows sending keep_parameters=[] to clear old params, and parameters=[] for new ones
          const payload: Record<string, any> = {
            record_name: editRecordName,
            record_type: editRecordType,
            doctor_name: editDoctorName,
            record_date: editRecordDate,
            for_whom: editRecordForWhom,
            keep_parameters: [], // Delete all existing parameters
            parameters: editLabParams.map((p) => ({
              parameter_name: p.parameterName,
              result: p.result,
              unit: p.unit,
              start_range: p.startRange,
              end_range: p.endRange,
            })),
          };
          if (editSpecialization) payload.specialization = editSpecialization;
          if (editReason) payload.reason = editReason;
          if (editRecordForWhom === 'dependant') payload.dependant = Number(editRecordDependantId);

          formData.append('data', JSON.stringify(payload));
          await healthRecordsService.updatePrescription(editRecordId, formData);
          break;
        }
        case 'Hospitalizations': {
          if (!editHospitalName || !editAdmissionDate || !editHospReason) {
            showToast(t('health:messages.fillRequired'), 'warning');
            setEditRecordSaving(false);
            return;
          }
          formData.append('for_whom', editRecordForWhom);
          if (editRecordForWhom === 'dependant') formData.append('dependant', String(editRecordDependantId));
          formData.append('hospitalization_type', editHospitalizationType);
          formData.append('hospital_name', editHospitalName);
          formData.append('admission_date', editAdmissionDate);
          if (editDischargeDate) formData.append('discharge_date', editDischargeDate);
          formData.append('reason', editHospReason);
          if (editHospDoctorName) formData.append('doctor_name', editHospDoctorName);
          if (editHospDiagnosis) formData.append('diagnosis', editHospDiagnosis);
          if (editHospitalizationType === 'surgery' && editHospProcedureName) formData.append('procedure_name', editHospProcedureName);
          if (editRoomType) formData.append('room_type', editRoomType);
          if (editTotalBill) formData.append('total_bill', editTotalBill);
          formData.append('insurance_claimed', editHospInsuranceClaimed ? 'true' : 'false');
          if (editHospInsuranceClaimed && editHospInsuranceAmount) formData.append('insurance_amount', editHospInsuranceAmount);
          if (editHospNotes) formData.append('notes', editHospNotes);
          await healthRecordsService.updateHospitalization(editRecordId, formData);
          break;
        }
        case 'Medical Bills': {
          if (!editBillHospitalName || !editBillDate || !editBillType || !editBillAmount) {
            showToast(t('health:messages.fillRequired'), 'warning');
            setEditRecordSaving(false);
            return;
          }
          formData.append('for_whom', editRecordForWhom);
          if (editRecordForWhom === 'dependant') formData.append('dependant', String(editRecordDependantId));
          formData.append('hospital_name', editBillHospitalName);
          if (editBillNumber) formData.append('record_bill_number', editBillNumber);
          formData.append('bill_date', editBillDate);
          formData.append('bill_type', editBillType);
          formData.append('amount', editBillAmount);
          if (editBillPaidAmount) formData.append('paid_amount', editBillPaidAmount);
          formData.append('insurance_claimed', editBillInsuranceClaimed ? 'true' : 'false');
          if (editBillInsuranceClaimed && editBillInsuranceAmount) formData.append('insurance_amount', editBillInsuranceAmount);
          if (editBillNotes) formData.append('notes', editBillNotes);
          await healthRecordsService.updateMedicalBill(editRecordId, formData);
          break;
        }
        case 'Vaccinations Certificates': {
          if (!editVaccineName || !editVaccinationDate) {
            showToast(t('health:messages.fillRequired'), 'warning');
            setEditRecordSaving(false);
            return;
          }
          formData.append('for_whom', editRecordForWhom);
          if (editRecordForWhom === 'dependant') formData.append('dependant', String(editRecordDependantId));
          formData.append('vaccine_name', editVaccineName);
          formData.append('vaccination_date', editVaccinationDate);
          if (editDoseNumber) formData.append('dose_number', editDoseNumber);
          if (editTotalDoses) formData.append('total_doses', editTotalDoses);
          if (editAdministeredBy) formData.append('administered_by', editAdministeredBy);
          if (editVaccineLocation) formData.append('location', editVaccineLocation);
          if (editBatchNumber) formData.append('batch_number', editBatchNumber);
          if (editNextDueDate) formData.append('next_due_date', editNextDueDate);
          if (editVaccineNotes) formData.append('notes', editVaccineNotes);
          await healthRecordsService.updateVaccinationCertificate(editRecordId, formData);
          break;
        }
      }

      await fetchRecords(editRecordTab);
      handleCloseEditModal();
      showToast(t('health:messages.updatedSuccess'), 'success');
    } catch (err) {
      console.error('Error updating record:', err);
      showToast(t('health:messages.updateFailed'), 'error');
    } finally {
      setEditRecordSaving(false);
    }
  };

  // ─── Compare records ───
  const compareModuleForTab: Record<string, string> = {
    'Prescription & Lab Tests': 'prescriptions',
    'Hospitalizations': 'hospitalizations',
    'Medical Bills': 'medical_bills',
    'Vaccinations Certificates': 'vaccination_certificates',
    'Health Conditions': 'health_conditions',
  };
  const [showCompareModal, setShowCompareModal] = useState(false);
  const [compareSelectedIds, setCompareSelectedIds] = useState<number[]>([]);
  const [compareResult, setCompareResult] = useState<CompareResult | null>(null);
  const [compareLoading, setCompareLoading] = useState(false);
  const [showDiffsOnly, setShowDiffsOnly] = useState(false);

  // Records of the currently active tab, for the compare picker
  const compareCandidates: { id: number; label: string; sub: string }[] = (() => {
    switch (activeTab) {
      case 'Prescription & Lab Tests':
        return prescriptions.map(r => ({ id: r.id, label: r.record_name, sub: formatDateOnly(r.record_date) }));
      case 'Hospitalizations':
        return hospitalizations.map(r => ({ id: r.id, label: r.hospital_name, sub: formatDateOnly(r.admission_date) }));
      case 'Medical Bills':
        return medicalBills.map(r => ({ id: r.id, label: r.hospital_name, sub: formatDateOnly(r.bill_date) }));
      case 'Vaccinations Certificates':
        return vaccinations.map(r => ({ id: r.id, label: r.vaccine_name, sub: formatDateOnly(r.vaccination_date) }));
      case 'Health Conditions':
        return conditionsList.map(r => ({ id: r.id, label: r.condition_name, sub: formatDateOnly(r.start_date) }));
      default:
        return [];
    }
  })();

  const openCompareModal = () => {
    setCompareSelectedIds([]);
    setCompareResult(null);
    setShowDiffsOnly(false);
    setShowCompareModal(true);
  };

  const toggleCompareId = (id: number) => {
    setCompareSelectedIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const handleRunCompare = async () => {
    const moduleName = compareModuleForTab[activeTab];
    if (!moduleName || compareSelectedIds.length < 2) return;
    setCompareLoading(true);
    try {
      const result = await healthRecordsService.compareRecords(moduleName, compareSelectedIds);
      setCompareResult(result);
    } catch (err) {
      console.error('Error comparing records:', err);
      showToast('Failed to compare records. Please try again.', 'error');
    } finally {
      setCompareLoading(false);
    }
  };

  // ─── Delete a saved record (prescription / hospitalization / medical bill / vaccination) ───
  const [deletingRecordId, setDeletingRecordId] = useState<number | null>(null);

  const handleDeleteRecord = (
    type: 'Prescription & Lab Tests' | 'Hospitalizations' | 'Medical Bills' | 'Vaccinations Certificates',
    id: number,
  ) => {
    setConfirmDialog({
      open: true,
      title: t('common:buttons.delete'),
      message: t('health:messages.deleteConfirm'),
      onConfirm: async () => {
        setDeletingRecordId(id);
        try {
          switch (type) {
            case 'Prescription & Lab Tests':
              await healthRecordsService.deletePrescription(id);
              break;
            case 'Hospitalizations':
              await healthRecordsService.deleteHospitalization(id);
              break;
            case 'Medical Bills':
              await healthRecordsService.deleteMedicalBill(id);
              break;
            case 'Vaccinations Certificates':
              await healthRecordsService.deleteVaccinationCertificate(id);
              break;
          }
          await fetchRecords(type);
          showToast(t('health:messages.deletedSuccess'), 'success');
        } catch (err) {
          console.error('Error deleting record:', err);
          showToast(t('health:messages.deleteFailed'), 'error');
        } finally {
          setDeletingRecordId(null);
        }
      },
    });
  };

  // Compute hero stat counts
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- superseded by the inline stat below, kept for reference
  const totalRecords = prescriptions.length + hospitalizations.length + medicalBills.length + vaccinations.length;
  const activeTabConfig = tabConfig.find((t) => t.key === activeTab);

  // ─── Main Page Render ───
  return (
    <div className="health-records-page">

      {/* ══════════ WELCOME BANNER ══════════ */}
      <section className="hr-welcome-banner">
        <Container>
          <BackButton />
          <div className="hr-welcome-layout">
            <div className="hr-welcome-text">
              <h1 className="hr-welcome-title">
                <FontAwesomeIcon icon={faStethoscope} />
                {t('health:title')}
              </h1>
              <p className="hr-welcome-subtitle">
                Manage all your medical records, prescriptions, lab reports, and vitals in one secure place.
              </p>
            </div>
            <div className="hr-welcome-actions">
              <button className="hr-welcome-btn primary" onClick={() => {
                if (activeTab === 'Health Conditions') {
                  resetConditionForm();
                }
                setIsAddingRecord(true);
              }}>
                <FontAwesomeIcon icon={faPlus} />
                {t('common:buttons.add')} Record
              </button>
            </div>
          </div>
        </Container>
      </section>

      {/* ══════════ STATS ROW ══════════ */}
      <section className="hr-stats-section">
        <Container>
          <div className="hr-stats-row">
            {([
              { icon: faClipboardList, color: '#06b6d4', value: prescriptions.length + hospitalizations.length + vaccinations.length, label: 'Total Records' },
              { icon: faPrescriptionBottleAlt, color: '#06b6d4', value: prescriptions.length, label: 'Prescriptions' },
              { icon: faHospital, color: '#f59e0b', value: hospitalizations.length, label: 'Hospitalizations' },
              { icon: faSyringe, color: '#8b5cf6', value: vaccinations.length, label: 'Vaccinations' },
            ] as { icon: IconDefinition; color: string; value: number; label: string }[]).map((item, idx) => (
              <div className="hr-stat-card" key={idx}>
                <div className="hr-stat-icon" style={{ background: `${item.color}12`, color: item.color }}>
                  <FontAwesomeIcon icon={item.icon} />
                </div>
                <div className="hr-stat-info">
                  <span className="hr-stat-value">{item.value}</span>
                  <span className="hr-stat-label">{item.label}</span>
                </div>
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* ══════════ DISEASE RISK WIDGET ══════════ */}
      {canAccess('disease-prediction') && riskProfile && (() => {
        const cat = riskProfile.overall_risk_category;
        const colorMap: Record<string, string> = { low: '#10b981', moderate: '#f59e0b', high: '#f97316', critical: '#ef4444' };
        const accentColor = colorMap[cat] || '#f59e0b';
        const DISEASE_LABELS: Record<string, string> = {
          diabetes: 'Diabetes',
          cardiovascular: 'Cardiovascular',
          hypertension: 'Hypertension',
          obesity: 'Obesity',
          respiratory: 'Respiratory',
          mental_health: 'Mental Health',
          liver: 'Liver',
          kidney: 'Kidney',
          thyroid: 'Thyroid',
          metabolic_syndrome: 'Metabolic Syndrome',
        };
        const humanizeDisease = (k: string) =>
          DISEASE_LABELS[k] || k.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
        const topFactors = riskProfile.disease_details
          ? Object.entries(riskProfile.disease_details)
              .filter(([, d]: [string, any]) => d.score > 30)
              .sort(([, a]: [string, any], [, b]: [string, any]) => b.score - a.score)
              .slice(0, 3)
              .map(([name]: [string, any]) => humanizeDisease(name))
          : [];
        return (
          <section className="hr-risk-widget-section">
            <Container>
              <div className="hr-risk-widget" onClick={() => navigate('/disease-prediction')} style={{ borderLeftColor: accentColor }}>
                <div className="hr-risk-widget-icon" style={{ background: `${accentColor}14`, color: accentColor }}>
                  <FontAwesomeIcon icon={faChartBar} />
                </div>
                <div className="hr-risk-widget-body">
                  <span className="hr-risk-widget-title">Disease Risk Profile</span>
                  {topFactors.length > 0 && (
                    <span className="hr-risk-widget-factors">Elevated: {topFactors.join(', ')}</span>
                  )}
                </div>
                <div className="hr-risk-widget-right">
                  <span className="hr-risk-widget-score" style={{ color: accentColor }}>{riskProfile.overall_risk_score}</span>
                  <span className="hr-risk-widget-cat" style={{ background: `${accentColor}14`, color: accentColor }}>{cat.charAt(0).toUpperCase() + cat.slice(1)}</span>
                </div>
                <FontAwesomeIcon icon={faChevronRight} className="hr-risk-widget-arrow" />
              </div>
            </Container>
          </section>
        );
      })()}

      {/* ══════════ TAB CONTENT ══════════ */}
      <Container>
        <div className="hr-tab-content" role="tabpanel">

          {/* Bug 1 fix: Success notification banner */}
          {saveSuccessMessage && (
            <div className="hr-success-banner" style={{
              background: '#ecfdf5',
              border: '1px solid #10b981',
              borderRadius: '8px',
              padding: '12px 20px',
              marginBottom: '16px',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              color: '#065f46',
              fontWeight: 500,
              animation: 'fadeIn 0.3s ease',
            }}>
              <FontAwesomeIcon icon={faCheck} style={{ color: '#10b981' }} />
              <span>{saveSuccessMessage}</span>
              <button
                onClick={() => setSaveSuccessMessage(null)}
                style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: '#065f46', fontSize: '16px' }}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
          )}

          {/* Vitals Error Banner */}
          {vitalsError && (
            <div className="hr-error-banner">
              <span>{vitalsError}</span>
              <button onClick={fetchVitals}>Retry</button>
            </div>
          )}

          {/* ── Vitals Carousel ── */}
          <div className="hr-vitals-carousel-wrapper" style={{ position: 'relative' }}>
            {vitalsLoading && (
              <div className="hr-vitals-loading-overlay">
                <FontAwesomeIcon icon={faSpinner} spin size="2x" style={{ color: '#06b6d4' }} />
              </div>
            )}
            <button
              className="hr-vitals-arrow hr-vitals-arrow-left"
              onClick={() => setVitalsCarouselIndex(prev => prev <= 0 ? Math.max(vitals.length - VITALS_VISIBLE, 0) : prev - 1)}
            >
              <FontAwesomeIcon icon={faChevronLeft} />
            </button>
            <div className="hr-vitals-viewport">
              <div
                className="hr-vitals-track"
                style={{ transform: `translateX(-${vitalsCarouselIndex * (100 / VITALS_VISIBLE)}%)` }}
              >
                {vitals.map((vital, index) => {
                  const vitalStyle = vitalIconMap[vital.type] || { icon: faFileMedicalAlt, color: '#ec4899' };
                  return (
                    <div className="hr-vital-slide" key={index}>
                      <div className="hr-vital-card" style={{ '--vital-color': vitalStyle.color } as React.CSSProperties}>
                        <div className="hr-vital-header">
                          <div className="hr-vital-icon-wrap">
                            <FontAwesomeIcon icon={vitalStyle.icon} />
                          </div>
                          <h3>{vital.name}</h3>
                        </div>
                        <div className="hr-vital-body">
                          {editingIndex === index ? (
                            <>
                              {vital.type === 'bloodPressure' ? (
                                <div className="hr-vital-bp-inputs">
                                  <input
                                    type="number"
                                    value={editedBpSystolic}
                                    onChange={(e) => setEditedBpSystolic(e.target.value)}
                                    className="hr-vital-edit-input"
                                    placeholder={t('health:vitals.systolicPlaceholder')}
                                    disabled={savingVital === vital.type}
                                  />
                                  <span className="hr-vital-bp-separator">/</span>
                                  <input
                                    type="number"
                                    value={editedBpDiastolic}
                                    onChange={(e) => setEditedBpDiastolic(e.target.value)}
                                    className="hr-vital-edit-input"
                                    placeholder={t('health:vitals.diastolicPlaceholder')}
                                    disabled={savingVital === vital.type}
                                  />
                                  <span className="hr-vital-input-unit">mmHg</span>
                                </div>
                              ) : vital.type === 'bmi' ? (
                                <div className="hr-vital-bmi-auto">
                                  <p className="hr-vital-bmi-note">BMI is auto-calculated from your Height and Weight.</p>
                                  {vital.bmiNumeric ? (
                                    <div className="hr-bmi-display">
                                  <span className={`hr-bmi-category ${getBmiCategory(vital.bmiCategory).className}`}>
                                    {vital.bmiCategoryLabel || getBmiCategory(vital.bmiCategory).label}
                                      </span>
                                      <span className="hr-bmi-value">{vital.value}</span>
                                    </div>
                                  ) : (
                                    <p className="hr-vital-value">{vital.value}</p>
                                  )}
                                </div>
                              ) : (
                                <div className="hr-vital-input-group">
                                  <input
                                    type="number"
                                    value={editedValue}
                                    onChange={(e) => setEditedValue(e.target.value)}
                                    className="hr-vital-edit-input"
                                    placeholder={vitalInputConfig[vital.type]?.placeholder || ''}
                                    disabled={savingVital === vital.type}
                                  />
                                  <span className="hr-vital-input-unit">{vitalInputConfig[vital.type]?.unit || ''}</span>
                                </div>
                              )}
                              <div className="hr-vital-edit-actions">
                                <button
                                  onClick={() => handleSave(index)}
                                  className="hr-vital-save-btn"
                                  disabled={savingVital === vital.type}
                                >
                                  {savingVital === vital.type ? (
                                    <><FontAwesomeIcon icon={faSpinner} spin /> Saving...</>
                                  ) : (
                                    'Update'
                                  )}
                                </button>
                                <button
                                  onClick={() => handleShowHistory(index)}
                                  className="hr-vital-history-btn"
                                  disabled={savingVital === vital.type}
                                >
                                  <FontAwesomeIcon icon={faHistory} />
                                </button>
                                <button
                                  onClick={handleCancelEdit}
                                  className="hr-vital-cancel-btn"
                                  disabled={savingVital === vital.type}
                                >
                                  {t('common:buttons.cancel')}
                                </button>
                              </div>
                            </>
                          ) : (
                            <>
                              {vital.type === 'bmi' && vital.bmiNumeric ? (
                                <div className="hr-bmi-display">
                                  <span className={`hr-bmi-category ${getBmiCategory(vital.bmiCategory).className}`}>
                                    {vital.bmiCategoryLabel || getBmiCategory(vital.bmiCategory).label}
                                  </span>
                                  <span className="hr-bmi-value">{vital.value}</span>
                                </div>
                              ) : (
                                <p className="hr-vital-value">{vital.value}</p>
                              )}
                              <p className="hr-vital-last-updated">
                                Last updated: {vital.lastUpdated}
                              </p>
                              <div className="hr-vital-card-actions">
                                {vital.history.length > 0 && (
                                  <button
                                    className="hr-vital-history-btn"
                                    onClick={() => handleShowHistory(index)}
                                    title="View history"
                                  >
                                    <FontAwesomeIcon icon={faHistory} />
                                  </button>
                                )}
                                {vital.type === 'bmi' ? (
                                  <span className="hr-vital-auto-badge-inline">Auto</span>
                                ) : (
                                  <>
                                    <button
                                      className="hr-vital-edit-btn-inline"
                                      onClick={() => handleEdit(index)}
                                      title="Edit"
                                    >
                                      <FontAwesomeIcon icon={faPencilAlt} />
                                    </button>
                                    {vital.history.length > 0 && vital.history[0]?.id !== undefined && (
                                      <button
                                        className="hr-vital-edit-btn-inline"
                                        onClick={() => handleDeleteVitalHistory(vital.type, vital.history[0].id)}
                                        title="Delete latest entry"
                                        disabled={deletingVitalId === vital.history[0].id}
                                        style={{ color: '#dc2626' }}
                                      >
                                        <FontAwesomeIcon icon={deletingVitalId === vital.history[0].id ? faSpinner : faTrashAlt} spin={deletingVitalId === vital.history[0].id} />
                                      </button>
                                    )}
                                  </>
                                )}
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            <button
              className="hr-vitals-arrow hr-vitals-arrow-right"
              onClick={() => setVitalsCarouselIndex(prev => prev >= vitals.length - VITALS_VISIBLE ? 0 : prev + 1)}
            >
              <FontAwesomeIcon icon={faChevronRight} />
            </button>
          </div>
          <div className="hr-vitals-dots">
            {Array.from({ length: Math.max(vitals.length - VITALS_VISIBLE + 1, 1) }).map((_, i) => (
              <span key={i} className={`hr-vitals-dot${i === vitalsCarouselIndex ? ' active' : ''}`} onClick={() => setVitalsCarouselIndex(i)} />
            ))}
          </div>

          {/* ── 3-Month Vitals Comparison ── */}
          {!vitalsLoading && comparisonRows.some(r => r.mo1 !== '—' || r.mo2 !== '—' || r.mo3 !== '—') && (
            <div className="hr-comparison-section">
              <div className="hr-comparison-header">
                <h3 className="hr-comparison-title">
                  <FontAwesomeIcon icon={faChartBar} /> 3-Month Comparison
                </h3>
                <button className="hr-comparison-toggle" onClick={() => setShowComparison(prev => !prev)}>
                  {showComparison ? 'Hide' : 'View Comparison'}
                </button>
              </div>
              {showComparison && <div className="hr-comparison-table-wrap">
                <table className="hr-comparison-table">
                  <thead>
                    <tr>
                      <th>Vital</th>
                      <th>Normal Range</th>
                      <th>Current</th>
                      <th>1 Mo Ago</th>
                      <th>2 Mo Ago</th>
                      <th>3 Mo Ago</th>
                      <th>Trend</th>
                    </tr>
                  </thead>
                  <tbody>
                    {comparisonRows.map(row => {
                      return (
                      <tr key={row.type}>
                        <td className="hr-comp-vital-name">{row.name}</td>
                        <td className="hr-comp-range">{row.rangeDisplay}</td>
                        <td className="hr-comp-current">{row.current}</td>
                        <td>{row.mo1}</td>
                        <td>{row.mo2}</td>
                        <td>{row.mo3}</td>
                        <td>
                          {row.trend === 'up' ? (
                            <FontAwesomeIcon icon={faArrowUp} style={{ color: row.trendColor }} />
                          ) : row.trend === 'down' ? (
                            <FontAwesomeIcon icon={faArrowDown} style={{ color: row.trendColor }} />
                          ) : (
                            <span className="hr-trend-stable">—</span>
                          )}
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>}
            </div>
          )}

          {/* ── Tab Section Header with embedded navigation ── */}
          {activeTabConfig && (
            <div className={`hr-tab-header ${activeTabConfig.headerClass}`}>
              <div className="hr-tab-header-top">
                <div className="hr-tab-header-text">
                  <h2>
                    <FontAwesomeIcon icon={activeTabConfig.icon} className={`hr-tab-header-icon ${activeTabConfig.iconClass}`} />
                    {activeTabConfig.headerTitle}
                  </h2>
                  <p>{activeTabConfig.headerDesc}</p>
                </div>
                <div className="hr-tab-header-img">
                  <img src="/Fitness-Profile.png" alt={activeTabConfig.headerTitle} />
                </div>
              </div>
              <div className="hr-tabs-container" role="tablist" aria-label="Health record sections">
                {tabConfig.map((tab) => (
                  <button
                    key={tab.key}
                    className={`hr-tab-btn ${activeTab === tab.key ? 'active' : ''}`}
                    style={activeTab === tab.key ? { '--tab-color': tab.color } as React.CSSProperties : {}}
                    onClick={() => handleTabClick(tab.key)}
                    role="tab"
                    aria-selected={activeTab === tab.key}
                    aria-label={tab.label}
                  >
                    <FontAwesomeIcon icon={tab.icon} className="hr-tab-icon" />
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* ── Filters Row ── */}
          <div className="hr-filters-row">
            <input
              type="text"
              placeholder={
                activeTab === 'Vaccinations Certificates' ? 'Vaccine Name'
                : activeTab === 'Health Conditions' ? t('health:conditions.nameLabel')
                : 'Doctor/Hospital Name'
              }
              className="hr-filter-input"
              value={filterName}
              onChange={(e) => setFilterName(e.target.value)}
            />
            <div className="hr-date-filter-wrap">
              <input
                ref={inputRef1}
                type="date"
                placeholder="Start Date"
                className="hr-date-input"
                value={filterStartDate}
                onChange={(e) => setFilterStartDate(e.target.value)}
              />
              <FontAwesomeIcon
                icon={faCalendarAlt}
                className="hr-date-filter-icon"
                onClick={() => openDatepicker(inputRef1)}
              />
            </div>
            <div className="hr-date-filter-wrap">
              <input
                ref={inputRef2}
                type="date"
                placeholder="End Date"
                className="hr-date-input"
                value={filterEndDate}
                onChange={(e) => setFilterEndDate(e.target.value)}
              />
              <FontAwesomeIcon
                icon={faCalendarAlt}
                className="hr-date-filter-icon"
                onClick={() => openDatepicker(inputRef2)}
              />
            </div>
            {!isDependantUser && (
              <select
                className="hr-filter-select"
                value={filterForWhom}
                onChange={(e) => setFilterForWhom(e.target.value as '' | 'self' | 'dependant')}
              >
                <option value="">All</option>
                <option value="self">Self</option>
                <option value="dependant">Dependant</option>
              </select>
            )}
          </div>

          {/* ── Actions Row ── */}
          <div className="hr-actions-row" ref={addRecordSectionRef}>
            <div className="hr-main-actions">
              <button
                className="hr-action-btn hr-action-btn-primary"
                onClick={() => {
                  if (activeTab === 'Health Conditions') resetConditionForm();
                  setIsAddingRecord(true);
                }}
              >
                <FontAwesomeIcon icon={faPlus} /> {t('common:buttons.add')} Record
              </button>
              <button className="hr-action-btn hr-action-btn-outline" onClick={openCompareModal}>
                <FontAwesomeIcon icon={faChartBar} /> Compare Records
              </button>
              {canAccess('health-insights') && (
                <button className="hr-action-btn hr-action-btn-outline" onClick={() => navigate('/health-summary')}>
                  <FontAwesomeIcon icon={faFileMedicalAlt} /> Health Insights
                </button>
              )}
            </div>
          </div>

          {/* ── Record Cards ── */}
          <div className="hr-records-grid">
            {/* Loading State */}
            {recordsLoading && (
              <div className="hr-loading-state">
                <FontAwesomeIcon icon={faSpinner} spin size="2x" style={{ color: '#06b6d4' }} />
                <p>Loading records...</p>
              </div>
            )}

            {/* Error State */}
            {recordsError && (
              <div className="hr-error-banner" style={{ gridColumn: '1 / -1' }}>
                <span>{recordsError}</span>
                <button onClick={() => fetchRecords(activeTab)}>Retry</button>
              </div>
            )}

            {/* ─── MEDICAL BILLS SUMMARY ─── */}
            {!recordsLoading && !recordsError && activeTab === "Medical Bills" && (() => {
              const total = filteredMedicalBills.reduce((s, r) => s + (Number(r.amount) || 0), 0);
              const paid = filteredMedicalBills.reduce((s, r) => s + (Number(r.paid_amount) || 0), 0);
              const pending = total - paid;
              const summaryItems = [
                { label: 'Total Amount', value: total, cls: 'hr-bills-total', icon: faFileInvoiceDollar },
                { label: 'Paid', value: paid, cls: 'hr-bills-paid', icon: faCheck },
                { label: 'Pending', value: pending, cls: 'hr-bills-pending', icon: faExclamationTriangle },
              ];
              return (
                <div className="hr-bills-summary-grid">
                  {summaryItems.map((item) => (
                    <div key={item.label} className={`hr-bills-summary-card ${item.cls}`}>
                      <div className="hr-bills-summary-icon">
                        <FontAwesomeIcon icon={item.icon} />
                      </div>
                      <div className="hr-bills-summary-meta">
                        <span className="hr-bills-summary-label">{item.label}</span>
                        <span className="hr-bills-summary-value">₹{item.value.toLocaleString('en-IN')}</span>
                      </div>
                    </div>
                  ))}
                </div>
              );
            })()}

            {/* ─── PRESCRIPTIONS TAB ─── */}
            {!recordsLoading && !recordsError && activeTab === "Prescription & Lab Tests" && paginatedPrescriptions.length === 0 && (
              <div className="hr-empty-state">
                <div className="hr-empty-state-icon">
                  <FontAwesomeIcon icon={faPrescriptionBottleAlt} />
                </div>
                <p className="hr-empty-state-text">No prescriptions or lab tests found.</p>
                <button className="hr-action-btn hr-action-btn-primary" onClick={() => setIsAddingRecord(true)}>
                  <FontAwesomeIcon icon={faPlus} /> Add Your First Record
                </button>
              </div>
            )}

            {!recordsLoading && !recordsError && activeTab === "Prescription & Lab Tests" && paginatedPrescriptions.map((record) => (
              <div
                key={record.id}
                className="hr-record-card"
                style={{ '--card-accent': 'var(--hr-color-prescriptions)' } as React.CSSProperties}
              >
                <div className="hr-record-card-header">
                  <div>
                    <strong className="hr-record-patient-name">
                      {record.dependant_data?.name || (record.for_whom === 'self' ? 'Self' : 'Dependant')}
                    </strong>
                    <p className="hr-record-patient-relation">
                      {record.for_whom === 'self' ? 'Self' : record.dependant_data?.relationship_name || record.dependant_data?.relationship || ''}
                    </p>
                  </div>
                  <div className="hr-record-actions">
                    <button className="hr-record-edit-btn" onClick={() => handleEditPrescription(record)} title={t('common:buttons.edit')}>
                      <FontAwesomeIcon icon={faPencilAlt} />
                    </button>
                    <button className="hr-record-delete-btn" onClick={() => handleDeleteRecord('Prescription & Lab Tests', record.id)} disabled={deletingRecordId === record.id} title={t('common:buttons.delete')}>
                      <FontAwesomeIcon icon={deletingRecordId === record.id ? faSpinner : faTrashAlt} spin={deletingRecordId === record.id} />
                    </button>
                  </div>
                </div>
                <div className="hr-record-card-body">
                  <div className="hr-record-field-row">
                    <span className="hr-record-field-label">{t('health:labReports.testName')}:</span>
                    <span className="hr-record-field-value">{record.record_name}</span>
                  </div>
                  <div className="hr-record-field-row">
                    <span className="hr-record-field-label">{t('health:doctorVisits.doctorName')}:</span>
                    <span className="hr-record-field-value">{record.doctor_name}</span>
                  </div>
                  <div className="hr-record-field-row">
                    <span className="hr-record-field-label">Specialization:</span>
                    <span className="hr-record-field-value">{record.specialization || '-'}</span>
                  </div>
                  <div className="hr-record-field-row">
                    <span className="hr-record-field-label">{t('health:labReports.reportDate')}:</span>
                    <span className="hr-record-field-value">{formatDateOnly(record.record_date)}</span>
                  </div>
                  <div className="hr-record-field-row">
                    <span className="hr-record-field-label">Type of Record:</span>
                    <span className="hr-record-field-value">{record.record_type}</span>
                  </div>
                </div>
                <div className="hr-record-card-footer">
                  <button className="hr-record-footer-btn" onClick={() => handleViewParameters(record.id)}>
                    View Parameters ({record.parameters?.length || 0})
                  </button>
                  <button className="hr-record-footer-btn" onClick={() => handleViewDocuments(record.id)}>
                    View Documents ({record.documents?.length || 0})
                  </button>
                </div>
                {/* Expandable parameters view */}
                {viewParamsRecordId === record.id && (
                  <div style={{ padding: '12px 16px', borderTop: '1px solid #e5e7eb', background: '#f9fafb' }}>
                    {record.parameters && record.parameters.length > 0 ? (
                      <table style={{ width: '100%', fontSize: '13px', borderCollapse: 'collapse' }}>
                        <thead>
                          <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                            <th style={{ textAlign: 'left', padding: '4px 8px', color: '#6b7280' }}>Parameter</th>
                            <th style={{ textAlign: 'left', padding: '4px 8px', color: '#6b7280' }}>Result</th>
                            <th style={{ textAlign: 'left', padding: '4px 8px', color: '#6b7280' }}>Unit</th>
                            <th style={{ textAlign: 'left', padding: '4px 8px', color: '#6b7280' }}>Range</th>
                          </tr>
                        </thead>
                        <tbody>
                          {record.parameters.map((p) => (
                            <tr key={p.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                              <td style={{ padding: '4px 8px' }}>{p.parameter_name}</td>
                              <td style={{ padding: '4px 8px', fontWeight: 600 }}>{p.result}</td>
                              <td style={{ padding: '4px 8px' }}>{p.unit}</td>
                              <td style={{ padding: '4px 8px', color: '#6b7280' }}>{p.start_range && p.end_range ? `${p.start_range} - ${p.end_range}` : '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : (
                      <p style={{ color: '#6b7280', fontSize: '13px', margin: 0 }}>No lab parameters recorded.</p>
                    )}
                  </div>
                )}
                {/* Expandable documents view */}
                {viewDocsRecordId === record.id && (
                  <div style={{ padding: '12px 16px', borderTop: '1px solid #e5e7eb', background: '#f9fafb' }}>
                    {record.documents && record.documents.length > 0 ? (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                        {record.documents.map((doc) => (
                          <a key={doc.id} href={doc.file} target="_blank" rel="noopener noreferrer"
                            style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '6px 12px', background: '#fff', border: '1px solid #e5e7eb', borderRadius: '6px', fontSize: '13px', color: '#06b6d4', textDecoration: 'none' }}>
                            <FontAwesomeIcon icon={faFileMedicalAlt} /> {getFileNameFromUrl(doc.file)}
                          </a>
                        ))}
                      </div>
                    ) : (
                      <p style={{ color: '#6b7280', fontSize: '13px', margin: 0 }}>No documents uploaded.</p>
                    )}
                  </div>
                )}
              </div>
            ))}

            {/* ─── HOSPITALIZATIONS TAB ─── */}
            {!recordsLoading && !recordsError && activeTab === "Hospitalizations" && paginatedHospitalizations.length === 0 && (
              <div className="hr-empty-state">
                <div className="hr-empty-state-icon">
                  <FontAwesomeIcon icon={faHospital} />
                </div>
                <p className="hr-empty-state-text">No hospitalization records found.</p>
                <button className="hr-action-btn hr-action-btn-primary" onClick={() => setIsAddingRecord(true)}>
                  <FontAwesomeIcon icon={faPlus} /> Add Your First Record
                </button>
              </div>
            )}

            {!recordsLoading && !recordsError && activeTab === "Hospitalizations" && paginatedHospitalizations.map((record) => (
              <div
                key={record.id}
                className="hr-record-card"
                style={{ '--card-accent': 'var(--hr-color-hospitalizations)' } as React.CSSProperties}
              >
                <div className="hr-record-card-header">
                  <div>
                    <strong className="hr-record-patient-name">
                      {record.dependant_data?.name || (record.for_whom === 'self' ? 'Self' : 'Dependant')}
                    </strong>
                    <p className="hr-record-patient-relation">
                      {record.for_whom === 'self' ? 'Self' : record.dependant_data?.relationship_name || record.dependant_data?.relationship || ''}
                    </p>
                  </div>
                  <div className="hr-record-actions">
                    <button className="hr-record-edit-btn" onClick={() => handleEditHospitalization(record)} title={t('common:buttons.edit')}>
                      <FontAwesomeIcon icon={faPencilAlt} />
                    </button>
                    <button className="hr-record-delete-btn" onClick={() => handleDeleteRecord('Hospitalizations', record.id)} disabled={deletingRecordId === record.id} title={t('common:buttons.delete')}>
                      <FontAwesomeIcon icon={deletingRecordId === record.id ? faSpinner : faTrashAlt} spin={deletingRecordId === record.id} />
                    </button>
                  </div>
                </div>
                <div className="hr-record-card-body">
                  <div className="hr-record-field-row">
                    <span className="hr-record-field-label">{t('health:hospitalization.hospitalName')}:</span>
                    <span className="hr-record-field-value">{record.hospital_name}</span>
                  </div>
                  <div className="hr-record-field-row">
                    <span className="hr-record-field-label">{t('health:hospitalization.admissionDate')}:</span>
                    <span className="hr-record-field-value">{formatDateOnly(record.admission_date)}</span>
                  </div>
                  <div className="hr-record-field-row">
                    <span className="hr-record-field-label">{t('health:hospitalization.dischargeDate')}:</span>
                    <span className="hr-record-field-value">{record.discharge_date ? formatDateOnly(record.discharge_date) : 'Ongoing'}</span>
                  </div>
                  <div className="hr-record-field-row">
                    <span className="hr-record-field-label">{t('health:hospitalization.reason')}:</span>
                    <span className="hr-record-field-value">{record.reason}</span>
                  </div>
                  <div className="hr-record-field-row">
                    <span className="hr-record-field-label">{t('health:hospitalization.totalBill')}:</span>
                    <span className="hr-record-field-value">{record.total_bill ? `₹${record.total_bill}` : '-'}</span>
                  </div>
                </div>
                <div className="hr-record-card-footer">
                  <button className="hr-record-footer-btn" onClick={() => handleViewDocuments(record.id)}>
                    View Documents ({record.documents?.length || 0})
                  </button>
                </div>
                {viewDocsRecordId === record.id && (
                  <div style={{ padding: '12px 16px', borderTop: '1px solid #e5e7eb', background: '#f9fafb' }}>
                    {record.documents && record.documents.length > 0 ? (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                        {record.documents.map((doc) => (
                          <a key={doc.id} href={doc.file} target="_blank" rel="noopener noreferrer"
                            style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '6px 12px', background: '#fff', border: '1px solid #e5e7eb', borderRadius: '6px', fontSize: '13px', color: '#f59e0b', textDecoration: 'none' }}>
                            <FontAwesomeIcon icon={faFileMedicalAlt} /> {getFileNameFromUrl(doc.file)}
                          </a>
                        ))}
                      </div>
                    ) : (
                      <p style={{ color: '#6b7280', fontSize: '13px', margin: 0 }}>No documents uploaded.</p>
                    )}
                  </div>
                )}
              </div>
            ))}

            {/* ─── MEDICAL BILLS TAB ─── */}
            {!recordsLoading && !recordsError && activeTab === "Medical Bills" && paginatedMedicalBills.length === 0 && (
              <div className="hr-empty-state">
                <div className="hr-empty-state-icon">
                  <FontAwesomeIcon icon={faFileInvoiceDollar} />
                </div>
                <p className="hr-empty-state-text">No medical bills found.</p>
                <button className="hr-action-btn hr-action-btn-primary" onClick={() => setIsAddingRecord(true)}>
                  <FontAwesomeIcon icon={faPlus} /> Add Your First Record
                </button>
              </div>
            )}

            {!recordsLoading && !recordsError && activeTab === "Medical Bills" && paginatedMedicalBills.map((record) => (
              <div
                key={record.id}
                className="hr-record-card"
                style={{ '--card-accent': 'var(--hr-color-bills)' } as React.CSSProperties}
              >
                <div className="hr-record-card-header">
                  <div>
                    <strong className="hr-record-patient-name">
                      {record.dependant_data?.name || (record.for_whom === 'self' ? 'Self' : 'Dependant')}
                    </strong>
                    <p className="hr-record-patient-relation">
                      {record.for_whom === 'self' ? 'Self' : record.dependant_data?.relationship_name || record.dependant_data?.relationship || ''}
                    </p>
                  </div>
                  <div className="hr-record-actions">
                    <button className="hr-record-edit-btn" onClick={() => handleEditMedicalBill(record)} title={t('common:buttons.edit')}>
                      <FontAwesomeIcon icon={faPencilAlt} />
                    </button>
                    <button className="hr-record-delete-btn" onClick={() => handleDeleteRecord('Medical Bills', record.id)} disabled={deletingRecordId === record.id} title={t('common:buttons.delete')}>
                      <FontAwesomeIcon icon={deletingRecordId === record.id ? faSpinner : faTrashAlt} spin={deletingRecordId === record.id} />
                    </button>
                  </div>
                </div>
                <div className="hr-record-card-body">
                  <div className="hr-record-field-row">
                    <span className="hr-record-field-label">{t('health:medicalBills.hospitalClinic')}:</span>
                    <span className="hr-record-field-value">{record.hospital_name}</span>
                  </div>
                  <div className="hr-record-field-row">
                    <span className="hr-record-field-label">{t('health:medicalBills.billDate')}:</span>
                    <span className="hr-record-field-value">{formatDateOnly(record.bill_date)}</span>
                  </div>
                  <div className="hr-record-field-row">
                    <span className="hr-record-field-label">Bill Type:</span>
                    <span className="hr-record-field-value">{record.bill_type}</span>
                  </div>
                  <div className="hr-record-field-row">
                    <span className="hr-record-field-label">{t('health:medicalBills.totalAmount')}:</span>
                    <span className="hr-record-field-value">₹{record.amount}</span>
                  </div>
                  <div className="hr-record-field-row">
                    <span className="hr-record-field-label">Paid Amount:</span>
                    <span className="hr-record-field-value">₹{Number(record.paid_amount) || 0}</span>
                  </div>
                  <div className="hr-record-field-row">
                    <span className="hr-record-field-label">Pending:</span>
                    <span className="hr-record-field-value hr-bills-pending-text">₹{((Number(record.amount) || 0) - (Number(record.paid_amount) || 0)).toLocaleString('en-IN')}</span>
                  </div>
                  <div className="hr-record-field-row">
                    <span className="hr-record-field-label">{t('health:medicalBills.insuranceClaimed')}:</span>
                    <span className="hr-record-field-value">{record.insurance_claimed ? `${t('common:buttons.yes')} (₹${record.insurance_amount})` : t('common:buttons.no')}</span>
                  </div>
                </div>
                <div className="hr-record-card-footer">
                  <button className="hr-record-footer-btn" onClick={() => handleViewDocuments(record.id)}>
                    View Documents ({record.documents?.length || 0})
                  </button>
                </div>
                {viewDocsRecordId === record.id && (
                  <div style={{ padding: '12px 16px', borderTop: '1px solid #e5e7eb', background: '#f9fafb' }}>
                    {record.documents && record.documents.length > 0 ? (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                        {record.documents.map((doc) => (
                          <a key={doc.id} href={doc.file} target="_blank" rel="noopener noreferrer"
                            style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '6px 12px', background: '#fff', border: '1px solid #e5e7eb', borderRadius: '6px', fontSize: '13px', color: '#10b981', textDecoration: 'none' }}>
                            <FontAwesomeIcon icon={faFileMedicalAlt} /> {getFileNameFromUrl(doc.file)}
                          </a>
                        ))}
                      </div>
                    ) : (
                      <p style={{ color: '#6b7280', fontSize: '13px', margin: 0 }}>No documents uploaded.</p>
                    )}
                  </div>
                )}
              </div>
            ))}

            {/* ─── VACCINATIONS TAB ─── */}
            {!recordsLoading && !recordsError && activeTab === "Vaccinations Certificates" && paginatedVaccinations.length === 0 && (
              <div className="hr-empty-state">
                <div className="hr-empty-state-icon">
                  <FontAwesomeIcon icon={faSyringe} />
                </div>
                <p className="hr-empty-state-text">No vaccination certificates found.</p>
                <button className="hr-action-btn hr-action-btn-primary" onClick={() => setIsAddingRecord(true)}>
                  <FontAwesomeIcon icon={faPlus} /> Add Your First Record
                </button>
              </div>
            )}

            {!recordsLoading && !recordsError && activeTab === "Vaccinations Certificates" && paginatedVaccinations.map((record) => (
              <div
                key={record.id}
                className="hr-record-card"
                style={{ '--card-accent': 'var(--hr-color-vaccinations)' } as React.CSSProperties}
              >
                <div className="hr-record-card-header">
                  <div>
                    <strong className="hr-record-patient-name">
                      {record.dependant_data?.name || (record.for_whom === 'self' ? 'Self' : 'Dependant')}
                    </strong>
                    <p className="hr-record-patient-relation">
                      {record.for_whom === 'self' ? 'Self' : record.dependant_data?.relationship_name || record.dependant_data?.relationship || ''}
                    </p>
                  </div>
                  <div className="hr-record-actions">
                    <button className="hr-record-edit-btn" onClick={() => handleEditVaccination(record)} title={t('common:buttons.edit')}>
                      <FontAwesomeIcon icon={faPencilAlt} />
                    </button>
                    <button className="hr-record-delete-btn" onClick={() => handleDeleteRecord('Vaccinations Certificates', record.id)} disabled={deletingRecordId === record.id} title={t('common:buttons.delete')}>
                      <FontAwesomeIcon icon={deletingRecordId === record.id ? faSpinner : faTrashAlt} spin={deletingRecordId === record.id} />
                    </button>
                  </div>
                </div>
                <div className="hr-record-card-body">
                  <div className="hr-record-field-row">
                    <span className="hr-record-field-label">{t('health:vaccinations.vaccineName')}:</span>
                    <span className="hr-record-field-value">{record.vaccine_name}</span>
                  </div>
                  <div className="hr-record-field-row">
                    <span className="hr-record-field-label">{t('health:vaccinations.vaccinationDate')}:</span>
                    <span className="hr-record-field-value">{formatDateOnly(record.vaccination_date)}</span>
                  </div>
                  <div className="hr-record-field-row">
                    <span className="hr-record-field-label">{t('health:vaccinations.doseNumber')}:</span>
                    <span className="hr-record-field-value">{record.dose_number ? `${record.dose_number}/${record.total_doses || '?'}` : '-'}</span>
                  </div>
                  <div className="hr-record-field-row">
                    <span className="hr-record-field-label">{t('health:vaccinations.administeredBy')}:</span>
                    <span className="hr-record-field-value">{record.administered_by || '-'}</span>
                  </div>
                  <div className="hr-record-field-row">
                    <span className="hr-record-field-label">{t('health:vaccinations.nextDueDate')}:</span>
                    <span className="hr-record-field-value">{record.next_due_date ? formatDateOnly(record.next_due_date) : '-'}</span>
                  </div>
                </div>
                <div className="hr-record-card-footer">
                  <button className="hr-record-footer-btn" onClick={() => handleViewDocuments(record.id)}>
                    View Documents ({record.documents?.length || 0})
                  </button>
                </div>
                {viewDocsRecordId === record.id && (
                  <div style={{ padding: '12px 16px', borderTop: '1px solid #e5e7eb', background: '#f9fafb' }}>
                    {record.documents && record.documents.length > 0 ? (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                        {record.documents.map((doc) => (
                          <a key={doc.id} href={doc.file} target="_blank" rel="noopener noreferrer"
                            style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '6px 12px', background: '#fff', border: '1px solid #e5e7eb', borderRadius: '6px', fontSize: '13px', color: '#8b5cf6', textDecoration: 'none' }}>
                            <FontAwesomeIcon icon={faFileMedicalAlt} /> {getFileNameFromUrl(doc.file)}
                          </a>
                        ))}
                      </div>
                    ) : (
                      <p style={{ color: '#6b7280', fontSize: '13px', margin: 0 }}>No documents uploaded.</p>
                    )}
                  </div>
                )}
              </div>
            ))}

          {activeTab === "Health Conditions" && (
            <>
              {conditionsLoading && (
                <div className="hr-loading-state">
                  <FontAwesomeIcon icon={faSpinner} spin size="2x" style={{ color: '#14b8a6' }} />
                  <p>{t('health:conditions.loading')}</p>
                </div>
              )}

              {!conditionsLoading && conditionsError && (
                <div className="hr-error-banner" style={{ gridColumn: '1 / -1' }}>
                  <span>{conditionsError}</span>
                  <button onClick={fetchConditions}>{t('common:buttons.tryAgain')}</button>
                </div>
              )}

              {!conditionsLoading && !conditionsError && conditionsList.length === 0 && (
                <div className="hr-empty-state">
                  <div className="hr-empty-state-icon">
                    <FontAwesomeIcon icon={faBriefcaseMedical} />
                  </div>
                  <p className="hr-empty-state-text">{t('health:conditions.empty')}</p>
                  <button className="hr-action-btn hr-action-btn-primary" onClick={() => {
                    resetConditionForm();
                    setIsAddingRecord(true);
                  }}>
                    <FontAwesomeIcon icon={faPlus} /> {t('health:conditions.addFirst')}
                  </button>
                </div>
              )}

              {!conditionsLoading && !conditionsError && conditionsList.map((record) => (
                <div
                  key={record.id}
                  className="hr-record-card"
                  style={{ '--card-accent': 'var(--hr-color-conditions)' } as React.CSSProperties}
                >
                  <div className="hr-record-card-header">
                    <div>
                      <strong className="hr-record-patient-name">
                        {record.dependant_data?.name || (record.for_whom === 'self' ? 'Self' : 'Dependant')}
                      </strong>
                      <p className="hr-record-patient-relation">
                        {record.for_whom === 'self' ? 'Self' : record.dependant_data?.relationship_name || record.dependant_data?.relationship || ''}
                      </p>
                    </div>
                    <div className="hr-record-actions">
                      {record.status === 'ongoing' && (
                        <button className="hr-record-edit-btn" onClick={() => handleResolveCondition(record)} title={t('health:conditions.markResolved')}>
                          <FontAwesomeIcon icon={faCheck} />
                        </button>
                      )}
                      <button className="hr-record-edit-btn" onClick={() => openEditCondition(record)} title={t('common:buttons.edit')}>
                        <FontAwesomeIcon icon={faPencilAlt} />
                      </button>
                      <button className="hr-record-delete-btn" onClick={() => handleDeleteCondition(record)} title={t('common:buttons.delete')}>
                        <FontAwesomeIcon icon={faTrashAlt} />
                      </button>
                    </div>
                  </div>
                  <div className="hr-record-card-body">
                    <div className="hr-record-field-row">
                      <span className="hr-record-field-label">{t('health:conditions.nameLabel')}:</span>
                      <span className="hr-record-field-value">{record.condition_name}</span>
                    </div>
                    <div className="hr-record-field-row">
                      <span className="hr-record-field-label">{t('health:conditions.typeLabel')}:</span>
                      <span className="hr-record-field-value">{conditionChoiceLabel(conditionChoices?.condition_types, record.condition_type, record.condition_type_display)}</span>
                    </div>
                    <div className="hr-record-field-row">
                      <span className="hr-record-field-label">{t('health:conditions.severityLabel')}:</span>
                      <span className="hr-record-field-value">
                        <span className={`hcp-chip hcp-severity-${record.severity}`}>
                          {conditionChoiceLabel(conditionChoices?.severities, record.severity, record.severity_display)}
                        </span>
                      </span>
                    </div>
                    <div className="hr-record-field-row">
                      <span className="hr-record-field-label">{t('health:conditions.startDate')}:</span>
                      <span className="hr-record-field-value">{formatDateOnly(record.start_date)}</span>
                    </div>
                    <div className="hr-record-field-row">
                      <span className="hr-record-field-label">{t('health:conditions.endDate')}:</span>
                      <span className="hr-record-field-value">
                        {record.end_date ? formatDateOnly(record.end_date) : '-'}
                        {record.duration_days !== null && record.duration_days !== undefined && (
                          <span className="hcp-chip hcp-duration">
                            {t('health:conditions.days', { count: record.duration_days })}
                          </span>
                        )}
                      </span>
                    </div>
                    <div className="hr-record-field-row">
                      <span className="hr-record-field-label">{t('health:conditions.statusLabel')}:</span>
                      <span className="hr-record-field-value">
                        <span className={`hcp-chip hcp-status-${record.status}`}>
                          {conditionChoiceLabel(conditionChoices?.statuses, record.status, record.status_display)}
                        </span>
                      </span>
                    </div>
                    {record.treated_by_doctor && (
                      <div className="hr-record-field-row">
                        <span className="hr-record-field-label">{t('health:conditions.doctorName')}:</span>
                        <span className="hr-record-field-value">{record.doctor_name || '-'}</span>
                      </div>
                    )}
                    {record.notes && (
                      <div className="hr-record-field-row">
                        <span className="hr-record-field-label">{t('health:conditions.notesLabel')}:</span>
                        <span className="hr-record-field-value">{record.notes}</span>
                      </div>
                    )}
                  </div>
                  <div className="hr-record-card-footer">
                    <button className="hr-record-footer-btn" onClick={() => setConditionExpandedId(conditionExpandedId === record.id ? null : record.id)}>
                      {t('health:conditions.viewMedicines', { count: record.medications?.length || 0 })}
                    </button>
                    <button className="hr-record-footer-btn" onClick={() => handleViewDocuments(record.id)}>
                      {t('health:conditions.viewDocuments', { count: record.documents?.length || 0 })}
                    </button>
                  </div>
                  {conditionExpandedId === record.id && (
                    <div className="hr-record-expand-panel">
                      {record.medications && record.medications.length > 0 ? (
                        <div className="hcp-med-list">
                          {record.medications.map((med) => (
                            <div className="hcp-med-item" key={med.id ?? med.medicine_name}>
                              <div className="hcp-med-item-main">
                                <strong>{med.medicine_name}</strong>
                                <span className="hcp-chip">{conditionChoiceLabel(conditionChoices?.medicine_types, med.medicine_type, med.medicine_type_display)}</span>
                                {med.is_self_prescribed && (
                                  <span className="hcp-chip hcp-chip-muted">{t('health:conditions.selfPrescribed')}</span>
                                )}
                              </div>
                              <div className="hcp-med-item-meta">
                                <span>{med.dosage || '-'}</span>
                                <span>{conditionChoiceLabel(conditionChoices?.frequencies, med.frequency, med.frequency_display)}</span>
                                {med.start_date && <span>{formatDateOnly(med.start_date)}</span>}
                                {med.source_display && <span>{med.source_display}</span>}
                              </div>
                              {med.notes && <p className="hcp-med-item-notes">{med.notes}</p>}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="hcp-med-empty">{t('health:conditions.noMedicines')}</p>
                      )}
                    </div>
                  )}
                  {viewDocsRecordId === record.id && (
                    <div className="hr-record-expand-panel">
                      {record.documents && record.documents.length > 0 ? (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                          {record.documents.map((doc) => (
                            <a key={doc.id} href={doc.file} target="_blank" rel="noopener noreferrer"
                              style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '6px 12px', background: '#fff', border: '1px solid #e5e7eb', borderRadius: '6px', fontSize: '13px', color: '#14b8a6', textDecoration: 'none' }}>
                              <FontAwesomeIcon icon={faFileMedicalAlt} /> {getFileNameFromUrl(doc.file)}
                            </a>
                          ))}
                        </div>
                      ) : (
                        <p className="hcp-med-empty">{t('health:conditions.noDocuments')}</p>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </>
          )}
          </div>

          {activeTab === "Health Conditions" && !conditionsLoading && !conditionsError && conditionsCount > CONDITIONS_PER_PAGE && (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px', padding: '20px 0' }}>
              <button
                onClick={() => setConditionsPage((p) => Math.max(1, p - 1))}
                disabled={conditionsPage === 1}
                style={{
                  padding: '8px 14px', borderRadius: '8px', border: '1px solid #d1d5db',
                  background: conditionsPage === 1 ? '#f3f4f6' : '#fff', color: conditionsPage === 1 ? '#9ca3af' : '#374151',
                  cursor: conditionsPage === 1 ? 'not-allowed' : 'pointer', fontSize: '14px', fontWeight: 500,
                }}
              >
                <FontAwesomeIcon icon={faChevronLeft} /> Prev
              </button>

              {Array.from({ length: conditionsTotalPages }, (_, i) => i + 1)
                .filter((page) => page === 1 || page === conditionsTotalPages || Math.abs(page - conditionsPage) <= 1)
                .reduce<(number | string)[]>((acc, page, idx, arr) => {
                  if (idx > 0 && page - (arr[idx - 1] as number) > 1) acc.push('...');
                  acc.push(page);
                  return acc;
                }, [])
                .map((item, idx) =>
                  item === '...' ? (
                    <span key={`dots-${idx}`} style={{ padding: '0 4px', color: '#9ca3af' }}>...</span>
                  ) : (
                    <button
                      key={item}
                      onClick={() => setConditionsPage(item as number)}
                      style={{
                        padding: '8px 14px', borderRadius: '8px', fontSize: '14px', fontWeight: 600,
                        border: conditionsPage === item ? '2px solid #14b8a6' : '1px solid #d1d5db',
                        background: conditionsPage === item ? '#f0fdfa' : '#fff',
                        color: conditionsPage === item ? '#14b8a6' : '#374151',
                        cursor: 'pointer',
                      }}
                    >
                      {item}
                    </button>
                  )
                )}

              <button
                onClick={() => setConditionsPage((p) => Math.min(conditionsTotalPages, p + 1))}
                disabled={conditionsPage >= conditionsTotalPages}
                style={{
                  padding: '8px 14px', borderRadius: '8px', border: '1px solid #d1d5db',
                  background: conditionsPage >= conditionsTotalPages ? '#f3f4f6' : '#fff', color: conditionsPage >= conditionsTotalPages ? '#9ca3af' : '#374151',
                  cursor: conditionsPage >= conditionsTotalPages ? 'not-allowed' : 'pointer', fontSize: '14px', fontWeight: 500,
                }}
              >
                Next <FontAwesomeIcon icon={faChevronRight} />
              </button>

              <span style={{ marginLeft: '12px', fontSize: '13px', color: '#6b7280' }}>
                {conditionsCount} record{conditionsCount !== 1 ? 's' : ''}
              </span>
            </div>
          )}

          {/* ── Pagination Controls ── */}
          {activeTab !== "Health Conditions" && !recordsLoading && !recordsError && activeFilteredCount > RECORDS_PER_PAGE && (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px', padding: '20px 0' }}>
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                style={{
                  padding: '8px 14px', borderRadius: '8px', border: '1px solid #d1d5db',
                  background: currentPage === 1 ? '#f3f4f6' : '#fff', color: currentPage === 1 ? '#9ca3af' : '#374151',
                  cursor: currentPage === 1 ? 'not-allowed' : 'pointer', fontSize: '14px', fontWeight: 500,
                }}
              >
                <FontAwesomeIcon icon={faChevronLeft} /> Prev
              </button>

              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter((page) => page === 1 || page === totalPages || Math.abs(page - currentPage) <= 1)
                .reduce<(number | string)[]>((acc, page, idx, arr) => {
                  if (idx > 0 && page - (arr[idx - 1] as number) > 1) acc.push('...');
                  acc.push(page);
                  return acc;
                }, [])
                .map((item, idx) =>
                  item === '...' ? (
                    <span key={`dots-${idx}`} style={{ padding: '0 4px', color: '#9ca3af' }}>...</span>
                  ) : (
                    <button
                      key={item}
                      onClick={() => setCurrentPage(item as number)}
                      style={{
                        padding: '8px 14px', borderRadius: '8px', fontSize: '14px', fontWeight: 600,
                        border: currentPage === item ? '2px solid #06b6d4' : '1px solid #d1d5db',
                        background: currentPage === item ? '#ecfeff' : '#fff',
                        color: currentPage === item ? '#06b6d4' : '#374151',
                        cursor: 'pointer',
                      }}
                    >
                      {item}
                    </button>
                  )
                )}

              <button
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                style={{
                  padding: '8px 14px', borderRadius: '8px', border: '1px solid #d1d5db',
                  background: currentPage === totalPages ? '#f3f4f6' : '#fff', color: currentPage === totalPages ? '#9ca3af' : '#374151',
                  cursor: currentPage === totalPages ? 'not-allowed' : 'pointer', fontSize: '14px', fontWeight: 500,
                }}
              >
                Next <FontAwesomeIcon icon={faChevronRight} />
              </button>

              <span style={{ marginLeft: '12px', fontSize: '13px', color: '#6b7280' }}>
                {activeFilteredCount} record{activeFilteredCount !== 1 ? 's' : ''}
              </span>
            </div>
          )}

        </div>
      </Container>

      {/* ══════════ EDIT RECORD MODAL ══════════ */}
      {showEditModal && editRecordId && (
        <div className="hr-add-record-overlay" onClick={handleCloseEditModal}>
          <div className="hr-add-record-modal" onClick={(e) => e.stopPropagation()}>
            <div className="hr-add-record-modal-header">
              <div className="hr-add-record-modal-icon">
                <FontAwesomeIcon icon={tabConfig.find(t => t.key === editRecordTab)?.icon || faPencilAlt} />
              </div>
              <div>
                <h3 className="hr-add-record-modal-title">
                  Edit {editRecordTab === 'Prescription & Lab Tests' ? 'Prescription / Lab Test'
                    : editRecordTab === 'Hospitalizations' ? 'Hospitalization Record'
                    : editRecordTab === 'Medical Bills' ? 'Medical Bill'
                    : 'Vaccination Certificate'}
                </h3>
                <p className="hr-add-record-modal-subtitle">Update the details below</p>
              </div>
              <button className="hr-add-record-modal-close" onClick={handleCloseEditModal}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            <div className="hr-add-record-modal-body">
              {/* Self / Dependant */}
              {!isDependantUser && (
                <div className="hr-add-record-radio-group">
                  <label className={`hr-add-record-radio${editRecordForWhom === 'self' ? ' active' : ''}`}>
                    <input type="radio" name="editForWhom" value="self" checked={editRecordForWhom === 'self'} onChange={() => setEditRecordForWhom('self')} />
                    Self
                  </label>
                  <label className={`hr-add-record-radio${editRecordForWhom === 'dependant' ? ' active' : ''}`}>
                    <input type="radio" name="editForWhom" value="dependant" checked={editRecordForWhom === 'dependant'} onChange={() => setEditRecordForWhom('dependant')} />
                    Dependant
                  </label>
                </div>
              )}

              {/* Dependant selector */}
              {!isDependantUser && editRecordForWhom === 'dependant' && (
                <div className="hr-add-record-field" style={{ marginBottom: '16px' }}>
                  <label>Select Dependant *</label>
                  {dependantsLoading ? (
                    <div style={{ padding: '8px 0', color: '#6b7280', fontSize: '14px' }}>
                      <FontAwesomeIcon icon={faSpinner} spin /> Loading dependants...
                    </div>
                  ) : dependantsList.length === 0 ? (
                    <div style={{ padding: '8px 12px', background: '#fffbeb', border: '1px solid #f59e0b', borderRadius: '6px', color: '#92400e', fontSize: '14px' }}>
                      <FontAwesomeIcon icon={faExclamationTriangle} style={{ marginRight: '6px' }} />
                      No dependants found. Please add a dependant from your profile first.
                    </div>
                  ) : (
                    <select
                      value={editRecordDependantId}
                      onChange={(e) => setEditRecordDependantId(e.target.value ? Number(e.target.value) : '')}
                      style={{ width: '100%', padding: '10px 12px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '14px' }}
                    >
                      <option value="">-- Select a dependant --</option>
                      {dependantsList.map((dep) => (
                        <option key={dep.id} value={dep.id}>
                          {dep.name} {dep.relationship_name ? `(${dep.relationship_name})` : ''}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              )}

              {/* ─── PRESCRIPTION EDIT FORM ─── */}
              {editRecordTab === 'Prescription & Lab Tests' && (
                <>
                  <div className="hr-add-record-grid">
                    <div className="hr-add-record-field">
                      <label>Record Name *</label>
                      <select value={editRecordName} onChange={(e) => setEditRecordName(e.target.value)}>
                        <option value="">Select</option>
                        <option value="Doctor Prescription">Doctor Prescription</option>
                        <option value="Lab Report / Blood Test">Lab Report / Blood Test</option>
                        <option value="Radiology Report">Radiology Report</option>
                        <option value="Discharge Summary">Discharge Summary</option>
                        <option value="Medical Certificate">Medical Certificate</option>
                        <option value="Referral Letter">Referral Letter</option>
                        <option value="Other Medical Record">Other Medical Record</option>
                      </select>
                    </div>
                    <div className="hr-add-record-field">
                      <label>Record Date *</label>
                      <input type="date" value={editRecordDate} onChange={(e) => setEditRecordDate(e.target.value)} />
                    </div>
                    <div className="hr-add-record-field">
                      <label>Type of Record *</label>
                      <select value={editRecordType} onChange={(e) => setEditRecordType(e.target.value)}>
                        <option value="">Select</option>
                        <option value="doctor_prescription">Doctor Prescription</option>
                        <option value="lab_report">Lab Report</option>
                        <option value="radiology_report">Radiology Report</option>
                        <option value="health_record">Health Record</option>
                      </select>
                    </div>
                    <div className="hr-add-record-field">
                      <label>{t('health:doctorVisits.doctorName')} *</label>
                      <input type="text" value={editDoctorName} onChange={(e) => setEditDoctorName(e.target.value)} placeholder={t('health:doctorVisits.doctorNamePlaceholder')} />
                    </div>
                    <div className="hr-add-record-field">
                      <label>Specialization</label>
                      <select value={editSpecialization} onChange={(e) => setEditSpecialization(e.target.value)}>
                        <option value="">Select</option>
                        <option value="General Physician">General Physician</option>
                        <option value="Cardiology">Cardiology</option>
                        <option value="Dermatology">Dermatology</option>
                        <option value="Endocrinology / Diabetes">Endocrinology / Diabetes</option>
                        <option value="ENT (Ear, Nose, Throat)">ENT (Ear, Nose, Throat)</option>
                        <option value="Gastroenterology">Gastroenterology</option>
                        <option value="General Surgery">General Surgery</option>
                        <option value="Gynecology / Obstetrics">Gynecology / Obstetrics</option>
                        <option value="Nephrology">Nephrology</option>
                        <option value="Neurology">Neurology</option>
                        <option value="Oncology">Oncology</option>
                        <option value="Ophthalmology">Ophthalmology</option>
                        <option value="Orthopedics">Orthopedics</option>
                        <option value="Pediatrics">Pediatrics</option>
                        <option value="Psychiatry">Psychiatry</option>
                        <option value="Pulmonology">Pulmonology</option>
                        <option value="Radiology">Radiology</option>
                        <option value="Rheumatology">Rheumatology</option>
                        <option value="Urology">Urology</option>
                        <option value="Dentistry">Dentistry</option>
                        <option value="Physiotherapy">Physiotherapy</option>
                        <option value="Ayurveda / Homeopathy">Ayurveda / Homeopathy</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                  </div>
                  <div className="hr-add-record-field hr-add-record-full-width">
                    <label>{t('health:doctorVisits.reason')}</label>
                    <textarea rows={2} value={editReason} onChange={(e) => setEditReason(e.target.value)} placeholder={t('health:doctorVisits.reasonPlaceholder')} />
                  </div>

                  {/* Lab Parameters Toggle */}
                  <div className="hr-add-record-lab-toggle">
                    <button
                      className={`hr-add-record-lab-btn${editShowLabParams ? ' active' : ''}`}
                      onClick={() => setEditShowLabParams((p) => !p)}
                    >
                      <FontAwesomeIcon icon={faPlus} /> Lab Parameters
                    </button>
                  </div>
                  {editShowLabParams && (
                    <div className="hr-add-record-lab-section">
                      <table className="hr-add-record-lab-table">
                        <thead>
                          <tr>
                            <th>Parameter Name</th>
                            <th>Result</th>
                            <th>Unit</th>
                            <th>Start Range</th>
                            <th>End Range</th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          {editLabParams.length === 0 && (
                            <tr><td colSpan={6} className="hr-add-record-lab-empty">No parameters added yet.</td></tr>
                          )}
                          {editLabParams.map((param) => (
                            <tr key={param.id}>
                              <td><input type="text" value={param.parameterName} onChange={(e) => handleEditLabParamChange(param.id, 'parameterName', e.target.value)} placeholder="Name" /></td>
                              <td><input type="text" value={param.result} onChange={(e) => handleEditLabParamChange(param.id, 'result', e.target.value)} placeholder="Result" /></td>
                              <td><input type="text" value={param.unit} onChange={(e) => handleEditLabParamChange(param.id, 'unit', e.target.value)} placeholder="Unit" /></td>
                              <td><input type="text" value={param.startRange} onChange={(e) => handleEditLabParamChange(param.id, 'startRange', e.target.value)} placeholder="Min" /></td>
                              <td><input type="text" value={param.endRange} onChange={(e) => handleEditLabParamChange(param.id, 'endRange', e.target.value)} placeholder="Max" /></td>
                              <td><button className="hr-add-record-lab-remove" onClick={() => handleRemoveEditLabParam(param.id)}><FontAwesomeIcon icon={faTimes} /></button></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      <button className="hr-add-record-lab-add" onClick={handleAddEditLabParam}>
                        <FontAwesomeIcon icon={faPlus} /> Add Parameter
                      </button>
                    </div>
                  )}
                </>
              )}

              {/* ─── HOSPITALIZATION EDIT FORM ─── */}
              {editRecordTab === 'Hospitalizations' && (
                <div className="hr-add-record-grid">
                  <div className="hr-add-record-field">
                    <label>Hospitalization Type <span className="hr-required">*</span></label>
                    <select value={editHospitalizationType} onChange={(e) => setEditHospitalizationType(e.target.value)}>
                      <option value="inpatient">Inpatient</option>
                      <option value="outpatient">Outpatient</option>
                      <option value="emergency">Emergency</option>
                      <option value="surgery">Surgery</option>
                      <option value="day_care">Day Care</option>
                    </select>
                  </div>
                  <div className="hr-add-record-field">
                    <label>{t('health:hospitalization.hospitalName')} *</label>
                    <input type="text" value={editHospitalName} onChange={(e) => setEditHospitalName(e.target.value)} placeholder={t('health:hospitalization.hospitalNamePlaceholder')} />
                  </div>
                  <div className="hr-add-record-field">
                    <label>{t('health:hospitalization.admissionDate')} *</label>
                    <input type="date" value={editAdmissionDate} onChange={(e) => setEditAdmissionDate(e.target.value)} />
                  </div>
                  <div className="hr-add-record-field">
                    <label>{t('health:hospitalization.dischargeDate')}</label>
                    <input type="date" value={editDischargeDate} onChange={(e) => setEditDischargeDate(e.target.value)} />
                  </div>
                  <div className="hr-add-record-field">
                    <label>{t('health:doctorVisits.doctorName')}</label>
                    <input type="text" value={editHospDoctorName} onChange={(e) => setEditHospDoctorName(e.target.value)} placeholder={t('health:doctorVisits.doctorNamePlaceholder')} />
                  </div>
                  <div className="hr-add-record-field hr-add-record-full-width">
                    <label>{t('health:doctorVisits.diagnosis')}</label>
                    <input type="text" placeholder={t('health:hospitalization.reasonPlaceholder')} value={editHospDiagnosis} onChange={(e) => setEditHospDiagnosis(e.target.value)} />
                  </div>
                  {editHospitalizationType === 'surgery' && (
                    <div className="hr-add-record-field">
                      <label>{t('health:hospitalization.procedure')}</label>
                      <input type="text" placeholder={t('health:hospitalization.procedurePlaceholder')} value={editHospProcedureName} onChange={(e) => setEditHospProcedureName(e.target.value)} />
                    </div>
                  )}
                  <div className="hr-add-record-field">
                    <label>Room Type</label>
                    <select value={editRoomType} onChange={(e) => setEditRoomType(e.target.value)}>
                      <option value="">Select</option>
                      <option value="general">General</option>
                      <option value="semi_private">Semi-Private</option>
                      <option value="private">Private</option>
                      <option value="icu">ICU</option>
                      <option value="nicu">NICU</option>
                    </select>
                  </div>
                  <div className="hr-add-record-field">
                    <label>{t('health:hospitalization.totalBill')}</label>
                    <input type="number" value={editTotalBill} onChange={(e) => setEditTotalBill(e.target.value)} placeholder={t('health:hospitalization.totalBillPlaceholder')} />
                  </div>
                  <div className="hr-add-record-field">
                    <label>{t('health:hospitalization.insuranceClaimed')}</label>
                    <div className="hr-add-record-toggle-wrap">
                      <label className="hr-add-record-toggle">
                        <input type="checkbox" checked={editHospInsuranceClaimed} onChange={(e) => setEditHospInsuranceClaimed(e.target.checked)} />
                        <span className="hr-add-record-toggle-slider" />
                      </label>
                      <span className="hr-add-record-toggle-label">{editHospInsuranceClaimed ? t('common:buttons.yes') : t('common:buttons.no')}</span>
                    </div>
                  </div>
                  {editHospInsuranceClaimed && (
                    <div className="hr-add-record-field">
                      <label>Insurance Amount</label>
                      <input type="number" value={editHospInsuranceAmount} onChange={(e) => setEditHospInsuranceAmount(e.target.value)} placeholder={t('health:hospitalization.insuranceClaimedPlaceholder')} />
                    </div>
                  )}
                  <div className="hr-add-record-field hr-add-record-span-3">
                    <label>{t('health:hospitalization.reason')} *</label>
                    <textarea rows={2} value={editHospReason} onChange={(e) => setEditHospReason(e.target.value)} placeholder={t('health:hospitalization.notesPlaceholder')} />
                  </div>
                  <div className="hr-add-record-field hr-add-record-span-3">
                    <label>{t('health:hospitalization.notes')}</label>
                    <textarea rows={2} value={editHospNotes} onChange={(e) => setEditHospNotes(e.target.value)} placeholder={t('health:doctorVisits.notesPlaceholder')} />
                  </div>
                </div>
              )}

              {/* ─── MEDICAL BILLS EDIT FORM ─── */}
              {editRecordTab === 'Medical Bills' && (
                <div className="hr-add-record-grid">
                  <div className="hr-add-record-field">
                    <label>{t('health:medicalBills.hospitalClinic')} *</label>
                    <input type="text" value={editBillHospitalName} onChange={(e) => setEditBillHospitalName(e.target.value)} placeholder={t('health:medicalBills.hospitalClinicPlaceholder')} />
                  </div>
                  <div className="hr-add-record-field">
                    <label>{t('health:medicalBills.invoiceNumber')}</label>
                    <input type="text" placeholder={t('health:medicalBills.invoicePlaceholder')} value={editBillNumber} onChange={(e) => setEditBillNumber(e.target.value)} />
                  </div>
                  <div className="hr-add-record-field">
                    <label>{t('health:medicalBills.billDate')} *</label>
                    <input type="date" value={editBillDate} onChange={(e) => setEditBillDate(e.target.value)} />
                  </div>
                  <div className="hr-add-record-field">
                    <label>Bill Type *</label>
                    <select value={editBillType} onChange={(e) => setEditBillType(e.target.value)}>
                      <option value="">Select</option>
                      <option value="Consultation">Consultation</option>
                      <option value="Lab Test">Lab Test</option>
                      <option value="Surgery">Surgery</option>
                      <option value="Pharmacy">Pharmacy</option>
                      <option value="Hospitalization">Hospitalization</option>
                      <option value="Diagnostics">Diagnostics</option>
                      <option value="Physiotherapy">Physiotherapy</option>
                      <option value="Hospital Bill">Hospital Bill</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  <div className="hr-add-record-field">
                    <label>{t('health:medicalBills.totalAmount')} *</label>
                    <input type="number" value={editBillAmount} onChange={(e) => setEditBillAmount(e.target.value)} placeholder={t('health:medicalBills.totalAmountPlaceholder')} />
                  </div>
                  <div className="hr-add-record-field">
                    <label>{t('health:medicalBills.amountPaid')}</label>
                    <input type="number" value={editBillPaidAmount} onChange={(e) => setEditBillPaidAmount(e.target.value)} placeholder={t('health:medicalBills.amountPaidPlaceholder')} />
                  </div>
                  <div className="hr-add-record-field">
                    <label>{t('health:medicalBills.insuranceClaimed')}</label>
                    <div className="hr-add-record-toggle-wrap">
                      <label className="hr-add-record-toggle">
                        <input type="checkbox" checked={editBillInsuranceClaimed} onChange={(e) => setEditBillInsuranceClaimed(e.target.checked)} />
                        <span className="hr-add-record-toggle-slider" />
                      </label>
                      <span className="hr-add-record-toggle-label">{editBillInsuranceClaimed ? t('common:buttons.yes') : t('common:buttons.no')}</span>
                    </div>
                  </div>
                  {editBillInsuranceClaimed && (
                    <div className="hr-add-record-field hr-add-record-span-3">
                      <label>Insurance Amount</label>
                      <input type="number" value={editBillInsuranceAmount} onChange={(e) => setEditBillInsuranceAmount(e.target.value)} placeholder={t('health:medicalBills.insuranceClaimedPlaceholder')} style={{ maxWidth: '300px' }} />
                    </div>
                  )}
                  <div className="hr-add-record-field hr-add-record-span-3">
                    <label>{t('health:medicalBills.description')}</label>
                    <textarea rows={2} value={editBillNotes} onChange={(e) => setEditBillNotes(e.target.value)} placeholder={t('health:medicalBills.descriptionPlaceholder')} />
                  </div>
                </div>
              )}

              {/* ─── VACCINATION EDIT FORM ─── */}
              {editRecordTab === 'Vaccinations Certificates' && (
                <div className="hr-add-record-grid">
                  <div className="hr-add-record-field">
                    <label>{t('health:vaccinations.vaccineName')} *</label>
                    <select value={editVaccineName} onChange={(e) => setEditVaccineName(e.target.value)} required>
                      <option value="">Select Vaccine</option>
                      <optgroup label={t('health:vaccinations.categories.covid19')}>
                        <option value="Covishield">Covishield (AstraZeneca)</option>
                        <option value="Covaxin">Covaxin (Bharat Biotech)</option>
                        <option value="Sputnik V">Sputnik V</option>
                        <option value="Pfizer-BioNTech">Pfizer-BioNTech</option>
                        <option value="Moderna">Moderna</option>
                        <option value="Johnson & Johnson">Johnson &amp; Johnson</option>
                      </optgroup>
                      <optgroup label={t('health:vaccinations.categories.childhoodRoutine')}>
                        <option value="BCG">BCG (Tuberculosis)</option>
                        <option value="OPV">OPV (Oral Polio)</option>
                        <option value="IPV">IPV (Injectable Polio)</option>
                        <option value="DPT">DPT (Diphtheria, Pertussis, Tetanus)</option>
                        <option value="Hepatitis B">Hepatitis B</option>
                        <option value="Hepatitis A">Hepatitis A</option>
                        <option value="MMR">MMR (Measles, Mumps, Rubella)</option>
                        <option value="Varicella">Varicella (Chickenpox)</option>
                        <option value="Rotavirus">Rotavirus</option>
                        <option value="Pneumococcal (PCV)">Pneumococcal (PCV)</option>
                        <option value="Hib">Hib (Haemophilus Influenzae B)</option>
                        <option value="Typhoid">Typhoid</option>
                        <option value="Meningococcal">Meningococcal</option>
                        <option value="Japanese Encephalitis">Japanese Encephalitis</option>
                      </optgroup>
                      <optgroup label={t('health:vaccinations.categories.adultTravel')}>
                        <option value="Influenza (Flu)">Influenza (Flu Shot)</option>
                        <option value="Tetanus Toxoid (TT)">Tetanus Toxoid (TT)</option>
                        <option value="Tdap">Tdap (Tetanus, Diphtheria, Pertussis)</option>
                        <option value="HPV">HPV (Human Papillomavirus)</option>
                        <option value="Yellow Fever">Yellow Fever</option>
                        <option value="Rabies">Rabies</option>
                        <option value="Cholera">Cholera</option>
                        <option value="Shingles (Zoster)">Shingles (Zoster)</option>
                      </optgroup>
                      <optgroup label={t('health:vaccinations.categories.other')}>
                        <option value="Other">Other (specify in notes)</option>
                      </optgroup>
                    </select>
                  </div>
                  <div className="hr-add-record-field">
                    <label>{t('health:vaccinations.vaccinationDate')} *</label>
                    <input type="date" value={editVaccinationDate} onChange={(e) => setEditVaccinationDate(e.target.value)} />
                  </div>
                  <div className="hr-add-record-field">
                    <label>{t('health:vaccinations.doseNumber')}</label>
                    <input type="number" value={editDoseNumber} onChange={(e) => setEditDoseNumber(e.target.value)} placeholder={t('health:vaccinations.doseNumberPlaceholder')} />
                  </div>
                  <div className="hr-add-record-field">
                    <label>{t('health:vaccinations.totalDoses')}</label>
                    <input type="number" value={editTotalDoses} onChange={(e) => setEditTotalDoses(e.target.value)} placeholder={t('health:vaccinations.totalDosesPlaceholder')} />
                  </div>
                  <div className="hr-add-record-field">
                    <label>{t('health:vaccinations.administeredBy')}</label>
                    <input type="text" value={editAdministeredBy} onChange={(e) => setEditAdministeredBy(e.target.value)} placeholder={t('health:vaccinations.administeredByPlaceholder')} />
                  </div>
                  <div className="hr-add-record-field">
                    <label>{t('health:vaccinations.location')}</label>
                    <input type="text" value={editVaccineLocation} onChange={(e) => setEditVaccineLocation(e.target.value)} placeholder={t('health:vaccinations.locationPlaceholder')} />
                  </div>
                  <div className="hr-add-record-field">
                    <label>{t('health:vaccinations.batchNumber')}</label>
                    <input type="text" value={editBatchNumber} onChange={(e) => setEditBatchNumber(e.target.value)} placeholder={t('health:vaccinations.batchNumberPlaceholder')} />
                  </div>
                  <div className="hr-add-record-field">
                    <label>{t('health:vaccinations.nextDueDate')}</label>
                    <input type="date" value={editNextDueDate} onChange={(e) => setEditNextDueDate(e.target.value)} />
                  </div>
                  <div className="hr-add-record-field hr-add-record-span-3">
                    <label>{t('health:hospitalization.notes')}</label>
                    <textarea rows={2} value={editVaccineNotes} onChange={(e) => setEditVaccineNotes(e.target.value)} placeholder={t('health:doctorVisits.notesPlaceholder')} />
                  </div>
                </div>
              )}

              {/* Existing Documents List */}
              {editExistingDocs.length > 0 && (
                <div className="hr-add-record-upload-section">
                  <label style={{ fontWeight: 600, fontSize: '14px', marginBottom: '8px', display: 'block' }}>Existing Documents</label>
                  <div className="hr-add-record-files-list">
                    {editExistingDocs.map((doc) => (
                      <div key={doc.id} className="hr-add-record-file-item">
                        <a href={doc.file} target="_blank" rel="noopener noreferrer" style={{ color: '#06b6d4', textDecoration: 'none', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {getFileNameFromUrl(doc.file)}
                        </a>
                        <button onClick={() => handleRemoveExistingDoc(doc.id)}><FontAwesomeIcon icon={faTimes} /></button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Upload New Documents */}
              <div className="hr-add-record-upload-section">
                <div className="hr-add-record-upload-area" onClick={() => editRecordFileRef.current?.click()}>
                  <FontAwesomeIcon icon={faUpload} className="hr-add-record-upload-icon" />
                  <p>Upload documents</p>
                  <small>(.jpeg, .jpg, .png, .pdf)</small>
                  <input
                    type="file"
                    ref={editRecordFileRef}
                    style={{ display: 'none' }}
                    accept=".jpeg,.jpg,.png,.pdf"
                    multiple
                    onChange={handleEditFileUpload}
                  />
                </div>
                {editRecordFiles.length > 0 && (
                  <div className="hr-add-record-files-list">
                    {editRecordFiles.map((file, idx) => (
                      <div key={idx} className="hr-add-record-file-item">
                        <span>{file.name}</span>
                        <button onClick={() => handleRemoveEditFile(idx)}><FontAwesomeIcon icon={faTimes} /></button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Footer Buttons */}
            <div className="hr-add-record-modal-footer">
              <button className="hr-add-record-cancel-btn" onClick={handleCloseEditModal} disabled={editRecordSaving}>
                {t('common:buttons.cancel')}
              </button>
              <button className="hr-add-record-save-btn" onClick={handleUpdate} disabled={editRecordSaving}>
                {editRecordSaving ? (
                  <><FontAwesomeIcon icon={faSpinner} spin /> {t('common:buttons.update')}...</>
                ) : (
                  <><FontAwesomeIcon icon={faSave} /> {t('common:buttons.update')} Record</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ══════════ VITAL HISTORY MODAL ══════════ */}
      {historyVital && (
        <div className="hr-history-overlay" onClick={handleCloseHistory}>
          <div className="hr-history-modal" onClick={(e) => e.stopPropagation()}>
            <div className="hr-history-modal-header" style={{ '--vital-color': vitalIconMap[historyVital.type]?.color || '#06b6d4' } as React.CSSProperties}>
              <div className="hr-history-modal-icon">
                <FontAwesomeIcon icon={vitalIconMap[historyVital.type]?.icon || faFileMedicalAlt} />
              </div>
              <div>
                <h3 className="hr-history-modal-title">{historyVital.name}</h3>
                <p className="hr-history-modal-current">Current: <strong>{historyVital.value}</strong></p>
              </div>
              <button className="hr-history-modal-close" onClick={handleCloseHistory}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            {/* Tabs */}
            <div className="hr-history-tabs">
              <button
                className={`hr-history-tab ${historyTab === 'history' ? 'active' : ''}`}
                onClick={() => setHistoryTab('history')}
              >
                <FontAwesomeIcon icon={faHistory} /> All Records
              </button>
              <button
                className={`hr-history-tab ${historyTab === 'stats' ? 'active' : ''}`}
                onClick={() => setHistoryTab('stats')}
              >
                <FontAwesomeIcon icon={faCalculator} /> Statistics
              </button>
              <button
                className={`hr-history-tab ${historyTab === 'trend' ? 'active' : ''}`}
                onClick={() => setHistoryTab('trend')}
              >
                <FontAwesomeIcon icon={faChartLine} /> Trend Chart
              </button>
              <button
                className={`hr-history-tab ${historyTab === 'range' ? 'active' : ''}`}
                onClick={() => setHistoryTab('range')}
              >
                <FontAwesomeIcon icon={faInfoCircle} /> Normal Range
              </button>
            </div>

            <div className="hr-history-modal-body">
              {historyTab === 'history' ? (
                <>
                  {historyVital.history.length === 0 ? (
                    <p className="hr-history-empty">No history records available.</p>
                  ) : (
                    <table className="hr-history-table">
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Value</th>
                          <th>Status</th>
                          <th>Date</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {historyVital.history.map((entry, i) => {
                          const range = getVitalRange(historyVital.type, customRanges);
                          const status = isInRange(historyVital.type, entry.value, range);
                          const statusCfg = rangeStatusConfig[status];
                          const isDeleting = deletingVitalId === entry.id;
                          return (
                            <tr key={entry.id ?? i}>
                              <td>{i + 1}</td>
                              <td className="hr-history-value">{entry.value}</td>
                              <td>
                                {status !== 'unknown' && (
                                  <span className="hr-history-status-badge" style={{ color: statusCfg.color, background: statusCfg.bg }}>
                                    {statusCfg.label}
                                  </span>
                                )}
                              </td>
                              <td>{entry.date}</td>
                              <td>
                                <button
                                  className="hr-history-delete-btn"
                                  onClick={() => handleDeleteVitalHistory(historyVital.type, entry.id)}
                                  disabled={isDeleting || !entry.id}
                                  title={t('common:buttons.delete') || 'Delete'}
                                  aria-label="Delete record"
                                >
                                  <FontAwesomeIcon icon={isDeleting ? faSpinner : faTrashAlt} spin={isDeleting} />
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </>
              ) : historyTab === 'stats' ? (
                (() => {
                  const stats = computeVitalStats(historyVital.type, historyVital.history);
                  if (!stats) {
                    return <p className="hr-history-empty">No numeric records available for statistics.</p>;
                  }
                  const unit = getVitalUnit(historyVital.type);
                  const range = getVitalRange(historyVital.type, customRanges);
                  const latestStatus = isInRange(historyVital.type, historyVital.value, range);
                  const latestStatusCfg = rangeStatusConfig[latestStatus];
                  const statCards = [
                    { label: 'Total Records', value: String(stats.count), sub: '' },
                    { label: 'Latest Value', value: `${stats.latest}${unit ? ' ' + unit : ''}`, sub: stats.latestDate || '' },
                    { label: 'Lowest', value: `${stats.min}${unit ? ' ' + unit : ''}`, sub: 'Minimum recorded' },
                    { label: 'Highest', value: `${stats.max}${unit ? ' ' + unit : ''}`, sub: 'Maximum recorded' },
                    { label: 'Average', value: `${stats.avg.toFixed(1)}${unit ? ' ' + unit : ''}`, sub: 'Mean of records' },
                    { label: 'Change', value: `${stats.delta >= 0 ? '+' : ''}${stats.delta.toFixed(1)}${unit ? ' ' + unit : ''}`, sub: stats.delta > 0 ? 'Up from previous' : stats.delta < 0 ? 'Down from previous' : 'No change' },
                  ];
                  return (
                    <div className="hr-stats-tab-content">
                      <div className="hr-stats-currentbar">
                        <span className="hr-stats-current-label">Overall status:</span>
                        {latestStatus !== 'unknown' && (
                          <span className="hr-stats-current-pill" style={{ color: latestStatusCfg.color, background: latestStatusCfg.bg }}>
                            {latestStatusCfg.label}
                          </span>
                        )}
                        <span className="hr-stats-normal-range">Normal: {range.display}</span>
                      </div>
                      <div className="hr-stats-grid">
                        {statCards.map((card, ci) => (
                          <div className="hr-stat-box" key={ci}>
                            <div className="hr-stat-box-value">{card.value}</div>
                            <div className="hr-stat-box-label">{card.label}</div>
                            {card.sub && <div className="hr-stat-box-sub">{card.sub}</div>}
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()
              ) : historyTab === 'trend' ? (
                (() => {
                  const data = buildVitalTrendData(historyVital.type, historyVital.history);
                  if (data.length === 0) {
                    return <p className="hr-history-empty">No records available for the trend chart.</p>;
                  }
                  const unit = getVitalUnit(historyVital.type);
                  return (
                    <div className="hr-trend-tab-content">
                      <div className="hr-trend-chart">
                        <ResponsiveContainer width="100%" height={320}>
                          <LineChart data={data} margin={{ top: 10, right: 16, left: -10, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                            <XAxis dataKey="label" tick={{ fontSize: 12, fill: '#64748b' }} />
                            <YAxis tick={{ fontSize: 12, fill: '#64748b' }} domain={['auto', 'auto']} />
                            <Tooltip
                              contentStyle={{ borderRadius: 10, border: '1px solid #e2e8f0', fontSize: '0.8rem' }}
                            />
                            <Legend wrapperStyle={{ fontSize: '0.78rem' }} />
                            {historyVital.type === 'bloodPressure' ? (
                              <>
                                <Line type="monotone" dataKey="sys" name="Systolic (mmHg)" stroke="#f43f5e" strokeWidth={2.5} dot={{ r: 3 }} />
                                <Line type="monotone" dataKey="dia" name="Diastolic (mmHg)" stroke="#8b5cf6" strokeWidth={2.5} dot={{ r: 3 }} />
                              </>
                            ) : (
                              <Line
                                type="monotone"
                                dataKey="value"
                                name={`${historyVital.name}${unit ? ' (' + unit + ')' : ''}`}
                                stroke="#06b6d4"
                                strokeWidth={2.5}
                                dot={{ r: 3 }}
                                activeDot={{ r: 5 }}
                              />
                            )}
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  );
                })()
              ) : (
                <div className="hr-range-tab-content">
                  {(() => {
                    const range = getVitalRange(historyVital.type, customRanges);
                    const status = isInRange(historyVital.type, historyVital.value, range);
                    const statusCfg = rangeStatusConfig[status];
                    const isCustom = !!customRanges[historyVital.type];
                    return (
                      <>
                        {/* Current status */}
                        <div className="hr-range-current-status">
                          <span className="hr-range-current-label">Your current value:</span>
                          <span className="hr-range-current-value">{historyVital.value}</span>
                          {status !== 'unknown' && (
                            <span className="hr-range-status-pill" style={{ color: statusCfg.color, background: statusCfg.bg }}>
                              {statusCfg.label}
                            </span>
                          )}
                        </div>

                        {/* Normal range display */}
                        <div className="hr-range-display-card">
                          <div className="hr-range-display-header">
                            <span className="hr-range-display-title">Normal Range</span>
                            {isCustom && <span className="hr-range-custom-badge">Customized</span>}
                          </div>
                          <div className="hr-range-display-value">{range.display}</div>

                          {/* Edit range */}
                          {editingRange === historyVital.type ? (
                            <div className="hr-range-edit-form">
                              <div className="hr-range-edit-inputs">
                                <div className="hr-range-edit-field">
                                  <label>Min</label>
                                  <input type="number" value={rangeEditMin} onChange={(e) => setRangeEditMin(e.target.value)} />
                                </div>
                                <span className="hr-range-edit-sep">–</span>
                                <div className="hr-range-edit-field">
                                  <label>Max</label>
                                  <input type="number" value={rangeEditMax} onChange={(e) => setRangeEditMax(e.target.value)} />
                                </div>
                                <span className="hr-range-edit-unit">{range.unit}</span>
                              </div>
                              <div className="hr-range-edit-actions">
                                <button className="hr-range-save-btn" onClick={() => handleSaveRange(historyVital.type)}>
                                  <FontAwesomeIcon icon={faCheck} /> Save
                                </button>
                                {isCustom && (
                                  <button className="hr-range-reset-btn" onClick={() => handleResetRange(historyVital.type)}>
                                    Reset to Default
                                  </button>
                                )}
                                <button className="hr-range-cancel-btn" onClick={() => setEditingRange(null)}>Cancel</button>
                              </div>
                            </div>
                          ) : (
                            <button className="hr-range-modify-btn" onClick={() => handleStartRangeEdit(historyVital.type)}>
                              <FontAwesomeIcon icon={faSliders} /> Modify Range
                            </button>
                          )}
                        </div>

                        {/* Source */}
                        <div className="hr-range-source">
                          <FontAwesomeIcon icon={faInfoCircle} />
                          <span>
                            Source: {range.sourceUrl
                              ? <a href={range.sourceUrl} target="_blank" rel="noopener noreferrer">{range.source}</a>
                              : <span>{range.source}</span>}
                          </span>
                        </div>

                        {/* Report Error */}
                        <a
                          className="hr-range-report-error"
                          href={`mailto:${REPORT_ERROR_EMAIL}?subject=Normal Range Error Report - ${historyVital.name}&body=Hi,%0A%0AI would like to report an error in the normal range for ${historyVital.name}.%0A%0ACurrent range shown: ${range.display}%0ASource: ${range.source}%0A%0AExpected range:%0AReason:%0A`}
                        >
                          <FontAwesomeIcon icon={faExclamationTriangle} />
                          Report an error in this range
                        </a>
                      </>
                    );
                  })()}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ══════════ ADD RECORD MODAL ══════════ */}
      {isAddingRecord && (
        <div className="hr-add-record-overlay" onClick={handleCloseAddRecord}>
          <div className="hr-add-record-modal" onClick={(e) => e.stopPropagation()}>
            <div className="hr-add-record-modal-header">
              <div className="hr-add-record-modal-icon">
                <FontAwesomeIcon icon={activeTabConfig?.icon || faPlus} />
              </div>
              <div>
                <h3 className="hr-add-record-modal-title">
                  Add {activeTab === 'Prescription & Lab Tests' ? 'Prescription / Lab Test'
                    : activeTab === 'Hospitalizations' ? 'Hospitalization Record'
                    : activeTab === 'Medical Bills' ? 'Medical Bill'
                    : activeTab === 'Health Conditions' ? 'Health Condition'
                    : 'Vaccination Certificate'}
                </h3>
                <p className="hr-add-record-modal-subtitle">Fill in the details below to add a new record</p>
              </div>
              <button className="hr-add-record-modal-close" onClick={handleCloseAddRecord}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            <div className="hr-add-record-modal-body">
              {/* Self / Dependant */}
              {!isDependantUser && (
                <div className="hr-add-record-radio-group">
                  <label className={`hr-add-record-radio${addRecordForWhom === 'self' ? ' active' : ''}`}>
                    <input
                      type="radio"
                      name="addForWhom"
                      value="self"
                      checked={addRecordForWhom === 'self'}
                      onChange={() => setAddRecordForWhom('self')}
                    />
                    Self
                  </label>
                  <label className={`hr-add-record-radio${addRecordForWhom === 'dependant' ? ' active' : ''}`}>
                    <input
                      type="radio"
                      name="addForWhom"
                      value="dependant"
                      checked={addRecordForWhom === 'dependant'}
                      onChange={() => setAddRecordForWhom('dependant')}
                    />
                    Dependant
                  </label>
                </div>
              )}

              {/* Bug 3 fix: Dependant selector dropdown */}
              {!isDependantUser && addRecordForWhom === 'dependant' && (
                <div className="hr-add-record-field" style={{ marginBottom: '16px' }}>
                  <label>Select Dependant *</label>
                  {dependantsLoading ? (
                    <div style={{ padding: '8px 0', color: '#6b7280', fontSize: '14px' }}>
                      <FontAwesomeIcon icon={faSpinner} spin /> Loading dependants...
                    </div>
                  ) : dependantsList.length === 0 ? (
                    <div style={{ padding: '8px 12px', background: '#fffbeb', border: '1px solid #f59e0b', borderRadius: '6px', color: '#92400e', fontSize: '14px' }}>
                      <FontAwesomeIcon icon={faExclamationTriangle} style={{ marginRight: '6px' }} />
                      No dependants found. Please add a dependant from your profile first.
                    </div>
                  ) : (
                    <select
                      value={addRecordDependantId}
                      onChange={(e) => setAddRecordDependantId(e.target.value ? Number(e.target.value) : '')}
                      style={{ width: '100%', padding: '10px 12px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '14px' }}
                    >
                      <option value="">-- Select a dependant --</option>
                      {dependantsList.map((dep) => (
                        <option key={dep.id} value={dep.id}>
                          {dep.name} {dep.relationship_name ? `(${dep.relationship_name})` : ''}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              )}

              {/* ─── HEALTH CONDITIONS FORM ─── */}
              {activeTab === 'Health Conditions' && (
                <>
                  {(() => {
                    type DraftKey =
                      | 'condition_name' | 'condition_type' | 'severity' | 'start_date' | 'end_date'
                      | 'status' | 'treated_by_doctor' | 'doctor_name' | 'notes';
                    const draft = {
                      condition_name: addConditionName,
                      condition_type: addConditionType,
                      severity: addConditionSeverity,
                      start_date: addConditionStartDate,
                      end_date: addConditionEndDate,
                      status: addConditionStatus,
                      treated_by_doctor: addConditionTreatedByDoctor,
                      doctor_name: addConditionDoctorName,
                      notes: addConditionNotes,
                      medications: addConditionMedicines,
                    };
                    const set = {
                      updateDraft: (key: DraftKey, value: string | boolean) => {
                        switch (key) {
                          case 'condition_name': setAddConditionName(value as string); break;
                          case 'condition_type': setAddConditionType(value as string); break;
                          case 'severity': setAddConditionSeverity(value as 'mild' | 'moderate' | 'severe'); break;
                          case 'start_date': setAddConditionStartDate(value as string); break;
                          case 'end_date': setAddConditionEndDate(value as string); break;
                          case 'status': setAddConditionStatus(value as 'ongoing' | 'resolved'); break;
                          case 'treated_by_doctor': setAddConditionTreatedByDoctor(value as boolean); break;
                          case 'doctor_name': setAddConditionDoctorName(value as string); break;
                          case 'notes': setAddConditionNotes(value as string); break;
                        }
                      },
                      updateMedication: (index: number, key: keyof ConditionMedicineRow, value: string | boolean) =>
                        updateConditionMedicine(index, key, value),
                      addMedicationRow: () =>
                        setAddConditionMedicines((prev) => [...prev, emptyConditionMedicine()]),
                      removeMedicationRow: (index: number) =>
                        setAddConditionMedicines((prev) => prev.filter((_, i) => i !== index)),
                    };
                    return (
                      <>
                        <div className="hr-add-record-grid">
                          <div className="hr-add-record-field hr-add-record-full-width">
                            <label>{t('health:conditions.nameLabel')} *</label>
                            <input
                              type="text"
                              value={draft.condition_name}
                              onChange={(e) => set.updateDraft('condition_name', e.target.value)}
                              placeholder={t('health:conditions.namePlaceholder')}
                            />
                          </div>

                          <div className="hr-add-record-field">
                            <label>{t('health:conditions.typeLabel')}</label>
                            <select
                              value={draft.condition_type}
                              onChange={(e) => set.updateDraft('condition_type', e.target.value)}
                            >
                              {(conditionChoices?.condition_types || []).map((o) => (
                                <option key={o.value} value={o.value}>{o.label}</option>
                              ))}
                            </select>
                          </div>

                          <div className="hr-add-record-field">
                            <label>{t('health:conditions.severityLabel')}</label>
                            <select
                              value={draft.severity}
                              onChange={(e) => set.updateDraft('severity', e.target.value as 'mild' | 'moderate' | 'severe')}
                            >
                              {(conditionChoices?.severities || []).map((o) => (
                                <option key={o.value} value={o.value}>{o.label}</option>
                              ))}
                            </select>
                          </div>

                          <div className="hr-add-record-field">
                            <label>{t('health:conditions.startDate')} *</label>
                            <input
                              type="date"
                              value={draft.start_date}
                              max={new Date().toISOString().slice(0, 10)}
                              onChange={(e) => set.updateDraft('start_date', e.target.value)}
                            />
                          </div>

                          <div className="hr-add-record-field">
                            <label>{t('health:conditions.endDate')}</label>
                            <input
                              type="date"
                              value={draft.end_date}
                              min={draft.start_date || undefined}
                              onChange={(e) => set.updateDraft('end_date', e.target.value)}
                            />
                          </div>

                          <div className="hr-add-record-field">
                            <label>{t('health:conditions.statusLabel')}</label>
                            <select
                              value={draft.status}
                              onChange={(e) => set.updateDraft('status', e.target.value as 'ongoing' | 'resolved')}
                            >
                              {(conditionChoices?.statuses || []).map((o) => (
                                <option key={o.value} value={o.value}>{o.label}</option>
                              ))}
                            </select>
                          </div>

                          <div className="hr-add-record-field">
                            <label className="hr-add-record-checkbox-label">
                              <input
                                type="checkbox"
                                checked={draft.treated_by_doctor}
                                onChange={(e) => set.updateDraft('treated_by_doctor', e.target.checked)}
                              />
                              <span>{t('health:conditions.treatedByDoctor')}</span>
                            </label>
                          </div>

                          {draft.treated_by_doctor && (
                            <div className="hr-add-record-field">
                              <label>{t('health:conditions.doctorName')}</label>
                              <input
                                type="text"
                                value={draft.doctor_name}
                                onChange={(e) => set.updateDraft('doctor_name', e.target.value)}
                                placeholder={t('health:conditions.doctorPlaceholder')}
                              />
                            </div>
                          )}

                          <div className="hr-add-record-field hr-add-record-full-width">
                            <label>{t('health:conditions.notesLabel')}</label>
                            <textarea
                              rows={2}
                              value={draft.notes}
                              onChange={(e) => set.updateDraft('notes', e.target.value)}
                              placeholder={t('health:conditions.notesPlaceholder')}
                            />
                          </div>
                        </div>

                        <div className="hcp-med-section">
                          <div className="hcp-med-head">
                            <h4>{t('health:conditions.medicationsTitle')}</h4>
                            <button
                              type="button"
                              className="hcp-add-med-btn"
                              onClick={() => set.addMedicationRow()}
                            >
                              <FontAwesomeIcon icon={faPlus} /> {t('health:conditions.addMedicine')}
                            </button>
                          </div>

                          {draft.medications.map((med, index) => (
                            <div className="hr-add-record-grid hcp-med-row" key={index}>
                              <div className="hr-add-record-field hcp-med-name">
                                <label>{t('health:conditions.medicineName')}</label>
                                <input
                                  type="text"
                                  value={med.medicine_name || ''}
                                  onChange={(e) => set.updateMedication(index, 'medicine_name', e.target.value)}
                                  placeholder={t('health:conditions.medicinePlaceholder')}
                                />
                              </div>

                              <div className="hr-add-record-field">
                                <label>{t('health:conditions.medicineType')}</label>
                                <select
                                  value={med.medicine_type || 'tablet'}
                                  onChange={(e) => set.updateMedication(index, 'medicine_type', e.target.value)}
                                >
                                  {(conditionChoices?.medicine_types || []).map((o) => (
                                    <option key={o.value} value={o.value}>{o.label}</option>
                                  ))}
                                </select>
                              </div>

                              <div className="hr-add-record-field">
                                <label>{t('health:conditions.dosage')}</label>
                                <input
                                  type="text"
                                  value={med.dosage || ''}
                                  onChange={(e) => set.updateMedication(index, 'dosage', e.target.value)}
                                  placeholder={t('health:conditions.dosagePlaceholder')}
                                />
                              </div>

                              <div className="hr-add-record-field">
                                <label>{t('health:conditions.frequency')}</label>
                                <select
                                  value={med.frequency || 'unknown'}
                                  onChange={(e) => set.updateMedication(index, 'frequency', e.target.value)}
                                >
                                  {(conditionChoices?.frequencies || []).map((o) => (
                                    <option key={o.value} value={o.value}>{o.label}</option>
                                  ))}
                                </select>
                              </div>

                              <div className="hr-add-record-field">
                                <label>{t('health:conditions.source')}</label>
                                <select
                                  value={med.source || 'self'}
                                  onChange={(e) => set.updateMedication(index, 'source', e.target.value)}
                                >
                                  {(conditionChoices?.medication_sources || []).map((o) => (
                                    <option key={o.value} value={o.value}>{o.label}</option>
                                  ))}
                                </select>
                              </div>

                              {draft.medications.length > 1 && (
                                <div className="hr-add-record-field hcp-med-remove">
                                  <button
                                    type="button"
                                    className="hr-record-delete-btn"
                                    onClick={() => set.removeMedicationRow(index)}
                                    title={t('health:conditions.removeMedicine')}
                                  >
                                    <FontAwesomeIcon icon={faTrashAlt} />
                                  </button>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>

                        {conditionExistingDocs.length > 0 && (
                          <div className="hr-add-record-upload-section">
                            <label style={{ fontWeight: 600, fontSize: '14px', marginBottom: '8px', display: 'block' }}>
                              Existing Documents
                            </label>
                            <div className="hr-add-record-files-list">
                              {conditionExistingDocs.map((doc) => (
                                <div key={doc.id} className="hr-add-record-file-item">
                                  <a
                                    href={doc.file}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{ color: '#14b8a6', textDecoration: 'none', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                  >
                                    {getFileNameFromUrl(doc.file)}
                                  </a>
                                  <button
                                    type="button"
                                    onClick={() => handleRemoveConditionDoc(doc.id)}
                                    title={t('health:conditions.removeDocument')}
                                  >
                                    <FontAwesomeIcon icon={faTimes} />
                                  </button>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </>
                    );
                  })()}
                </>
              )}

              {/* ─── PRESCRIPTION FORM ─── */}
              {activeTab === 'Prescription & Lab Tests' && (
                <>
                  <div className="hr-add-record-grid">
                    <div className="hr-add-record-field">
                      <label>Record Name *</label>
                      <select value={addRecordName} onChange={(e) => setAddRecordName(e.target.value)}>
                        <option value="">Select</option>
                        <option value="Doctor Prescription">Doctor Prescription</option>
                        <option value="Lab Report / Blood Test">Lab Report / Blood Test</option>
                        <option value="Radiology Report">Radiology Report</option>
                        <option value="Discharge Summary">Discharge Summary</option>
                        <option value="Medical Certificate">Medical Certificate</option>
                        <option value="Referral Letter">Referral Letter</option>
                        <option value="Other Medical Record">Other Medical Record</option>
                      </select>
                    </div>
                    <div className="hr-add-record-field">
                      <label>Record Date *</label>
                      <input type="date" value={addRecordDate} onChange={(e) => setAddRecordDate(e.target.value)} />
                    </div>
                    <div className="hr-add-record-field">
                      <label>Type of Record *</label>
                      <select value={addRecordType} onChange={(e) => setAddRecordType(e.target.value)}>
                        <option value="">Select</option>
                        <option value="doctor_prescription">Doctor Prescription</option>
                        <option value="lab_report">Lab Report</option>
                        <option value="radiology_report">Radiology Report</option>
                        <option value="health_record">Health Record</option>
                      </select>
                    </div>
                    <div className="hr-add-record-field">
                      <label>{t('health:doctorVisits.doctorName')} *</label>
                      <input type="text" value={addDoctorName} onChange={(e) => setAddDoctorName(e.target.value)} placeholder={t('health:doctorVisits.doctorNamePlaceholder')} />
                    </div>
                    <div className="hr-add-record-field">
                      <label>Specialization</label>
                      <select value={addSpecialization} onChange={(e) => setAddSpecialization(e.target.value)}>
                        <option value="">Select</option>
                        <option value="General Physician">General Physician</option>
                        <option value="Cardiology">Cardiology</option>
                        <option value="Dermatology">Dermatology</option>
                        <option value="Endocrinology / Diabetes">Endocrinology / Diabetes</option>
                        <option value="ENT (Ear, Nose, Throat)">ENT (Ear, Nose, Throat)</option>
                        <option value="Gastroenterology">Gastroenterology</option>
                        <option value="General Surgery">General Surgery</option>
                        <option value="Gynecology / Obstetrics">Gynecology / Obstetrics</option>
                        <option value="Nephrology">Nephrology</option>
                        <option value="Neurology">Neurology</option>
                        <option value="Oncology">Oncology</option>
                        <option value="Ophthalmology">Ophthalmology</option>
                        <option value="Orthopedics">Orthopedics</option>
                        <option value="Pediatrics">Pediatrics</option>
                        <option value="Psychiatry">Psychiatry</option>
                        <option value="Pulmonology">Pulmonology</option>
                        <option value="Radiology">Radiology</option>
                        <option value="Rheumatology">Rheumatology</option>
                        <option value="Urology">Urology</option>
                        <option value="Dentistry">Dentistry</option>
                        <option value="Physiotherapy">Physiotherapy</option>
                        <option value="Ayurveda / Homeopathy">Ayurveda / Homeopathy</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                  </div>
                  <div className="hr-add-record-field hr-add-record-full-width">
                    <label>{t('health:doctorVisits.reason')}</label>
                    <textarea rows={2} value={addReason} onChange={(e) => setAddReason(e.target.value)} placeholder={t('health:doctorVisits.reasonPlaceholder')} />
                  </div>

                  {/* Lab Parameters Toggle */}
                  <div className="hr-add-record-lab-toggle">
                    <button
                      className={`hr-add-record-lab-btn${addShowLabParams ? ' active' : ''}`}
                      onClick={() => setAddShowLabParams((p) => !p)}
                    >
                      <FontAwesomeIcon icon={faPlus} /> Lab Parameters
                    </button>
                  </div>
                  {addShowLabParams && (
                    <div className="hr-add-record-lab-section">
                      <table className="hr-add-record-lab-table">
                        <thead>
                          <tr>
                            <th>Parameter Name</th>
                            <th>Result</th>
                            <th>Unit</th>
                            <th>Start Range</th>
                            <th>End Range</th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          {addLabParams.length === 0 && (
                            <tr><td colSpan={6} className="hr-add-record-lab-empty">No parameters added yet.</td></tr>
                          )}
                          {addLabParams.map((param) => (
                            <tr key={param.id}>
                              <td><input type="text" value={param.parameterName} onChange={(e) => handleAddLabParamChange(param.id, 'parameterName', e.target.value)} placeholder="Name" /></td>
                              <td><input type="text" value={param.result} onChange={(e) => handleAddLabParamChange(param.id, 'result', e.target.value)} placeholder="Result" /></td>
                              <td><input type="text" value={param.unit} onChange={(e) => handleAddLabParamChange(param.id, 'unit', e.target.value)} placeholder="Unit" /></td>
                              <td><input type="text" value={param.startRange} onChange={(e) => handleAddLabParamChange(param.id, 'startRange', e.target.value)} placeholder="Min" /></td>
                              <td><input type="text" value={param.endRange} onChange={(e) => handleAddLabParamChange(param.id, 'endRange', e.target.value)} placeholder="Max" /></td>
                              <td><button className="hr-add-record-lab-remove" onClick={() => handleRemoveAddLabParam(param.id)}><FontAwesomeIcon icon={faTimes} /></button></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      <button className="hr-add-record-lab-add" onClick={handleAddLabParam}>
                        <FontAwesomeIcon icon={faPlus} /> Add Parameter
                      </button>
                    </div>
                  )}
                </>
              )}

              {/* ─── HOSPITALIZATION FORM ─── */}
              {activeTab === 'Hospitalizations' && (
                <div className="hr-add-record-grid">
                  <div className="hr-add-record-field">
                    <label>Hospitalization Type <span className="hr-required">*</span></label>
                    <select
                      value={addHospitalizationType}
                      onChange={(e) => setAddHospitalizationType(e.target.value)}
                    >
                      <option value="inpatient">Inpatient</option>
                      <option value="outpatient">Outpatient</option>
                      <option value="emergency">Emergency</option>
                      <option value="surgery">Surgery</option>
                      <option value="day_care">Day Care</option>
                    </select>
                  </div>
                  <div className="hr-add-record-field">
                    <label>{t('health:hospitalization.hospitalName')} *</label>
                    <input type="text" value={addHospitalName} onChange={(e) => setAddHospitalName(e.target.value)} placeholder={t('health:hospitalization.hospitalNamePlaceholder')} />
                  </div>
                  <div className="hr-add-record-field">
                    <label>{t('health:hospitalization.admissionDate')} *</label>
                    <input type="date" value={addAdmissionDate} onChange={(e) => setAddAdmissionDate(e.target.value)} />
                  </div>
                  <div className="hr-add-record-field">
                    <label>{t('health:hospitalization.dischargeDate')}</label>
                    <input type="date" value={addDischargeDate} onChange={(e) => setAddDischargeDate(e.target.value)} />
                  </div>
                  <div className="hr-add-record-field">
                    <label>{t('health:doctorVisits.doctorName')}</label>
                    <input type="text" value={addHospDoctorName} onChange={(e) => setAddHospDoctorName(e.target.value)} placeholder={t('health:doctorVisits.doctorNamePlaceholder')} />
                  </div>
                  <div className="hr-add-record-field hr-add-record-full-width">
                    <label>{t('health:doctorVisits.diagnosis')}</label>
                    <input
                      type="text"
                      placeholder={t('health:hospitalization.reasonPlaceholder')}
                      value={addHospDiagnosis}
                      onChange={(e) => setAddHospDiagnosis(e.target.value)}
                    />
                  </div>
                  {addHospitalizationType === 'surgery' && (
                    <div className="hr-add-record-field">
                      <label>{t('health:hospitalization.procedure')}</label>
                      <input
                        type="text"
                        placeholder={t('health:hospitalization.procedurePlaceholder')}
                        value={addHospProcedureName}
                        onChange={(e) => setAddHospProcedureName(e.target.value)}
                      />
                    </div>
                  )}
                  <div className="hr-add-record-field">
                    <label>Room Type</label>
                    <select value={addRoomType} onChange={(e) => setAddRoomType(e.target.value)}>
                      <option value="">Select</option>
                      <option value="general">General</option>
                      <option value="semi_private">Semi-Private</option>
                      <option value="private">Private</option>
                      <option value="icu">ICU</option>
                      <option value="nicu">NICU</option>
                    </select>
                  </div>
                  <div className="hr-add-record-field">
                    <label>{t('health:hospitalization.totalBill')}</label>
                    <input type="number" value={addTotalBill} onChange={(e) => setAddTotalBill(e.target.value)} placeholder={t('health:hospitalization.totalBillPlaceholder')} />
                  </div>
                  <div className="hr-add-record-field">
                    <label>{t('health:hospitalization.insuranceClaimed')}</label>
                    <div className="hr-add-record-toggle-wrap">
                      <label className="hr-add-record-toggle">
                        <input type="checkbox" checked={addHospInsuranceClaimed} onChange={(e) => setAddHospInsuranceClaimed(e.target.checked)} />
                        <span className="hr-add-record-toggle-slider" />
                      </label>
                      <span className="hr-add-record-toggle-label">{addHospInsuranceClaimed ? t('common:buttons.yes') : t('common:buttons.no')}</span>
                    </div>
                  </div>
                  {addHospInsuranceClaimed && (
                    <div className="hr-add-record-field">
                      <label>Insurance Amount</label>
                      <input type="number" value={addHospInsuranceAmount} onChange={(e) => setAddHospInsuranceAmount(e.target.value)} placeholder={t('health:hospitalization.insuranceClaimedPlaceholder')} />
                    </div>
                  )}
                  <div className="hr-add-record-field hr-add-record-span-3">
                    <label>{t('health:hospitalization.reason')} *</label>
                    <textarea rows={2} value={addHospReason} onChange={(e) => setAddHospReason(e.target.value)} placeholder={t('health:hospitalization.notesPlaceholder')} />
                  </div>
                  <div className="hr-add-record-field hr-add-record-span-3">
                    <label>{t('health:hospitalization.notes')}</label>
                    <textarea rows={2} value={addHospNotes} onChange={(e) => setAddHospNotes(e.target.value)} placeholder={t('health:doctorVisits.notesPlaceholder')} />
                  </div>
                </div>
              )}

              {/* ─── MEDICAL BILLS FORM ─── */}
              {activeTab === 'Medical Bills' && (
                <div className="hr-add-record-grid">
                  <div className="hr-add-record-field">
                    <label>{t('health:medicalBills.hospitalClinic')} *</label>
                    <input type="text" value={addBillHospitalName} onChange={(e) => setAddBillHospitalName(e.target.value)} placeholder={t('health:medicalBills.hospitalClinicPlaceholder')} />
                  </div>
                  <div className="hr-add-record-field">
                    <label>{t('health:medicalBills.invoiceNumber')}</label>
                    <input
                      type="text"
                      placeholder={t('health:medicalBills.invoicePlaceholder')}
                      value={addBillNumber}
                      onChange={(e) => setAddBillNumber(e.target.value)}
                    />
                  </div>
                  <div className="hr-add-record-field">
                    <label>{t('health:medicalBills.billDate')} *</label>
                    <input type="date" value={addBillDate} onChange={(e) => setAddBillDate(e.target.value)} />
                  </div>
                  <div className="hr-add-record-field">
                    <label>Bill Type *</label>
                    <select value={addBillType} onChange={(e) => setAddBillType(e.target.value)}>
                      <option value="">Select</option>
                      <option value="Consultation">Consultation</option>
                      <option value="Lab Test">Lab Test</option>
                      <option value="Surgery">Surgery</option>
                      <option value="Pharmacy">Pharmacy</option>
                      <option value="Hospitalization">Hospitalization</option>
                      <option value="Diagnostics">Diagnostics</option>
                      <option value="Physiotherapy">Physiotherapy</option>
                      <option value="Hospital Bill">Hospital Bill</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  <div className="hr-add-record-field">
                    <label>{t('health:medicalBills.totalAmount')} *</label>
                    <input type="number" value={addBillAmount} onChange={(e) => setAddBillAmount(e.target.value)} placeholder={t('health:medicalBills.totalAmountPlaceholder')} />
                  </div>
                  <div className="hr-add-record-field">
                    <label>{t('health:medicalBills.amountPaid')}</label>
                    <input type="number" value={addBillPaidAmount} onChange={(e) => setAddBillPaidAmount(e.target.value)} placeholder={t('health:medicalBills.amountPaidPlaceholder')} />
                  </div>
                  <div className="hr-add-record-field">
                    <label>{t('health:medicalBills.insuranceClaimed')}</label>
                    <div className="hr-add-record-toggle-wrap">
                      <label className="hr-add-record-toggle">
                        <input type="checkbox" checked={addBillInsuranceClaimed} onChange={(e) => setAddBillInsuranceClaimed(e.target.checked)} />
                        <span className="hr-add-record-toggle-slider" />
                      </label>
                      <span className="hr-add-record-toggle-label">{addBillInsuranceClaimed ? t('common:buttons.yes') : t('common:buttons.no')}</span>
                    </div>
                  </div>
                  {addBillInsuranceClaimed && (
                    <div className="hr-add-record-field hr-add-record-span-3">
                      <label>Insurance Amount</label>
                      <input type="number" value={addBillInsuranceAmount} onChange={(e) => setAddBillInsuranceAmount(e.target.value)} placeholder={t('health:medicalBills.insuranceClaimedPlaceholder')} style={{ maxWidth: '300px' }} />
                    </div>
                  )}
                  <div className="hr-add-record-field hr-add-record-span-3">
                    <label>{t('health:medicalBills.description')}</label>
                    <textarea rows={2} value={addBillNotes} onChange={(e) => setAddBillNotes(e.target.value)} placeholder={t('health:medicalBills.descriptionPlaceholder')} />
                  </div>
                </div>
              )}

              {/* ─── VACCINATION FORM ─── */}
              {activeTab === 'Vaccinations Certificates' && (
                <div className="hr-add-record-grid">
                  <div className="hr-add-record-field">
                    <label>{t('health:vaccinations.vaccineName')} *</label>
                    <select
                      value={addVaccineName}
                      onChange={(e) => setAddVaccineName(e.target.value)}
                      required
                    >
                      <option value="">Select Vaccine</option>
                      <optgroup label={t('health:vaccinations.categories.covid19')}>
                        <option value="Covishield">Covishield (AstraZeneca)</option>
                        <option value="Covaxin">Covaxin (Bharat Biotech)</option>
                        <option value="Sputnik V">Sputnik V</option>
                        <option value="Pfizer-BioNTech">Pfizer-BioNTech</option>
                        <option value="Moderna">Moderna</option>
                        <option value="Johnson & Johnson">Johnson &amp; Johnson</option>
                      </optgroup>
                      <optgroup label={t('health:vaccinations.categories.childhoodRoutine')}>
                        <option value="BCG">BCG (Tuberculosis)</option>
                        <option value="OPV">OPV (Oral Polio)</option>
                        <option value="IPV">IPV (Injectable Polio)</option>
                        <option value="DPT">DPT (Diphtheria, Pertussis, Tetanus)</option>
                        <option value="Hepatitis B">Hepatitis B</option>
                        <option value="Hepatitis A">Hepatitis A</option>
                        <option value="MMR">MMR (Measles, Mumps, Rubella)</option>
                        <option value="Varicella">Varicella (Chickenpox)</option>
                        <option value="Rotavirus">Rotavirus</option>
                        <option value="Pneumococcal (PCV)">Pneumococcal (PCV)</option>
                        <option value="Hib">Hib (Haemophilus Influenzae B)</option>
                        <option value="Typhoid">Typhoid</option>
                        <option value="Meningococcal">Meningococcal</option>
                        <option value="Japanese Encephalitis">Japanese Encephalitis</option>
                      </optgroup>
                      <optgroup label={t('health:vaccinations.categories.adultTravel')}>
                        <option value="Influenza (Flu)">Influenza (Flu Shot)</option>
                        <option value="Tetanus Toxoid (TT)">Tetanus Toxoid (TT)</option>
                        <option value="Tdap">Tdap (Tetanus, Diphtheria, Pertussis)</option>
                        <option value="HPV">HPV (Human Papillomavirus)</option>
                        <option value="Yellow Fever">Yellow Fever</option>
                        <option value="Rabies">Rabies</option>
                        <option value="Cholera">Cholera</option>
                        <option value="Shingles (Zoster)">Shingles (Zoster)</option>
                      </optgroup>
                      <optgroup label={t('health:vaccinations.categories.other')}>
                        <option value="Other">Other (specify in notes)</option>
                      </optgroup>
                    </select>
                  </div>
                  <div className="hr-add-record-field">
                    <label>{t('health:vaccinations.vaccinationDate')} *</label>
                    <input type="date" value={addVaccinationDate} onChange={(e) => setAddVaccinationDate(e.target.value)} />
                  </div>
                  <div className="hr-add-record-field">
                    <label>{t('health:vaccinations.doseNumber')}</label>
                    <input type="number" value={addDoseNumber} onChange={(e) => setAddDoseNumber(e.target.value)} placeholder={t('health:vaccinations.doseNumberPlaceholder')} />
                  </div>
                  <div className="hr-add-record-field">
                    <label>{t('health:vaccinations.totalDoses')}</label>
                    <input type="number" value={addTotalDoses} onChange={(e) => setAddTotalDoses(e.target.value)} placeholder={t('health:vaccinations.totalDosesPlaceholder')} />
                  </div>
                  <div className="hr-add-record-field">
                    <label>{t('health:vaccinations.administeredBy')}</label>
                    <input type="text" value={addAdministeredBy} onChange={(e) => setAddAdministeredBy(e.target.value)} placeholder={t('health:vaccinations.administeredByPlaceholder')} />
                  </div>
                  <div className="hr-add-record-field">
                    <label>{t('health:vaccinations.location')}</label>
                    <input type="text" value={addVaccineLocation} onChange={(e) => setAddVaccineLocation(e.target.value)} placeholder={t('health:vaccinations.locationPlaceholder')} />
                  </div>
                  <div className="hr-add-record-field">
                    <label>{t('health:vaccinations.batchNumber')}</label>
                    <input type="text" value={addBatchNumber} onChange={(e) => setAddBatchNumber(e.target.value)} placeholder={t('health:vaccinations.batchNumberPlaceholder')} />
                  </div>
                  <div className="hr-add-record-field">
                    <label>{t('health:vaccinations.nextDueDate')}</label>
                    <input type="date" value={addNextDueDate} onChange={(e) => setAddNextDueDate(e.target.value)} />
                  </div>
                  <div className="hr-add-record-field hr-add-record-span-3">
                    <label>{t('health:hospitalization.notes')}</label>
                    <textarea rows={2} value={addVaccineNotes} onChange={(e) => setAddVaccineNotes(e.target.value)} placeholder={t('health:doctorVisits.notesPlaceholder')} />
                  </div>
                </div>
              )}

              {/* Upload Area */}
              <div className="hr-add-record-upload-section">
                <div className="hr-add-record-upload-area" onClick={() => addRecordFileRef.current?.click()}>
                  <FontAwesomeIcon icon={faUpload} className="hr-add-record-upload-icon" />
                  <p>Upload documents</p>
                  <small>(.jpeg, .jpg, .png, .pdf)</small>
                  <input
                    type="file"
                    ref={addRecordFileRef}
                    style={{ display: 'none' }}
                    accept=".jpeg,.jpg,.png,.pdf"
                    multiple
                    onChange={handleAddRecordFileUpload}
                  />
                </div>
                {addRecordFiles.length > 0 && (
                  <div className="hr-add-record-files-list">
                    {addRecordFiles.map((file, idx) => (
                      <div key={idx} className="hr-add-record-file-item">
                        <span>{file.name}</span>
                        <button onClick={() => handleRemoveAddFile(idx)}><FontAwesomeIcon icon={faTimes} /></button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Footer Buttons */}
            <div className="hr-add-record-modal-footer">
              <button className="hr-add-record-cancel-btn" onClick={handleCloseAddRecord} disabled={addRecordSaving}>
                {t('common:buttons.cancel')}
              </button>
              <button className="hr-add-record-save-btn" onClick={handleSaveAddRecord} disabled={addRecordSaving}>
                {addRecordSaving ? (
                  <><FontAwesomeIcon icon={faSpinner} spin /> {t('common:buttons.save')}...</>
                ) : (
                  <><FontAwesomeIcon icon={faSave} /> {t('common:buttons.save')} Record</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={confirmDialog.open}
        title={confirmDialog.title}
        message={confirmDialog.message}
        onConfirm={confirmDialog.onConfirm}
        onClose={() => setConfirmDialog(d => ({ ...d, open: false }))}
        confirmLabel={confirmDialog.confirmLabel ?? t('common:buttons.delete')}
        variant={confirmDialog.variant ?? 'danger'}
      />

      {/* In-app toast notification (replaces browser alert) */}
      {/* ═══════════ COMPARE RECORDS MODAL ═══════════ */}
      {showCompareModal && (
        <div className="hr-compare-overlay" onClick={() => setShowCompareModal(false)}>
          <div className="hr-compare-modal" onClick={(e) => e.stopPropagation()}>
            <div className="hr-compare-header">
              <div className="hr-compare-title">
                <div className="hr-compare-title-icon"><FontAwesomeIcon icon={faChartBar} /></div>
                <div>
                  <h3>Compare {activeTab}</h3>
                  <p>{compareResult ? 'Side-by-side view of your selected records' : 'Pick 2 or more records to compare side-by-side'}</p>
                </div>
              </div>
              <button className="hr-compare-close" onClick={() => setShowCompareModal(false)} aria-label="Close">
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            <div className="hr-compare-body">
              {!compareResult ? (
                <>
                  {compareCandidates.length < 2 ? (
                    <div className="hr-compare-empty-state">
                      <FontAwesomeIcon icon={faExclamationTriangle} />
                      <h4>Not enough records</h4>
                      <p>You need at least 2 records in this tab to run a comparison.</p>
                    </div>
                  ) : (
                    <>
                      <div className="hr-compare-hint">
                        <FontAwesomeIcon icon={faInfoCircle} />
                        <span>Tap records below to select them. Fields that differ will be highlighted for you.</span>
                      </div>
                      <div className="hr-compare-picker">
                        {compareCandidates.map((c) => {
                          const selected = compareSelectedIds.includes(c.id);
                          const idx = compareSelectedIds.indexOf(c.id);
                          return (
                            <button
                              type="button"
                              key={c.id}
                              className={`hr-compare-pick ${selected ? 'is-selected' : ''}`}
                              onClick={() => toggleCompareId(c.id)}
                            >
                              <span className="hr-compare-pick-check">
                                {selected ? <span className="hr-compare-pick-num">{idx + 1}</span> : <FontAwesomeIcon icon={faPlus} />}
                              </span>
                              <span className="hr-compare-pick-main">
                                <span className="hr-compare-pick-title">{c.label || '(untitled)'}</span>
                                <span className="hr-compare-pick-sub"><FontAwesomeIcon icon={faCalendarAlt} /> {c.sub}</span>
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    </>
                  )}
                </>
              ) : (
                (() => {
                  const records = compareResult.records || [];
                  const diffs = compareResult.differences || {};

                  const HIDDEN_FIELDS = new Set<string>([
                    'id', 'member_id', 'patient_id', 'family_member_id',
                    'dependant_data', 'dependant', 'dependant_id', 'whom', 'for_whom',
                    'created_at', 'updated_at', 'deleted_at', 'user', 'user_id',
                    'raw_data', 'extracted_data', 'ai_analysis', 'ai_metadata',
                    'metadata', 'ocr_data',
                  ]);
                  const FIELD_LABELS: Record<string, string> = {
                    record_name: 'Record Name',
                    record_type: 'Record Type',
                    record_date: 'Record Date',
                    doctor_name: 'Doctor',
                    specialization: 'Specialization',
                    reason: 'Reason for Visit',
                    parameters: 'Lab Parameters',
                    documents: 'Attached Documents',
                    hospital_name: 'Hospital',
                    admission_date: 'Admission Date',
                    discharge_date: 'Discharge Date',
                    treatment: 'Treatment',
                    doctor: 'Treating Doctor',
                    bill_date: 'Bill Date',
                    total_amount: 'Total Amount',
                    amount: 'Amount',
                    vaccine_name: 'Vaccine',
                    vaccination_date: 'Vaccination Date',
                    dose: 'Dose',
                    manufacturer: 'Manufacturer',
                    condition_name: 'Condition',
                    condition_type: 'Type',
                    severity: 'Severity',
                    status: 'Status',
                    is_ongoing: 'Ongoing',
                    duration_days: 'Duration (days)',
                    treated_by_doctor: 'Treated by Doctor',
                    notes: 'Notes',
                    medications: 'Medicines Taken',
                    medication_count: 'Medicines',
                    medicine_name: 'Medicine',
                    medicine_type: 'Type',
                    dosage: 'Dosage',
                    frequency: 'Frequency',
                    still_taking: 'Still Taking',
                    is_self_prescribed: 'Self-prescribed',
                  };
                  const humanize = (k: string) => FIELD_LABELS[k] || k.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
                  const isDateKey = (k: string) => /(_date|_at)$/i.test(k);
                  const isDateValue = (v: any) => typeof v === 'string' && /^\d{4}-\d{2}-\d{2}/.test(v);

                  const CHOICE_FIELDS = new Set<string>([
                    'condition_type', 'severity', 'status',
                    'medicine_type', 'frequency', 'source',
                  ]);
                  const displayFor = (key: string, holder: any): string | null => {
                    if (!holder || typeof holder !== 'object') return null;
                    const d = holder[`${key}_display`];
                    return typeof d === 'string' && d ? d : null;
                  };
                  const choiceLabel = (key: string, raw: any, holder: any): string => {
                    const d = displayFor(key, holder);
                    if (d) return d;
                    if (raw === null || raw === undefined || raw === '') return '—';
                    return String(raw).replace(/_/g, ' ');
                  };
                  const fmtValue = (key: string, v: any, record?: any): React.ReactNode => {
                    if (v === null || v === undefined || v === '' || (Array.isArray(v) && v.length === 0)) {
                      return <span className="hr-compare-empty-val">—</span>;
                    }
                    if (key === 'documents' && Array.isArray(v)) {
                      return (
                        <div className="hr-compare-docs">
                          {v.map((d: any, i: number) => {
                            const url = typeof d === 'string' ? d : (d?.file || d?.url || '');
                            const name = typeof d === 'object' && d?.name ? d.name : (url ? getFileNameFromUrl(url) : `Document ${i + 1}`);
                            const shortName = name.length > 22 ? `${name.slice(0, 22)}…` : name;
                            return url ? (
                              <a key={i} href={url} target="_blank" rel="noreferrer" className="hr-compare-doc-chip" title={name}>
                                <FontAwesomeIcon icon={faFileMedicalAlt} />
                                <span>{shortName}</span>
                              </a>
                            ) : (
                              <span key={i} className="hr-compare-doc-chip is-plain">
                                <FontAwesomeIcon icon={faFileMedicalAlt} /> <span>Document {i + 1}</span>
                              </span>
                            );
                          })}
                        </div>
                      );
                    }
                    if (key === 'medications' && Array.isArray(v)) {
                      return (
                        <div className="hr-compare-meds">
                          {v.map((m: any, i: number) => (
                            <div key={i} className="hr-compare-med-row">
                              <div className="hr-compare-med-head">
                                <span className="hr-compare-med-name">{m?.medicine_name || `Medicine ${i + 1}`}</span>
                                {m?.medicine_type_display && (
                                  <span className="hr-compare-med-chip">{m.medicine_type_display}</span>
                                )}
                                {m?.is_self_prescribed && (
                                  <span className="hr-compare-med-chip is-muted">Self-prescribed</span>
                                )}
                              </div>
                              <div className="hr-compare-med-meta">
                                {m?.dosage && <span>{m.dosage}</span>}
                                {m?.frequency_display && <span>{m.frequency_display}</span>}
                                {m?.start_date && <span>From {formatDateOnly(m.start_date)}</span>}
                                {m?.end_date && <span>Until {formatDateOnly(m.end_date)}</span>}
                                {m?.source_display && <span>{m.source_display}</span>}
                              </div>
                              {m?.notes && <p className="hr-compare-med-notes">{m.notes}</p>}
                            </div>
                          ))}
                        </div>
                      );
                    }
                    if (key === 'parameters' && Array.isArray(v)) {
                      return (
                        <div className="hr-compare-params">
                          {v.map((p: any, i: number) => (
                            <div key={i} className="hr-compare-param-row">
                              <span className="hr-compare-param-name">{p?.parameter_name || p?.name || `Parameter ${i + 1}`}</span>
                              <span className="hr-compare-param-val">
                                {p?.result ?? '—'}{p?.unit ? ` ${p.unit}` : ''}
                              </span>
                              {(p?.start_range || p?.end_range) && (
                                <span className="hr-compare-param-range">Normal: {p?.start_range ?? '?'} – {p?.end_range ?? '?'}</span>
                              )}
                            </div>
                          ))}
                        </div>
                      );
                    }
                    if (CHOICE_FIELDS.has(key)) {
                      return <span className="hr-compare-chip">{choiceLabel(key, v, record)}</span>;
                    }
                    if (key === 'treated_by_doctor' || key === 'is_ongoing' || key === 'still_taking' || key === 'is_self_prescribed') {
                      const b = typeof v === 'boolean' ? v : String(v).toLowerCase() === 'true';
                      return <span className={`hr-compare-chip ${b ? 'is-yes' : 'is-no'}`}>{b ? 'Yes' : 'No'}</span>;
                    }
                    if (key === 'duration_days') {
                      return <span>{v} days</span>;
                    }
                    if (isDateKey(key) && isDateValue(v)) {
                      try { return formatDateOnly(v); } catch { /* noop */ }
                    }
                    if (Array.isArray(v)) {
                      return (
                        <div className="hr-compare-list">
                          {v.map((item: any, i: number) => (
                            <div key={i} className="hr-compare-list-item">
                              {typeof item === 'object' && item !== null
                                ? Object.entries(item)
                                    .filter(([k]) => !HIDDEN_FIELDS.has(k) && !k.endsWith('_display'))
                                    .map(([k, val]) => {
                                      if (CHOICE_FIELDS.has(k)) {
                                        return `${humanize(k)}: ${choiceLabel(k, val, item)}`;
                                      }
                                      if (typeof val === 'boolean') return `${humanize(k)}: ${val ? 'Yes' : 'No'}`;
                                      if (val === null || val === undefined) return null;
                                      return `${humanize(k)}: ${val}`;
                                    })
                                    .filter(Boolean)
                                    .join(' • ')
                                : String(item)}
                            </div>
                          ))}
                        </div>
                      );
                    }
                    if (typeof v === 'object') {
                      const entries = Object.entries(v).filter(([k]) => !HIDDEN_FIELDS.has(k) && !k.endsWith('_display'));
                      if (entries.length === 0) {
                        return <span className="hr-compare-empty-val">—</span>;
                      }
                      return (
                        <div className="hr-compare-obj">
                          {entries.map(([k, val]) => (
                            <div key={k} className="hr-compare-obj-row">
                              <span className="hr-compare-obj-key">{humanize(k)}:</span>{' '}
                              <span className="hr-compare-obj-val">
                                {CHOICE_FIELDS.has(k)
                                  ? choiceLabel(k, val, v)
                                  : typeof val === 'boolean'
                                    ? (val ? 'Yes' : 'No')
                                    : val === null || val === undefined || val === ''
                                      ? '—'
                                      : String(val)}
                              </span>
                            </div>
                          ))}
                        </div>
                      );
                    }
                    return String(v);
                  };

                  const allKeys = Array.from(new Set(records.flatMap((r) => Object.keys(r))));
                  const visibleKeys = allKeys.filter(k => !HIDDEN_FIELDS.has(k) && !k.endsWith('_display'));
                  const displayKeys = showDiffsOnly ? visibleKeys.filter(k => k in diffs) : visibleKeys;
                  const diffCount = visibleKeys.filter(k => k in diffs).length;

                  const recordTitle = (r: any, i: number) =>
                    r?.record_name || r?.hospital_name || r?.vaccine_name || r?.condition_name || `Record ${i + 1}`;
                  const recordDate = (r: any) => {
                    const d = r?.record_date || r?.admission_date || r?.bill_date || r?.vaccination_date || r?.start_date;
                    return d && isDateValue(d) ? formatDateOnly(d) : '';
                  };

                  const gridTemplate = `minmax(150px, 190px) repeat(${records.length}, minmax(220px, 1fr))`;

                  return (
                    <>
                      <div className="hr-compare-summary">
                        <div className="hr-compare-summary-item">
                          <span className="hr-compare-summary-num">{records.length}</span>
                          <span className="hr-compare-summary-label">Records</span>
                        </div>
                        <div className="hr-compare-summary-item">
                          <span className="hr-compare-summary-num">{visibleKeys.length}</span>
                          <span className="hr-compare-summary-label">Fields</span>
                        </div>
                        <div className={`hr-compare-summary-item ${diffCount > 0 ? 'is-diff' : 'is-match'}`}>
                          <span className="hr-compare-summary-num">{diffCount}</span>
                          <span className="hr-compare-summary-label">{diffCount === 1 ? 'Differs' : 'Differ'}</span>
                        </div>
                        <label className="hr-compare-toggle">
                          <input type="checkbox" checked={showDiffsOnly} onChange={(e) => setShowDiffsOnly(e.target.checked)} />
                          <span>Show differences only</span>
                        </label>
                      </div>

                      <div className="hr-compare-grid-wrap">
                        <div className="hr-compare-grid" style={{ gridTemplateColumns: gridTemplate }}>
                          <div className="hr-compare-cell hr-compare-cell-head hr-compare-cell-corner">Field</div>
                          {records.map((r, i) => (
                            <div key={i} className="hr-compare-cell hr-compare-cell-head">
                              <div className="hr-compare-record-title">{recordTitle(r, i)}</div>
                              {recordDate(r) && (
                                <div className="hr-compare-record-sub">
                                  <FontAwesomeIcon icon={faCalendarAlt} /> {recordDate(r)}
                                </div>
                              )}
                            </div>
                          ))}

                          {displayKeys.length === 0 ? (
                            <div className="hr-compare-cell hr-compare-empty-msg" style={{ gridColumn: `1 / span ${records.length + 1}` }}>
                              <FontAwesomeIcon icon={faCheck} />
                              <span>{showDiffsOnly ? 'All shown fields are identical between these records.' : 'No fields available to compare.'}</span>
                            </div>
                          ) : displayKeys.map((key) => {
                            const isDiff = key in diffs;
                            return (
                              <React.Fragment key={key}>
                                <div className={`hr-compare-cell hr-compare-cell-label ${isDiff ? 'is-diff' : ''}`}>
                                  <span className="hr-compare-cell-label-text">{humanize(key)}</span>
                                  {isDiff && <span className="hr-compare-diff-tag">Differs</span>}
                                </div>
                                {records.map((r, i) => (
                                  <div key={i} className={`hr-compare-cell ${isDiff ? 'is-diff' : ''}`}>
                                    {fmtValue(key, r[key], r)}
                                  </div>
                                ))}
                              </React.Fragment>
                            );
                          })}
                        </div>
                      </div>
                    </>
                  );
                })()
              )}
            </div>

            <div className="hr-compare-footer">
              {!compareResult ? (
                <>
                  <span className="hr-compare-count-pill">
                    <FontAwesomeIcon icon={faCheck} /> {compareSelectedIds.length} selected
                  </span>
                  <button
                    onClick={handleRunCompare}
                    disabled={compareSelectedIds.length < 2 || compareLoading}
                    className="hr-action-btn hr-action-btn-primary"
                  >
                    {compareLoading
                      ? <><FontAwesomeIcon icon={faSpinner} spin /> Comparing…</>
                      : <><FontAwesomeIcon icon={faChartBar} /> Compare Records</>}
                  </button>
                </>
              ) : (
                <button onClick={() => setCompareResult(null)} className="hr-action-btn hr-action-btn-outline">
                  <FontAwesomeIcon icon={faChevronLeft} /> Back to Selection
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {toastMsg && (
        <div
          className="hr-toast-notification"
          style={{
            position: 'fixed',
            bottom: 32,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 10000,
            padding: '12px 24px',
            borderRadius: 12,
            color: '#fff',
            fontWeight: 500,
            fontSize: 14,
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            boxShadow: '0 4px 24px rgba(0,0,0,0.18)',
            animation: 'fadeIn 0.3s ease',
            background: toastMsg.type === 'success' ? '#10b981'
              : toastMsg.type === 'warning' ? '#f59e0b'
              : '#ef4444',
          }}
        >
          <FontAwesomeIcon icon={toastMsg.type === 'success' ? faCheck : faExclamationTriangle} />
          <span>{toastMsg.message}</span>
          <button
            onClick={() => setToastMsg(null)}
            style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', marginLeft: 8, fontSize: 16 }}
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
      )}

    </div>
  );
};

export default HealthRecords;
